import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./DeveloperIDETheme.css";
import AdvancedAIEngine from "../AdvancedAIEngine.js";
import {
  getApiBase,
  getBackendReady,
  isBackendLikelyDown,
  setBackendFailed,
  clearBackendBackoff,
} from "../backendProbe.js";
import {
  webPreviewSlotForPath,
  remapPatchFilesForIdeStarterKit,
  filterIdeStarterIndexRegression,
  wouldDestroyIdeStarterIndex,
} from "../utils/webPreviewPath.js";
import { getIdeStarterAiContextDigest } from "../utils/ideSiteStarterKit.js";

// When backend falls back to 8081, remember so all DevTools requests use it for the session
let _devToolsPreferredPort = null;

function apiBase(overridePort = null) {
  const port = overridePort != null ? overridePort : _devToolsPreferredPort;
  try {
    const origin = String(window?.location?.origin || "");
    const p = port != null ? port : 8080;
    if (/^https?:\/\/localhost(:\d+)?$/i.test(origin)) return `http://localhost:${p}`;
    if (/^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin)) return `http://127.0.0.1:${p}`;
  } catch (_) {}
  if (port != null) {
    try {
      const origin = String(window?.location?.origin || "");
      if (/^https?:\/\/localhost(:\d+)?$/i.test(origin)) return `http://localhost:${port}`;
      if (/^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin)) return `http://127.0.0.1:${port}`;
    } catch (_) {}
    return "";
  }
  const fromEnv = getApiBase();
  if (fromEnv) return fromEnv;
  return (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
}

/** Absolute API root for raw fetch fallbacks (matches runtime-config / getApiBase). */
function devtoolsApiRoot() {
  const b = apiBase();
  if (b) return b.replace(/\/+$/, "");
  return (process.env.REACT_APP_API_URL || "http://localhost:8080").replace(/\/+$/, "");
}

async function apiGet(path, params = {}, baseOverride = null) {
  if (baseOverride == null) {
    const ok = await getBackendReady();
    if (!ok) {
      throw new Error(
        "Backend not running. In the project root (folder with backend/ and frontend/), run: npm run start:backend â€” or npm run dev for both. Confirm http://localhost:8080/health (try :8081 if 8080 was busy). Windows: start-backend.bat."
      );
    }
  }
  const base = baseOverride != null ? (typeof baseOverride === "number" ? apiBase(baseOverride) : baseOverride) : apiBase();
  const url = new URL(base ? `${base}${path}` : path, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    url.searchParams.set(k, String(v));
  });
  const resp = await fetch(url.toString(), { credentials: "include" });
  const text = await resp.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = {};
  }
  if (!resp.ok) {
    if (resp.status >= 500) setBackendFailed();
    const msg = data?.error || (text ? String(text).slice(0, 240) : "") || `Request failed (${resp.status})`;
    throw new Error(`${msg}`);
  }
  return data;
}

async function apiPost(path, body = {}, opts = {}) {
  const base = apiBase();
  const url = base ? `${base}${path}` : path;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
    credentials: "include",
    signal: opts?.signal,
  });
  const text = await resp.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = {};
  }
  if (!resp.ok) {
    const msg = data?.error || (text ? String(text).slice(0, 240) : "") || `Request failed (${resp.status})`;
    throw new Error(`${msg} (HTTP ${resp.status})`);
  }
  return data;
}

function sseUrl(path, params = {}) {
  const base = apiBase();
  const url = new URL(base ? `${base}${path}` : path, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    url.searchParams.set(k, String(v));
  });
  return url.toString();
}

async function probeSse(url, { timeoutMs = 1500 } = {}) {
  // Best-effort probe to surface HTTP status for SSE endpoints (EventSource hides it).
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), Math.max(200, Number(timeoutMs || 1500)));
  try {
    const resp = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "text/event-stream" },
      signal: ctrl.signal,
    });
    // We only care about headers/status; abort immediately to avoid hanging the probe.
    try {
      ctrl.abort();
    } catch (_) {}
    return { ok: resp.ok, status: resp.status };
  } catch (e) {
    const msg = String(e?.message || "probe failed");
    return { ok: false, status: 0, error: msg };
  } finally {
    clearTimeout(t);
  }
}

/** True when Generated files include the Zolaid IDE built-in marketing starter (multi-file frontend). */
function isIdeStarterSiteKit(gen) {
  try {
    const o = gen && typeof gen === "object" ? gen : {};
    return Object.prototype.hasOwnProperty.call(o, "frontend/index.html");
  } catch (_) {
    return false;
  }
}

const IDE_STARTER_SITE_AI_GUIDANCE = [
  "ZOLAID BUILT-IN SITE: This workspace already has a full multi-file marketing product: frontend/index.html (shell + sections), frontend/styles.css, frontend/app.js, many section files (e.g. frontend/product.css + frontend/product.js tied to #product), plus backend/* stubs (auth, contact, Stripe), config, etc.",
  "CONTEXT BELOW: A structure digest plus selected Generated file contents are included in this message so you can see the real built-in code (disk repo search does not index Generated files). Use them for refurbish / redesign / copy / layout tasks.",
  "DEFAULT TASK: improve, refurbish, polish, extend, or finish THAT site. Edit real paths from the Generated files list. Prefer preserving header/footer, section ids, CSS/JS load order, and file naming unless the user asks for a full redesign.",
  "PATH RULE (CRITICAL): Put site files under frontend/â€¦ â€” e.g. frontend/index.html, frontend/about.js, frontend/pricing.css. Do NOT use bare index.html, styles.css, or app.js at repository root for this kit (the IDE will remap them, but you should output correct paths).",
  "frontend/index.html RULE (CRITICAL): It must stay an HTML FRAGMENT, NOT a full document. Do NOT output <!DOCTYPE html>, <html>, or <body> wrappers. Live Preview injects this file into a template. The root element MUST remain <div id=\"zolaid-site-root\" class=\"site-root\"> â€¦ </div> with multiple <section id=\"product|pricing|auth|contact|â€¦\"> regions. Never replace with a one-page brochure (e.g. only About/Product/Features/Pricing/Contact headings and lorem text). Keep Stripe buttons, auth section, contact form hooks, and footer structure unless the user explicitly asks to remove commerce/auth.",
  "Do NOT throw away the starter by outputting only a minimal greenfield site (e.g. only index.html + styles.css + app.js at repo root) unless the user explicitly requests replacing everything.",
  "For copy, visuals, accessibility, SEO, new sections, auth/Stripe/contact/legal: update the matching frontend/* (and backend/* when needed) so Live Preview stays coherent.",
].join("\n");

function remapAiOutputFilesForKit(files, generatedFiles) {
  const f = files && typeof files === "object" ? { ...files } : {};
  if (!isIdeStarterSiteKit(generatedFiles)) return f;
  return remapPatchFilesForIdeStarterKit(f);
}

const CHAT_RETRY_ATTEMPTS = 3;
const CHAT_RETRY_DELAY_MS = 1000;
const CHAT_FAILURE_LOG_MAX = 5;
const chatFailureLog = [];

function logChatFailure(url, attempt, err) {
  const entry = { ts: new Date().toISOString(), url, attempt, message: err?.message || String(err) };
  chatFailureLog.push(entry);
  if (chatFailureLog.length > CHAT_FAILURE_LOG_MAX) chatFailureLog.shift();
  if (typeof console !== "undefined" && console.warn) {
    console.warn("[Zolaid IDE] /chat request failed after retries:", entry);
  }
}

async function postChatWithFallback(body) {
  const payload = body && typeof body === "object" ? body : {};
  const base = apiBase();
  const candidates = [];
  if (base) candidates.push(`${base}/chat`);
  candidates.push("/chat");
  try {
    const o = window.location.origin || "";
    if (o && !o.includes("localhost") && !o.includes("127.0.0.1")) candidates.push(`${devtoolsApiRoot()}/chat`);
  } catch (_) {}

  let lastErr = null;
  let lastUrl = "";
  let lastAttempt = 0;
  for (const url of candidates) {
    for (let attempt = 0; attempt < CHAT_RETRY_ATTEMPTS; attempt++) {
      lastUrl = url;
      lastAttempt = attempt + 1;
      try {
        if (attempt > 0) {
          const delay = CHAT_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
          await new Promise((r) => setTimeout(r, delay));
        }
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        });
        const text = await resp.text().catch(() => "");
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (_) {
          data = null;
        }
        if (!resp.ok) {
          if (resp.status === 404) {
            lastErr = new Error("Request failed (HTTP 404)");
            break;
          }
          const code = data?.code || data?.details?.code || null;
          const msg = data?.error || (text ? String(text).slice(0, 260) : "") || `Request failed (HTTP ${resp.status})`;
          if (String(code || "").includes("insufficient_quota") || String(msg || "").toLowerCase().includes("insufficient_quota")) {
            throw new Error("OpenAI quota exceeded. Add billing/credits or use a different OPENAI_API_KEY on the backend.");
          }
          if (String(code || "").includes("missing_openai_key") || String(msg || "").toLowerCase().includes("missing openai_api_key")) {
            throw new Error("Missing OPENAI_API_KEY on the backend. Set it and restart the backend.");
          }
          lastErr = new Error(`${msg} (HTTP ${resp.status})`);
          if (resp.status >= 500 && attempt < CHAT_RETRY_ATTEMPTS - 1) continue;
          throw lastErr;
        }
        return data;
      } catch (e) {
        lastErr = e;
        const isRetryable = e?.message?.includes("Failed to fetch") || e?.message?.includes("network") || (e?.message?.includes("HTTP 5") && attempt < CHAT_RETRY_ATTEMPTS - 1);
        if (isRetryable && attempt < CHAT_RETRY_ATTEMPTS - 1) continue;
        break;
      }
    }
  }
  const finalErr = lastErr || new Error("Failed to reach /chat. Check backend is running and try again.");
  logChatFailure(lastUrl, lastAttempt, finalErr);
  throw finalErr;
}

// Chat uses markdown with copyable code blocks; onOpenPath(path) opens file in Monaco
function IdeChatMarkdown({ content, onOpenPath }) {
  const text = String(content || "").trim();
  if (!text) return <span style={{ opacity: 0.6 }}>â€”</span>;

  const isFileLikePath = (href) => {
    if (!href || typeof href !== "string") return false;
    const h = href.trim();
    if (/^(\w+:|\/\/)/.test(h)) return false;
    return /\.(js|jsx|ts|tsx|css|html|json|md|mjs|cjs|vue|svelte)$/i.test(h) || (h.includes("/") && !h.includes(" "));
  };

  const CodeBlock = ({ inline, className, children, ...props }) => {
    const [copied, setCopied] = useState(false);
    const codeRef = useRef(null);
    const handleCopy = async () => {
      try {
        const raw = codeRef.current ? codeRef.current.innerText : String(children ?? "").trim();
        await navigator.clipboard.writeText(raw);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (_) {}
    };
    if (inline) {
      return (
        <code
          style={{
            background: "rgba(255,255,255,0.1)",
            padding: "2px 6px",
            borderRadius: 4,
            fontFamily: "var(--font-mono, monospace)",
            fontSize: "0.95em",
          }}
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <div style={{ position: "relative", marginTop: 6, marginBottom: 6 }}>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            position: "absolute",
            top: 6,
            right: 8,
            fontSize: 10,
            padding: "2px 8px",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 6,
            color: "rgba(255,255,255,0.9)",
            cursor: "pointer",
          }}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
        <pre
          ref={codeRef}
          style={{
            background: "rgba(0,0,0,0.25)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            padding: "10px 12px 10px 12px",
            overflow: "auto",
            fontSize: 11,
            fontFamily: "var(--font-mono, monospace)",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <code className={className} {...props}>{children}</code>
        </pre>
      </div>
    );
  };

  return (
    <div className="dev-ide-chat-markdown" style={{ lineHeight: 1.5 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <div style={{ margin: "4px 0" }}>{children}</div>,
          ul: ({ children }) => <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ol>,
          li: ({ children }) => <li style={{ margin: "2px 0" }}>{children}</li>,
          strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
          em: ({ children }) => <em style={{ fontStyle: "italic" }}>{children}</em>,
          code: CodeBlock,
          pre: ({ children }) => <>{children}</>,
          blockquote: ({ children }) => (
            <blockquote style={{ borderLeft: "3px solid rgba(255,255,255,0.3)", margin: "6px 0", paddingLeft: 10, opacity: 0.95 }}>
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => {
            if (onOpenPath && href && isFileLikePath(href)) {
              return (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); onOpenPath(href); }}
                  style={{ background: "none", border: "none", padding: 0, color: "#58a6ff", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
                >
                  {children}
                </button>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#58a6ff", textDecoration: "underline" }}>
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function MonacoIframe({ value, language = "javascript", onChange, onCommand, revealRequest, markers }) {
  const iframeRef = useRef(null);
  const lastValueRef = useRef(String(value || ""));
  const pendingRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!e?.data || e.data.__zolaidMonaco !== true) return;
      if (e.data.type === "change") {
        const v = String(e.data.value || "");
        lastValueRef.current = v;
        if (typeof onChange === "function") onChange(v);
      }
      if (e.data.type === "cursor") {
        try {
          if (typeof onCommand === "function") {
            onCommand({
              name: "__cursor",
              line: Number(e.data.line || 1),
              column: Number(e.data.column || 1),
            });
          }
        } catch (_) {}
      }
      if (e.data.type === "command") {
        try {
          if (typeof onCommand === "function") {
            onCommand({
              name: String(e.data.name || ""),
              line: Number(e.data.line || 1),
              column: Number(e.data.column || 1),
            });
          }
        } catch (_) {}
      }
      if (e.data.type === "ready") {
        // push latest
        try {
          iframeRef.current?.contentWindow?.postMessage(
            { __zolaidHost: true, type: "set", value: lastValueRef.current, language },
            "*"
          );
        } catch (_) {}
      }
      if (e.data.type === "inlineCompletionRequest") {
        const { requestId, position, modelValue, language: reqLang } = e.data;
        const source = e.source;
        if (!requestId || !source) return;
        (async () => {
          try {
            const data = await apiPost("/chat/complete", {
              prefix: typeof modelValue === "string" ? modelValue : "",
              language: typeof reqLang === "string" ? reqLang : language || "javascript",
            });
            source.postMessage(
              { __zolaidHost: true, type: "inlineCompletionResponse", requestId, insertText: data?.completion ?? "" },
              "*"
            );
          } catch (err) {
            source.postMessage(
              { __zolaidHost: true, type: "inlineCompletionResponse", requestId, insertText: "", error: String(err?.message || "Completion failed") },
              "*"
            );
          }
        })();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [language, onChange, onCommand]);

  useEffect(() => {
    const next = String(value || "");
    if (next === lastValueRef.current) return;
    lastValueRef.current = next;
    try {
      if (pendingRef.current) clearTimeout(pendingRef.current);
      pendingRef.current = setTimeout(() => {
        try {
          iframeRef.current?.contentWindow?.postMessage(
            { __zolaidHost: true, type: "set", value: next, language },
            "*"
          );
        } catch (_) {}
      }, 50);
    } catch (_) {}
  }, [value, language]);

  useEffect(() => {
    const r = revealRequest && typeof revealRequest === "object" ? revealRequest : null;
    if (!r) return;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { __zolaidHost: true, type: "reveal", line: Number(r.line || 1), column: Number(r.column || 1) },
        "*"
      );
    } catch (_) {}
  }, [revealRequest]);

  useEffect(() => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { __zolaidHost: true, type: "markers", markers: Array.isArray(markers) ? markers : [] },
        "*"
      );
    } catch (_) {}
  }, [markers]);

  const srcDoc = useMemo(() => {
    // Monaco via CDN (no npm deps). Uses loader.js + vs path.
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { height: 100%; margin: 0; background: #0b1020; }
      #root { height: 100%; }
      .bar { height: 34px; display:flex; gap:8px; align-items:center; padding: 0 10px; color: rgba(255,255,255,0.8); font: 600 12px system-ui; border-bottom: 1px solid rgba(255,255,255,0.08); }
      .pill { padding: 4px 8px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.06); }
      #editor { height: calc(100% - 34px); }
    </style>
    <script src="https://unpkg.com/monaco-editor@0.47.0/min/vs/loader.js"></script>
  </head>
  <body>
    <div id="root">
      <div class="bar">
        <span class="pill">Monaco (CDN)</span>
        <span id="lang" class="pill"></span>
      </div>
      <div id="editor"></div>
    </div>
    <script>
      (function(){
        var currentLanguage = "javascript";
        var currentValue = "";
        var editor = null;
        var pendingCompletions = {};

        function send(type, payload){
          try { parent.postMessage(Object.assign({ __zolaidMonaco: true, type: type }, payload||{}), "*"); } catch (_) {}
        }

        function setLangPill(lang){
          try { document.getElementById("lang").textContent = String(lang || ""); } catch (_) {}
        }

        window.addEventListener("message", function(ev){
          try {
            var d = ev && ev.data ? ev.data : null;
            if (!d || d.__zolaidHost !== true) return;
            if (d.type === "set") {
              currentLanguage = String(d.language || "javascript");
              currentValue = String(d.value || "");
              setLangPill(currentLanguage);
              if (editor) {
                var m = editor.getModel();
                if (m) monaco.editor.setModelLanguage(m, currentLanguage);
                if (editor.getValue() !== currentValue) editor.setValue(currentValue);
              }
            }
            if (d.type === "reveal" && editor) {
              var line = Number(d.line || 1);
              var col = Number(d.column || 1);
              editor.revealPositionInCenter({ lineNumber: line, column: col });
              editor.setPosition({ lineNumber: line, column: col });
              editor.focus();
            }
            if (d.type === "markers" && editor) {
              try {
                var m2 = editor.getModel();
                if (!m2) return;
                var markers = Array.isArray(d.markers) ? d.markers : [];
                monaco.editor.setModelMarkers(m2, "zolaid-ts", markers);
              } catch (_) {}
            }
            if (d.type === "inlineCompletionResponse") {
              var rid = d.requestId;
              var entry = pendingCompletions[rid];
              if (entry) {
                delete pendingCompletions[rid];
                var insertText = typeof d.insertText === "string" ? d.insertText : "";
                entry.resolve({ items: insertText ? [{ insertText: insertText }] : [] });
              }
            }
          } catch (_) {}
        }, true);

        require.config({ paths: { vs: "https://unpkg.com/monaco-editor@0.47.0/min/vs" } });
        require(["vs/editor/editor.main"], function(){
          setLangPill(currentLanguage);
          editor = monaco.editor.create(document.getElementById("editor"), {
            value: currentValue,
            language: currentLanguage,
            theme: "vs-dark",
            automaticLayout: true,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontLigatures: true,
            fontSize: 13,
            lineHeight: 20,
            padding: { top: 10, bottom: 10 },
            cursorBlinking: "smooth",
            cursorSmoothCaretAnimation: "on",
            cursorStyle: "line",
            renderLineHighlight: "all",
            renderWhitespace: "selection",
            smoothScrolling: true,
            mouseWheelScrollSensitivity: 1,
            fastScrollSensitivity: 5,
            minimap: { enabled: true },
            lineNumbersMinChars: 4,
            quickSuggestions: { other: true, comments: false, strings: true },
            suggestOnTriggerCharacters: true,
            parameterHints: { enabled: true },
            acceptSuggestionOnEnter: "on",
            tabCompletion: "on",
            wordWrap: "off",
            scrollBeyondLastLine: false,
          });

          editor.onDidChangeModelContent(function(){
            var v = editor.getValue();
            send("change", { value: v });
          });

          editor.onDidChangeCursorPosition(function(){
            try {
              var pos = editor.getPosition();
              send("cursor", { line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Ctrl/Cmd+Click â†’ go to definition (delegated to host/backend)
          editor.onMouseDown(function(e){
            try {
              if (!e || !e.event || !e.target) return;
              var ev2 = e.event;
              if (!ev2.leftButton) return;
              var isMod = !!(ev2.ctrlKey || ev2.metaKey);
              if (!isMod) return;
              var pos2 = e.target.position;
              if (!pos2) return;
              send("command", { name: "definition", line: pos2.lineNumber, column: pos2.column });
            } catch (_) {}
          });

          // Keybindings: IDE navigation
          editor.addCommand(monaco.KeyCode.F12, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "definition", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });
          editor.addCommand(monaco.KeyMod.Shift | monaco.KeyCode.F12, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "references", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Rename symbol (F2)
          editor.addCommand(monaco.KeyCode.F2, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "rename", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Find (Ctrl/Cmd+F) + Replace (Ctrl/Cmd+H)
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, function(){
            try { editor.getAction("actions.find") && editor.getAction("actions.find").run(); } catch (_) {}
          });
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH, function(){
            try { editor.getAction("editor.action.startFindReplaceAction") && editor.getAction("editor.action.startFindReplaceAction").run(); } catch (_) {}
          });

          // Go to Line (Ctrl/Cmd+G)
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyG, function(){
            try { editor.getAction("editor.action.gotoLine") && editor.getAction("editor.action.gotoLine").run(); } catch (_) {}
          });

          // Quick Fix (Ctrl/Cmd+.)
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Period, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "codefix", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Go to Symbol in file (Ctrl/Cmd+Shift+O)
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyO, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "symbols", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Format document (Shift+Alt+F)
          editor.addCommand(monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF, function(){
            try {
              var pos = editor.getPosition();
              send("command", { name: "format", line: pos.lineNumber, column: pos.column });
            } catch (_) {}
          });

          // Inline completions (ghost text) via host /chat/complete
          if (typeof monaco.languages.registerInlineCompletionsProvider === "function") {
            monaco.languages.registerInlineCompletionsProvider({ pattern: "**" }, {
              provideInlineCompletions: function(model, position, context, token) {
                var requestId = "ic_" + Date.now() + "_" + Math.random().toString(36).slice(2);
                var prefix = model.getValueInRange({ startLineNumber: 1, startColumn: 1 }, position);
                return new Promise(function(resolve) {
                  pendingCompletions[requestId] = { resolve: resolve };
                  send("inlineCompletionRequest", {
                    requestId: requestId,
                    position: { line: position.lineNumber, column: position.column },
                    modelValue: prefix,
                    language: (model.getLanguageId && model.getLanguageId()) || currentLanguage
                  });
                  token.onCancellationRequested && token.onCancellationRequested(function() {
                    if (pendingCompletions[requestId]) {
                      pendingCompletions[requestId].resolve({ items: [] });
                      delete pendingCompletions[requestId];
                    }
                  });
                  setTimeout(function() {
                    if (pendingCompletions[requestId]) {
                      pendingCompletions[requestId].resolve({ items: [] });
                      delete pendingCompletions[requestId];
                    }
                  }, 12000);
                });
              }
            });
          }

          send("ready", {});
        });
      })();
    </script>
  </body>
</html>`;
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Zolaid Dev Editor"
      style={{ width: "100%", height: "100%", border: "none", background: "#0b1020" }}
      sandbox="allow-scripts allow-same-origin"
      srcDoc={srcDoc}
    />
  );
}

export default function DeveloperIDE({
  generatedFiles = null,
  onUpdateGeneratedFile = null,
  onSyncWebPreview = null,
  onDeleteGeneratedFile = null,
  onRenameGeneratedFile = null,
  onQueueActions = null,
  /** Called when the active editor tab is a generated file (open or tab switch) â€” e.g. scroll Live Preview to #section */
  onIdeActiveGeneratedPathChange = null,
}) {
  const [uiTheme, setUiTheme] = useState(() => {
    try {
      const v = String(window?.localStorage?.getItem("zolaid.ide.theme") || "").trim();
      return v === "glass" ? "glass" : "vscode";
    } catch (_) {
      return "vscode";
    }
  });

  useEffect(() => {
    try {
      window?.localStorage?.setItem("zolaid.ide.theme", String(uiTheme || "vscode"));
    } catch (_) {}
  }, [uiTheme]);

  const [leftView, setLeftView] = useState("explorer"); // explorer | search | scm | run | extensions
  // Sidebar icon definitions (VS Code style)
  const sidebarIcons = [
    { id: 'explorer', icon: 'ðŸ“', label: 'Explorer' },
    { id: 'search', icon: 'ðŸ”', label: 'Search' },
    { id: 'scm', icon: 'ðŸ”€', label: 'Source Control' },
    { id: 'run', icon: 'â–¶ï¸', label: 'Run & Debug' },
    { id: 'extensions', icon: 'ðŸ§©', label: 'Extensions' },
  ];
  const [explorerMoreOpen, setExplorerMoreOpen] = useState(null); // { x, y } | null
  const [tabCtxMenu, setTabCtxMenu] = useState(null); // { key, source, path, x, y } | null
  const [enabled, setEnabled] = useState(null); // null unknown
  const [cwd, setCwd] = useState(""); // shown folder label (root by default)
  const [tree, setTree] = useState([]); // root items
  const [expandedDirs, setExpandedDirs] = useState(() => new Set()); // Set<dirPath>
  const [childrenByDir, setChildrenByDir] = useState(() => new Map()); // Map<dirPath, items[]>
  const [selectedPath, setSelectedPath] = useState("");
  const [selectedSource, setSelectedSource] = useState("repo"); // "repo" | "generated"
  const [content, setContent] = useState("");
  const [dirty, setDirty] = useState(false);
  const [autoSave, setAutoSave] = useState(false); // save-on-switch like VS Code
  const [openTabs, setOpenTabs] = useState([]); // [{ key, source, path }]
  const [splitEditors, setSplitEditors] = useState(false); // true = show two editors
  const [leftTabs, setLeftTabs] = useState([]); // tabs in left pane
  const [rightTabs, setRightTabs] = useState([]); // tabs in right pane
  const [activePane, setActivePane] = useState('left'); // 'left' or 'right'
  const openBuffersRef = useRef(new Map()); // key -> { content, dirty }
  const suppressMonacoChangeRef = useRef(null); // { expected:string, until:number }
  const livePreviewDebounceRef = useRef(null); // timer for pushing Code edits to Preview immediately
  const [status, setStatus] = useState("");
  const [buildOut, setBuildOut] = useState("");
  const [buildErr, setBuildErr] = useState("");
  const [activeFolder, setActiveFolder] = useState(""); // folder for breadcrumb ('' = root)
  const [fileIndex, setFileIndex] = useState([]); // array of file paths
  const [indexing, setIndexing] = useState(false);
  const [generatedExpanded, setGeneratedExpanded] = useState(() => new Set()); // Set<folderPath>
  const [generatedRootExpanded, setGeneratedRootExpanded] = useState(true);
  const [terminalOpen, setTerminalOpen] = useState(true);
  const [bottomTab, setBottomTab] = useState("terminal"); // problems | output | debug | terminal | ports
  const [ports, setPorts] = useState(() => [
    { port: 3000, label: "Frontend (React)" },
    { port: 8080, label: "Backend (DevTools)" },
  ]);
  const [portDraft, setPortDraft] = useState("");
  const [outlineCollapsed, setOutlineCollapsed] = useState(false);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [outlineBusy, setOutlineBusy] = useState(false);
  const [outlineSymbols, setOutlineSymbols] = useState([]);
  const [outlineErr, setOutlineErr] = useState("");
  const outlineAbortRef = useRef(null);
  const outlineTimerRef = useRef(null);
  const [timelineEvents, setTimelineEvents] = useState([]);

  const hasActiveEditor = Boolean(selectedPath);
  const canCloseOthersEditors = Array.isArray(openTabs) && openTabs.length > 1;
  const [shellCwd, setShellCwd] = useState(".zolaid_generated"); // "" (repo root) | ".zolaid_generated"
  const [shellId, setShellId] = useState("");
  const canRunTerminalTasks = Boolean(shellId);
  const shellEsRef = useRef(null);
  const [shellLog, setShellLog] = useState(""); // terminal output log
  const [shellInput, setShellInput] = useState(""); // current input in terminal
  const shellInputRef = useRef(null); // reference to the input field
  const [shellHistory, setShellHistory] = useState([]); // command history
  const [shellHistoryIdx, setShellHistoryIdx] = useState(-1); // for up/down navigation
  const shellLogRef = useRef(null); // reference to the log display
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOut, setAiOut] = useState("");
  const [aiPlanOut, setAiPlanOut] = useState("");
  const [executablePlanSteps, setExecutablePlanSteps] = useState([]); // [{ id, description, confidence, files?, textEdits?, applied }]
  const [planStepSnapshots, setPlanStepSnapshots] = useState([]); // [{ path: content }] per applied step for rollback
  const [planStepExpanded, setPlanStepExpanded] = useState(() => new Set()); // step indices expanded to show files
  const [patchShowUnifiedDiff, setPatchShowUnifiedDiff] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [aiLastIntent, setAiLastIntent] = useState("send"); // send | plan | patch | agent
  const [agentRunning, setAgentRunning] = useState(false);
  const [activityDots, setActivityDots] = useState(1); // 1..3
  const [activityDetail, setActivityDetail] = useState("");
  const [aiTrace, setAiTrace] = useState([]); // activity log lines
  const aiTraceLastRef = useRef("");
  const aiOutputRef = useRef(null);
  const conversationScrollRef = useRef(null);
  const [aiOutputStickBottom, setAiOutputStickBottom] = useState(true);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const [aiToolsPos, setAiToolsPos] = useState({ x: 0, y: 0 });
  const aiToolsBtnRef = useRef(null);
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [agentMenuPos, setAgentMenuPos] = useState({ x: 0, y: 0 });
  const agentMenuBtnRef = useRef(null);
  const [leftMenuOpen, setLeftMenuOpen] = useState(null); // "File"|"Edit"|"Selection"|"View"|"Go"|"Run"|"Terminal"|"Help"|null
  const [leftMenuPos, setLeftMenuPos] = useState({ x: 0, y: 0 });
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newItemKind, setNewItemKind] = useState("file"); // file | folder
  const [newItemFolder, setNewItemFolder] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemExt, setNewItemExt] = useState("js");
  const [aiAttachments, setAiAttachments] = useState([]); // [{ name, type, size, text?: string, truncated?: boolean, note?: string }]
  const aiFileInputRef = useRef(null);
  const [aiAttachBusy, setAiAttachBusy] = useState(false);
  const [patchBusy, setPatchBusy] = useState(false);
  const [patchFiles, setPatchFiles] = useState(null); // { path: content }
  const [patchSummary, setPatchSummary] = useState("");
  const [patchBaseFiles, setPatchBaseFiles] = useState(null); // { path: contentBefore }
  const [lastAppliedPatchSnapshot, setLastAppliedPatchSnapshot] = useState(null); // { paths: string[], before: { [path]: content } } for rollback
  const [patchExpanded, setPatchExpanded] = useState(() => new Set()); // Set<path>
  const [patchSelected, setPatchSelected] = useState(() => new Set()); // Set<path> selected to apply
  const [lastAiContractMode, setLastAiContractMode] = useState(null); // "plan" | "patch" | null for badge
  const [agentMode, setAgentMode] = useState(false);
  const [agentTask, setAgentTask] = useState("frontend_build"); // frontend_build | frontend_test | frontend_lint
  const [agentAutoApply, setAgentAutoApply] = useState(false);
  const [agentAutoFixOnce, setAgentAutoFixOnce] = useState(false);
  const [agentMaxFixIters, setAgentMaxFixIters] = useState(2);
  const [agentLog, setAgentLog] = useState("");
  const [agentProcId, setAgentProcId] = useState("");
  const agentEsRef = useRef(null);
  const agentStopRef = useRef(false);

  // VS Code-style rename UI (no window.prompt)
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameCtx, setRenameCtx] = useState(null); // { oldName, locations, selectedPath, selectedContent }
  const renameInputRef = useRef(null);

  // Save Asâ€¦ (normal editor behavior)
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsTarget, setSaveAsTarget] = useState("generated"); // generated | repo
  const [saveAsFolder, setSaveAsFolder] = useState(""); // e.g. frontend/src
  const [saveAsName, setSaveAsName] = useState(""); // basename only
  const [saveAsExt, setSaveAsExt] = useState("js"); // no dot
  const [scanBusy, setScanBusy] = useState(false);
  const [scanSummary, setScanSummary] = useState("");
  const [aiUseScan, setAiUseScan] = useState(true);
  // Default: automatically attach relevant files unless user opts out
  const [aiAttachRelevant, setAiAttachRelevant] = useState(true);
  const [aiIncludeGenerated, setAiIncludeGenerated] = useState(true);
  const [aiQuality, setAiQuality] = useState("fast"); // fast | best
  // Model selector (same list as chatbot; backend resolves to OpenAI when needed)
  const IDE_MODELS_DEFAULT = [
    { id: "gpt-4o-mini", name: "GPT-4o Mini (fast)" },
    { id: "gpt-4o", name: "GPT-4o" },
    { id: "claude-3-opus-20240229", name: "Claude 3 Opus" },
    { id: "gemini-1.5-pro-latest", name: "Gemini 1.5 Pro" },
  ];
  const [ideModels, setIdeModels] = useState(IDE_MODELS_DEFAULT);
  const [ideSelectedModel, setIdeSelectedModel] = useState(() => {
    try {
      return localStorage.getItem("zolaid-ide-model") || IDE_MODELS_DEFAULT[0].id;
    } catch (_) {
      return IDE_MODELS_DEFAULT[0].id;
    }
  });
  const [aiReasoningStages, setAiReasoningStages] = useState([]); // [{ stage, icon, text, duration }]
  const [aiCurrentStage, setAiCurrentStage] = useState(null); // { stage, icon, text }
  const [aiStreamingResponse, setAiStreamingResponse] = useState(""); // for character-by-character display
  const [streamingMessageId, setStreamingMessageId] = useState(null); // id of assistant message being typed
  const [streamingFullText, setStreamingFullText] = useState(""); // full text to reveal word-by-word
  const [streamingWordIndex, setStreamingWordIndex] = useState(0);
  const streamingIntervalRef = useRef(null);
  // Conversation: each user/assistant turn is a message in the panel
  const [aiConversation, setAiConversation] = useState([]); // [{ id, role: 'user'|'assistant', content, contract? }]
  const [aiCodeAnalysis, setAiCodeAnalysis] = useState(null); // Analysis from AdvancedAIEngine
  const [aiSuggestions, setAiSuggestions] = useState([]); // Smart suggestions
  const [aiBugs, setAiBugs] = useState([]); // Bug analysis results
  const [aiRefactorings, setAiRefactorings] = useState([]); // Refactoring suggestions
  const [aiPerformance, setAiPerformance] = useState([]); // Performance issues
  const [aiShowAdvanced, setAiShowAdvanced] = useState(false); // Toggle advanced features panel
  const aiEngineRef = useRef(new AdvancedAIEngine());
  const retrievalCacheRef = useRef(new Map()); // key -> { at:number, value:string }
  const retrievalAbortRef = useRef({ ctx: null, search: null }); // AbortController(s)
  const autoScanOnceRef = useRef(false);
  /** Once per IDE mount: focus Generated + starter entry so Code view shows the built-in kit (not repo-only). */
  const starterExplorerSeededRef = useRef(false);
  const projectGraphCacheRef = useRef({ graph: null, at: 0 }); // 2-min TTL
  const getProjectGraph = useCallback(async () => {
    const now = Date.now();
    if (projectGraphCacheRef.current.graph && (now - projectGraphCacheRef.current.at) < 120000)
      return projectGraphCacheRef.current.graph;
    try {
      const graphData = await apiGet("/devtools/project-graph");
      const graph = graphData?.graph && typeof graphData.graph === "object" ? graphData.graph : {};
      projectGraphCacheRef.current = { graph, at: now };
      return graph;
    } catch (_) {
      return {};
    }
  }, []);

  // Latest models fetched on first backend use to avoid 500 on initial load when backend is down.
  const [navSelection, setNavSelection] = useState(null); // { source:"repo"|"generated", path:string } for single-click selection
  const [ctxMenu, setCtxMenu] = useState(null); // { source, path, x, y, isDir }
  const [quickFixOpen, setQuickFixOpen] = useState(false);
  const [quickFixBusy, setQuickFixBusy] = useState(false);
  const [quickFixDiag, setQuickFixDiag] = useState(null);
  const [quickFixFixes, setQuickFixFixes] = useState([]); // fixes from /devtools/ts/codefix
  const [symbolOpen, setSymbolOpen] = useState(false);
  const [symbolBusy, setSymbolBusy] = useState(false);
  const [symbolQuery, setSymbolQuery] = useState("");
  const [symbolIndex, setSymbolIndex] = useState(0);
  const [symbolItems, setSymbolItems] = useState([]); // [{name,kind,container,line,character}]
  const symbolRef = useRef(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [hoverText, setHoverText] = useState("");
  const [cursorQuickInfo, setCursorQuickInfo] = useState("");
  const cursorQuickInfoRef = useRef({ t: null, abort: null, lastKey: "" });
  const [cursorPos, setCursorPos] = useState({ line: 1, column: 1 });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [paletteIndex, setPaletteIndex] = useState(0);
  const paletteRef = useRef(null);

  // Global keybinding for command palette (Ctrl+Shift+P)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [findRoot, setFindRoot] = useState("");
  const [findMax, setFindMax] = useState(80);
  const [findBusy, setFindBusy] = useState(false);
  const [findResults, setFindResults] = useState([]); // [{path,line,text}]
  const [gotoOpen, setGotoOpen] = useState(false);
  const [gotoQuery, setGotoQuery] = useState("");
  const [gotoIndex, setGotoIndex] = useState(0);
  const [gotoIndexing, setGotoIndexing] = useState(false);
  const gotoRef = useRef(null);
  const [tsDiagnostics, setTsDiagnostics] = useState([]); // [{message, category, code, start:{line,character}, end:{line,character}}]
  const [tsRefs, setTsRefs] = useState([]); // [{path,start,end,isDefinition}]
  const [tsPanel, setTsPanel] = useState("problems"); // problems | references
  const [revealRequest, setRevealRequest] = useState(null); // { line, column, nonce }
  const [monacoMarkers, setMonacoMarkers] = useState([]); // monaco marker objects
  const diagDebounceRef = useRef(null);
  const pendingRevealRef = useRef(null); // { path, line, column }

  const language = useMemo(() => {
    const p = String(selectedPath || "").toLowerCase();
    if (p.endsWith(".ts") || p.endsWith(".tsx")) return "typescript";
    if (p.endsWith(".js") || p.endsWith(".jsx")) return "javascript";
    if (p.endsWith(".json")) return "json";
    if (p.endsWith(".css")) return "css";
    if (p.endsWith(".html")) return "html";
    if (p.endsWith(".md")) return "markdown";
    return "plaintext";
  }, [selectedPath]);

  const isTsLike = useMemo(() => {
    const p = String(selectedPath || "").toLowerCase();
    return p.endsWith(".ts") || p.endsWith(".tsx") || p.endsWith(".js") || p.endsWith(".jsx");
  }, [selectedPath]);

  const applyRevealIfPending = useRef(null);
  applyRevealIfPending.current = () => {
    try {
      const pend = pendingRevealRef.current;
      if (!pend) return;
      if (String(pend.path || "") !== String(selectedPath || "")) return;
      pendingRevealRef.current = null;
      setRevealRequest({ line: Number(pend.line || 1), column: Number(pend.column || 1), nonce: Date.now() });
    } catch (_) {}
  };

  useEffect(() => {
    // when file opens, apply any pending reveal
    applyRevealIfPending.current?.();
  }, [selectedPath, content]);

  const refreshDiagnostics = async (path, text) => {
    if (!path || !isTsLike) return;
    try {
      const data = await apiPost("/devtools/ts/diagnostics", { path, content: String(text || ""), max: 200 });
      const diags = Array.isArray(data.diagnostics) ? data.diagnostics : [];
      setTsDiagnostics(diags);
      const markers = diags.map((d) => {
        const cat = String(d.category || "").toLowerCase();
        const severity = cat === "warning" ? 4 : 8; // monaco MarkerSeverity: Warning=4, Error=8
        return {
          startLineNumber: Number(d.start?.line || 1),
          startColumn: Number(d.start?.character || 1),
          endLineNumber: Number(d.end?.line || d.start?.line || 1),
          endColumn: Number(d.end?.character || d.start?.character || 1),
          message: String(d.message || ""),
          severity,
        };
      });
      setMonacoMarkers(markers);
    } catch (_) {}
  };

  const handleMonacoCommand = async ({ name, line, column }) => {
    try {
      if (String(name || "") === "__cursor") {
        setCursorPos({ line: Number(line || 1), column: Number(column || 1) });
        return;
      }
      if (!selectedPath) return;
      if (!isTsLike) return;
      const cmd = String(name || "").toLowerCase();
      if (cmd === "symbols") {
        await openSymbolPicker();
        return;
      }
      if (cmd === "format") {
        await formatDocument();
        return;
      }
      if (cmd === "definition") {
        const data = await apiPost("/devtools/ts/definition", {
          path: selectedPath,
          line: Number(line || 1),
          character: Number(column || 1),
          content: String(content || ""),
        });
        if (!data?.found) return;
        const targetPath = String(data.path || "");
        const targetLine = Number(data.line || 1);
        const targetCol = Number(data.character || 1);
        if (!targetPath) return;
        if (targetPath !== selectedPath) {
          pendingRevealRef.current = { path: targetPath, line: targetLine, column: targetCol };
          await openFile(targetPath);
          return;
        }
        setRevealRequest({ line: targetLine, column: targetCol, nonce: Date.now() });
        return;
      }
      if (cmd === "references") {
        const data = await apiPost("/devtools/ts/references", {
          path: selectedPath,
          line: Number(line || 1),
          character: Number(column || 1),
          content: String(content || ""),
          max: 200,
        });
        const results = Array.isArray(data?.results) ? data.results : [];
        setTsRefs(results);
        setTsPanel("references");
        return;
      }
      if (cmd === "rename") {
        const ln = Number(line || 1);
        const col = Number(column || 1);
        try {
          const data = await apiPost("/devtools/ts/prepareRename", {
            path: selectedPath,
            line: ln,
            character: col,
            content: String(content || ""),
          });
          const oldName = data?.oldName != null ? String(data.oldName) : "";
          const locs = Array.isArray(data?.locations) ? data.locations : [];
          if (!locs.length) {
            setStatus("No rename locations");
            setTimeout(() => setStatus(""), 900);
            return;
          }
          setRenameCtx({
            oldName,
            locations: locs,
            selectedPath: selectedPath || "",
            selectedContent: String(content || ""),
          });
          setRenameValue(oldName);
          setRenameOpen(true);
          setTimeout(() => renameInputRef.current?.focus?.(), 0);
        } catch (err) {
          setStatus(err?.message || "Rename not available");
          setTimeout(() => setStatus(""), 1200);
        }
        return;
      }
      if (cmd === "codefix") {
        const ln = Number(line || 1);
        const col = Number(column || 1);
        const diags = Array.isArray(tsDiagnostics) ? tsDiagnostics : [];
        const hit =
          diags.find((d) => {
            const sL = Number(d.start?.line || 1);
            const sC = Number(d.start?.character || 1);
            const eL = Number(d.end?.line || sL);
            const eC = Number(d.end?.character || sC);
            if (ln < sL || ln > eL) return false;
            if (ln === sL && col < sC) return false;
            if (ln === eL && col > eC) return false;
            return true;
          }) || diags[0];
        if (!hit) {
          setStatus("No quick fixes");
          setTimeout(() => setStatus(""), 900);
          return;
        }
        await openQuickFixPicker(hit);
        return;
      }
    } catch (_) {}
  };

  const applyTextEdits = (text, edits) => {
    const src = String(text || "");
    const arr = Array.isArray(edits) ? edits : [];
    const sorted = arr
      .map((e) => ({ start: Number(e.start || 0), length: Number(e.length || 0), newText: String(e.newText ?? "") }))
      .filter((e) => Number.isFinite(e.start) && Number.isFinite(e.length) && e.start >= 0 && e.length >= 0)
      .sort((a, b) => b.start - a.start);
    let out = src;
    for (const e of sorted) {
      const s = Math.max(0, Math.min(out.length, e.start));
      const end = Math.max(0, Math.min(out.length, s + e.length));
      out = out.slice(0, s) + e.newText + out.slice(end);
    }
    return out;
  };

  // Convert range-based edits (Monaco-style: 1-based line/column) to start/length and apply.
  const applyTextEditsByRange = (text, rangeEdits) => {
    const lines = String(text || "").split("\n");
    const toOffset = (line, col) => {
      let o = 0;
      for (let i = 0; i < line - 1 && i < lines.length; i++) o += lines[i].length + 1;
      return o + Math.max(0, (col || 1) - 1);
    };
    const edits = (Array.isArray(rangeEdits) ? rangeEdits : [])
      .map((e) => {
        const r = e?.range && typeof e.range === "object" ? e.range : {};
        const sL = Number(r.startLine ?? r.start_line ?? 1);
        const sC = Number(r.startColumn ?? r.start_character ?? 1);
        const eL = Number(r.endLine ?? r.end_line ?? sL);
        const eC = Number(r.endColumn ?? r.end_character ?? sC);
        if (!Number.isFinite(sL) || !Number.isFinite(sC) || !Number.isFinite(eL) || !Number.isFinite(eC)) return null;
        const start = toOffset(sL, sC);
        const end = toOffset(eL, eC);
        return { start, length: Math.max(0, end - start), newText: String(e?.newText ?? "") };
      })
      .filter(Boolean);
    return applyTextEdits(text, edits);
  };

  const preparePatchFromFiles = async (filesObj, summary) => {
    const files = filesObj && typeof filesObj === "object" ? filesObj : null;
    if (!files || !Object.keys(files).length) return;
    setPatchFiles(files);
    setPatchSummary(String(summary || "Patch ready"));
    setPatchSelected(new Set(Object.keys(files).map((p) => normalizePath(p)).filter(Boolean)));
    setPatchExpanded(new Set());
    setPatchBaseFiles(null);

    // Load "before" content for diff preview (best-effort)
    try {
      const base = {};
      const entries = Object.keys(files)
        .map((p) => normalizePath(p))
        .filter(Boolean)
        .slice(0, 60);
      for (const p of entries) {
        try {
          base[p] = (await apiGet("/devtools/read", { path: p }))?.content ?? "";
        } catch (_) {}
      }
      setPatchBaseFiles(base);
    } catch (_) {}
  };

  const confirmRename = async () => {
    const rctx = renameCtx;
    if (!rctx) return;
    try {
      const nextName = String(renameValue || "").trim();
      const oldName = String(rctx.oldName || "").trim();
      if (!nextName || nextName === oldName) {
        setRenameOpen(false);
        setRenameCtx(null);
        setRenameValue("");
        return;
      }

      const locs = Array.isArray(rctx.locations) ? rctx.locations : [];
      if (!locs.length) {
        setStatus("No rename locations");
        setTimeout(() => setStatus(""), 900);
        setRenameOpen(false);
        setRenameCtx(null);
        setRenameValue("");
        return;
      }

      const byFile = new Map();
      for (const l of locs) {
        const p = normalizePath(l?.path || "");
        const start = Number(l?.start || 0);
        const length = Number(l?.length || 0);
        if (!p || !Number.isFinite(start) || !Number.isFinite(length)) continue;
        const arr = byFile.get(p) || [];
        arr.push({ start, length, newText: String(nextName) });
        byFile.set(p, arr);
      }

      const files = {};
      for (const [p, edits] of byFile.entries()) {
        let base = "";
        try {
          if (p === String(rctx.selectedPath || "")) base = String(rctx.selectedContent || "");
          else base = String((await apiGet("/devtools/read", { path: p }))?.content ?? "");
        } catch (_) {
          base = "";
        }
        files[p] = applyTextEdits(base, edits);
      }

      await preparePatchFromFiles(files, `Rename: ${oldName || "(symbol)"} â†’ ${String(nextName)} (${locs.length} change(s))`);
      try {
        await applyPatchNow({ confirm: false, filesOverride: files, selectedOverride: new Set(Object.keys(files).map((fp) => normalizePath(fp)).filter(Boolean)) });
      } catch (_) {}
      setStatus("Renamed");
      setTimeout(() => setStatus(""), 900);
    } catch (e) {
      setStatus(e?.message || "Rename failed");
      setTimeout(() => setStatus(""), 1200);
    } finally {
      setRenameOpen(false);
      setRenameCtx(null);
      setRenameValue("");
    }
  };

  const applyCodeFix = async (fix) => {
    // Apply to disk immediately (also leaves a patch preview visible)
    const f = fix && typeof fix === "object" ? fix : null;
    if (!f) return;
    const changes = Array.isArray(f?.changes) ? f.changes : [];
    const byFile = new Map(); // path -> edits[]
    for (const ch of changes) {
      const p = normalizePath(ch?.path || "");
      const tcs = Array.isArray(ch?.textChanges) ? ch.textChanges : [];
      if (!p || !tcs.length) continue;
      byFile.set(
        p,
        tcs.map((tc) => ({
          start: Number(tc?.start || 0),
          length: Number(tc?.length || 0),
          newText: String(tc?.newText ?? ""),
        }))
      );
    }
    if (!byFile.size) {
      setStatus("No quick fixes");
      setTimeout(() => setStatus(""), 900);
      return;
    }

    const files = {};
    for (const [p, edits] of byFile.entries()) {
      let base = "";
      try {
        if (p === selectedPath) base = String(content || "");
        else base = String((await apiGet("/devtools/read", { path: p }))?.content ?? "");
      } catch (_) {
        base = "";
      }
      files[p] = applyTextEdits(base, edits);
    }

    const desc = String(f?.description || "Quick fix");
    await preparePatchFromFiles(files, `TS Quick Fix: ${desc}`);
    try {
      await applyPatchNow({ confirm: false, filesOverride: files, selectedOverride: new Set(Object.keys(files).map((p) => normalizePath(p)).filter(Boolean)) });
    } catch (_) {}
    setStatus("Quick fix patch ready");
    setTimeout(() => setStatus(""), 900);
  };

  const openQuickFixPicker = async (diag) => {
    try {
      if (!selectedPath || !isTsLike) return;
      const d = diag && typeof diag === "object" ? diag : null;
      if (!d) return;
      setQuickFixBusy(true);
      setQuickFixDiag(d);
      setQuickFixFixes([]);
      setQuickFixOpen(true);
      setStatus("Quick fixâ€¦");
      const data = await apiPost("/devtools/ts/codefix", {
        path: selectedPath,
        startLine: Number(d.start?.line || 1),
        startCharacter: Number(d.start?.character || 1),
        endLine: Number(d.end?.line || d.start?.line || 1),
        endCharacter: Number(d.end?.character || d.start?.character || 1),
        errorCode: Number(d.code || 0),
        content: String(content || ""),
        maxFixes: 8,
      });
      const fixes = Array.isArray(data?.fixes) ? data.fixes : [];
      if (!fixes.length) {
        setStatus("No quick fixes");
        setTimeout(() => setStatus(""), 900);
        setQuickFixFixes([]);
        return;
      }
      setQuickFixFixes(fixes);
      // If only one fix, apply immediately
      if (fixes.length === 1) {
        setQuickFixOpen(false);
        await applyCodeFix(fixes[0]);
      } else {
        setStatus("Pick a quick fixâ€¦");
        setTimeout(() => setStatus(""), 900);
      }
    } catch (e) {
      setStatus(e?.message || "Quick fix failed");
      setTimeout(() => setStatus(""), 1200);
      setQuickFixFixes([]);
    } finally {
      setQuickFixBusy(false);
    }
  };

  useEffect(() => {
    if (!selectedPath || !isTsLike) {
      setTsDiagnostics([]);
      setTsRefs([]);
      setMonacoMarkers([]);
      return;
    }
    try {
      if (diagDebounceRef.current) clearTimeout(diagDebounceRef.current);
      diagDebounceRef.current = setTimeout(() => {
        refreshDiagnostics(selectedPath, content);
      }, 450);
    } catch (_) {}
    return () => {
      try {
        if (diagDebounceRef.current) clearTimeout(diagDebounceRef.current);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, content, isTsLike]);

  // Inline understanding: show symbol/quick info for current editor position (debounced).
  useEffect(() => {
    try {
      if (!selectedPath || !isTsLike) {
        setCursorQuickInfo("");
        return;
      }
      const ln = Number(cursorPos?.line || 1);
      const col = Number(cursorPos?.column || 1);
      const key = `${selectedPath}@${ln}:${col}`;
      if (cursorQuickInfoRef.current.lastKey === key) return;
      cursorQuickInfoRef.current.lastKey = key;

      if (cursorQuickInfoRef.current.t) clearTimeout(cursorQuickInfoRef.current.t);
      try {
        cursorQuickInfoRef.current.abort?.abort?.();
      } catch (_) {}
      const ctrl = new AbortController();
      cursorQuickInfoRef.current.abort = ctrl;

      cursorQuickInfoRef.current.t = setTimeout(async () => {
        try {
          const data = await apiPost(
            "/devtools/ts/hover",
            { path: selectedPath, line: ln, character: col, content: String(content || "") },
            { signal: ctrl.signal }
          );
          const txt = data?.ok && data?.found ? String(data?.text || "").trim() : "";
          // Keep it short in the status bar; full text is shown in tooltip.
          setCursorQuickInfo(txt ? txt.split("\n")[0].slice(0, 140) : "");
        } catch (_) {
          // ignore
        }
      }, 550);
    } catch (_) {}
    return () => {
      try {
        if (cursorQuickInfoRef.current.t) clearTimeout(cursorQuickInfoRef.current.t);
        cursorQuickInfoRef.current.t = null;
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, isTsLike, cursorPos?.line, cursorPos?.column]);

  const refresh = async (path = cwd) => {
    const onLocalhost = typeof window !== "undefined" && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.origin);
    try {
      setStatus("Loadingâ€¦");
      let data;
      try {
        data = await apiGet("/devtools/list", { path: path || "" });
      } catch (e) {
        // If default backend unreachable and on localhost, retry on 8081 (backend may have fallen back to 8081)
        if (onLocalhost && /failed to fetch|network error|ECONNREFUSED|proxy error/i.test(String(e?.message || ""))) {
          try {
            data = await apiGet("/devtools/list", { path: path || "" }, 8081);
            _devToolsPreferredPort = 8081;
          } catch (e2) {
            throw e;
          }
        } else {
          throw e;
        }
      }
      setTree(Array.isArray(data.items) ? data.items : []);
      setCwd(String(data.path || ""));
      if (!path) {
        setExpandedDirs(new Set());
        setChildrenByDir(new Map());
        setActiveFolder("");
        setFileIndex([]);
      }
      setEnabled(true);
      setStatus("");
    } catch (e) {
      setEnabled(false);
      const msg = e?.message || "DevTools unavailable";
      const isNetwork = /failed to fetch|network error|load failed|ECONNREFUSED|proxy error/i.test(msg) || (e?.name === "TypeError" && /fetch/i.test(String(e)));
      setStatus(
        isNetwork
          ? "Backend not running. In the project root run: npm run start:backend â€” or npm run dev. Check http://localhost:8080/health (or :8081). Windows: start-backend.bat."
          : msg
      );
    }
  };

  const normalizeFolder = (p) => {
    const s = String(p || "").replace(/\\/g, "/").replace(/^\/+/, "").replace(/\/+$/, "");
    return s;
  };

  const tabKey = (source, path) => `${String(source || "repo")}:${String(path || "")}`;
  const tabLabel = (source, path) => {
    const p = String(path || "").replace(/\\/g, "/");
    const base = p.split("/").filter(Boolean).pop() || "(untitled)";
    return source === "generated" ? base : base;
  };

  const breadcrumbs = useMemo(() => {
    try {
      if (!selectedPath) return [];
      const p = String(selectedPath || "").replace(/\\/g, "/").replace(/^\/+/, "");
      return p.split("/").filter(Boolean);
    } catch (_) {
      return [];
    }
  }, [selectedPath]);

  const stashCurrentBuffer = () => {
    try {
      if (!selectedPath) return;
      const k = tabKey(selectedSource, selectedPath);
      openBuffersRef.current.set(k, { content: String(content || ""), dirty: Boolean(dirty) });
    } catch (_) {}
  };

  const maybeAutoSaveCurrent = async () => {
    try {
      if (!autoSave) return;
      if (!selectedPath) return;
      if (!dirty) return;
      // Save current buffer before switching
      if (selectedSource === "generated") {
        if (typeof onUpdateGeneratedFile !== "function") return;
        onUpdateGeneratedFile(selectedPath, String(content || ""));
        expandGeneratedTo(selectedPath);
      } else {
        await apiPost("/devtools/write", { path: selectedPath, content: String(content || "") });
      }
      setDirty(false);
      const k = tabKey(selectedSource, selectedPath);
      const prev = openBuffersRef.current.get(k) || {};
      openBuffersRef.current.set(k, { ...prev, content: String(content || ""), dirty: false });
    } catch (_) {}
  };

  const ensureTabOpen = (source, path) => {
    const p = String(path || "");
    if (!p) return;
    const src = source === "generated" ? "generated" : "repo";
    const k = tabKey(src, p);
    setOpenTabs((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.some((t) => t.key === k)) return arr;
      return [...arr, { key: k, source: src, path: p }];
    });
  };

  const closeTab = async (k) => {
    try {
      const key = String(k || "");
      if (!key) return;
      const buf = openBuffersRef.current.get(key);
      if (buf?.dirty) {
        const ok = window.confirm("This file has unsaved changes. Close and discard changes?");
        if (!ok) return;
      }
      openBuffersRef.current.delete(key);
      setOpenTabs((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        const idx = arr.findIndex((t) => t.key === key);
        const next = arr.filter((t) => t.key !== key);
        // If closing active tab, switch to neighbor
        if (key === tabKey(selectedSource, selectedPath)) {
          const pick = next[Math.min(idx, next.length - 1)] || next[next.length - 1];
          if (pick) {
            // best-effort switch (no autosave needed; already stashed/discarded)
            if (pick.source === "generated") openGeneratedFile(pick.path);
            else openFile(pick.path);
          } else {
            setSelectedPath("");
            setSelectedSource("repo");
            setContent("");
            setDirty(false);
          }
        }
        return next;
      });
    } catch (_) {}
  };

  const openTabContextMenu = (e, tab) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      const t = tab && typeof tab === "object" ? tab : null;
      if (!t) return;
      setTabCtxMenu({
        key: String(t.key || ""),
        source: String(t.source || "repo"),
        path: String(t.path || ""),
        x: Number(e.clientX || 8),
        y: Number(e.clientY || 8),
      });
    } catch (_) {}
  };

  const clipboardWrite = async (text) => {
    const s = String(text ?? "");
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s);
      setStatus("Copied");
      setTimeout(() => setStatus(""), 800);
      return;
    } catch (_) {}
    // fallback
    try {
      const ta = document.createElement("textarea");
      ta.value = s;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setStatus("Copied");
      setTimeout(() => setStatus(""), 800);
    } catch (_) {}
  };

  const downloadTextFile = (filename, text) => {
    try {
      const name = String(filename || "file.txt");
      const blob = new Blob([String(text ?? "")], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
    } catch (_) {}
  };

  const closeAllTabs = async () => {
    try {
      const dirtyKeys = [];
      for (const t of Array.isArray(openTabs) ? openTabs : []) {
        const k = String(t?.key || "");
        const buf = openBuffersRef.current.get(k);
        if (buf?.dirty) dirtyKeys.push(k);
      }
      if (dirtyKeys.length) {
        const ok = window.confirm("Some tabs have unsaved changes. Close all tabs and discard changes?");
        if (!ok) return;
      }
      openBuffersRef.current = new Map();
      setOpenTabs([]);
      setSelectedPath("");
      setSelectedSource("repo");
      setContent("");
      setDirty(false);
    } catch (_) {}
  };

  const closeOtherTabs = async (keepKey) => {
    try {
      const keep = String(keepKey || "");
      const arr = Array.isArray(openTabs) ? openTabs : [];
      const toClose = arr.filter((t) => String(t?.key || "") !== keep);
      const dirtyAny = toClose.some((t) => openBuffersRef.current.get(String(t?.key || ""))?.dirty);
      if (dirtyAny) {
        const ok = window.confirm("Other tabs have unsaved changes. Close them and discard changes?");
        if (!ok) return;
      }
      for (const t of toClose) openBuffersRef.current.delete(String(t?.key || ""));
      setOpenTabs(arr.filter((t) => String(t?.key || "") === keep));
    } catch (_) {}
  };

  const suggestCopyName = (p) => {
    const s = String(p || "").replace(/\\/g, "/");
    const base = s.split("/").pop() || "file";
    const dot = base.lastIndexOf(".");
    const name = dot > 0 ? base.slice(0, dot) : base;
    const ext = dot > 0 ? base.slice(dot) : "";
    return `${name}-copy${ext}`;
  };

  const uniqueGeneratedPath = (basePath) => {
    const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
    const exists = (p) => Object.prototype.hasOwnProperty.call(src, p);
    let p = normalizePath(basePath);
    if (!exists(p)) return p;
    // add -2, -3...
    const s = String(p);
    const dir = s.includes("/") ? s.slice(0, s.lastIndexOf("/")) : "";
    const base = s.split("/").pop() || s;
    const dot = base.lastIndexOf(".");
    const name = dot > 0 ? base.slice(0, dot) : base;
    const ext = dot > 0 ? base.slice(dot) : "";
    for (let i = 2; i <= 50; i++) {
      const candidate = normalizePath(dir ? `${dir}/${name}-${i}${ext}` : `${name}-${i}${ext}`);
      if (!exists(candidate)) return candidate;
    }
    return p;
  };

  const duplicatePath = async ({ source, path }) => {
    const p = normalizePath(path);
    if (!p) return;
    try {
      setStatus("Duplicatingâ€¦");
      if (source === "generated") {
        const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
        const content = String(src[p] ?? "");
        const dir = p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "";
        const copyBase = suggestCopyName(p);
        const out = uniqueGeneratedPath(normalizePath(dir ? `${dir}/${copyBase}` : copyBase));
        if (typeof onUpdateGeneratedFile !== "function") throw new Error("Generated files store is not available.");
        onUpdateGeneratedFile(out, content);
        ensureTabOpen("generated", out);
        pushTimeline({ kind: "duplicate", text: `Duplicate generated: ${p} â†’ ${out}`, source: "generated", path: out });
        setStatus("Duplicated");
        setTimeout(() => setStatus(""), 900);
        return;
      }
      // repo
      const data = await apiGet("/devtools/read", { path: p });
      const content = String(data?.content ?? "");
      const dir = p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "";
      const copyBase = suggestCopyName(p);
      const out = normalizePath(dir ? `${dir}/${copyBase}` : copyBase);
      await apiPost("/devtools/write", { path: out, content });
      ensureTabOpen("repo", out);
      pushTimeline({ kind: "duplicate", text: `Duplicate repo: ${p} â†’ ${out}`, source: "repo", path: out });
      setStatus("Duplicated");
      setTimeout(() => setStatus(""), 900);
    } catch (e) {
      setStatus(e?.message || "Duplicate failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const openNewFileInFolder = (folderPath) => {
    setNewItemKind("file");
    setNewItemFolder(normalizeFolder(folderPath));
    setNewItemName("new-file");
    setNewItemExt("js");
    setNewItemOpen(true);
  };

  const openNewFolderInFolder = (folderPath) => {
    setNewItemKind("folder");
    setNewItemFolder(normalizeFolder(folderPath));
    setNewItemName("new-folder");
    setNewItemExt("js");
    setNewItemOpen(true);
  };

  const folderOfPath = (p) => {
    const s = String(p || "").replace(/\\/g, "/");
    const idx = s.lastIndexOf("/");
    if (idx <= 0) return "";
    return normalizeFolder(s.slice(0, idx));
  };

  const loadChildren = async (dirPath) => {
    const p = String(dirPath || "");
    if (!p) return;
    // If already loaded, no-op
    if (childrenByDir.has(p)) return;
    try {
      setStatus("Loadingâ€¦");
      const data = await apiGet("/devtools/list", { path: p });
      const items = Array.isArray(data.items) ? data.items : [];
      setChildrenByDir((prev) => {
        const next = new Map(prev);
        next.set(p, items);
        return next;
      });
      setStatus("");
    } catch (e) {
      setStatus(e?.message || "Failed to load folder");
    }
  };

  const toggleDir = async (dirPath) => {
    const p = String(dirPath || "");
    if (!p) return;
    setActiveFolder(normalizeFolder(p));
    const isExpanded = expandedDirs.has(p);
    if (isExpanded) {
      setExpandedDirs((prev) => {
        const next = new Set(prev);
        next.delete(p);
        return next;
      });
      return;
    }
    await loadChildren(p);
    setExpandedDirs((prev) => {
      const next = new Set(prev);
      next.add(p);
      return next;
    });
  };

  useEffect(() => {
    refresh("").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      try {
        // Esc closes overlays
        if (String(e.key || "") === "Escape") {
          if (paletteOpen) {
            e.preventDefault();
            setPaletteOpen(false);
            return;
          }
          if (ctxMenu) {
            e.preventDefault();
            setCtxMenu(null);
            return;
          }
          if (quickFixOpen) {
            e.preventDefault();
            setQuickFixOpen(false);
            return;
          }
          if (saveAsOpen) {
            e.preventDefault();
            setSaveAsOpen(false);
            return;
          }
          if (findOpen) {
            e.preventDefault();
            setFindOpen(false);
            return;
          }
          if (gotoOpen) {
            e.preventDefault();
            setGotoOpen(false);
            return;
          }
          if (symbolOpen) {
            e.preventDefault();
            setSymbolOpen(false);
            return;
          }
          if (hoverOpen) {
            e.preventDefault();
            setHoverOpen(false);
            return;
          }
        }

        // Ctrl/Cmd+P â†’ Focus AI prompt (Quick Open removed)
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && String(e.key || "").toLowerCase() === "p") {
          e.preventDefault();
          try { aiPromptRef.current?.scrollIntoView?.({ block: "center" }); } catch (_) {}
          aiPromptRef.current?.focus?.();
          return;
        }
        // Ctrl/Cmd+Shift+F â†’ Find in files (VS Code-like)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && String(e.key || "").toLowerCase() === "f") {
          e.preventDefault();
          setFindOpen(true);
          setFindQuery("");
          setFindResults([]);
          setFindRoot("");
          setTimeout(() => document.getElementById("zolaid-find-input")?.focus?.(), 0);
          return;
        }
        // Ctrl/Cmd+Shift+S â†’ Save Asâ€¦
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && String(e.key || "").toLowerCase() === "s") {
          e.preventDefault();
          setSaveAsOpen(true);
          return;
        }
        // Ctrl/Cmd+S â†’ Save
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && String(e.key || "").toLowerCase() === "s") {
          e.preventDefault();
          save().catch(() => {});
          return;
        }

        // Ctrl/Cmd+` â†’ Toggle terminal (VS Code-like)
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && String(e.key || "") === "`") {
          e.preventDefault();
          toggleBottom("terminal");
          return;
        }

        // Ctrl/Cmd+Shift+P â†’ Command Palette
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && String(e.key || "").toLowerCase() === "p") {
          e.preventDefault();
          setPaletteOpen(true);
          setPaletteQuery("");
          setPaletteIndex(0);
          setTimeout(() => paletteRef.current?.focus?.(), 0);
          return;
        }

        // Ctrl/Cmd+Shift+O â†’ Go to Symbol (in file)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && String(e.key || "").toLowerCase() === "o") {
          e.preventDefault();
          openSymbolPicker().catch(() => {});
          return;
        }
      } catch (_) {}
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [paletteOpen, ctxMenu, quickFixOpen, saveAsOpen, findOpen, gotoOpen, symbolOpen, hoverOpen]);


  const gotoItems = useMemo(() => {
    const q = String(gotoQuery || "").trim().toLowerCase();
    const all = Array.isArray(fileIndex) ? fileIndex : [];
    if (!q) return all.slice(0, 60);
    const scored = [];
    for (const p of all) {
      const s = String(p || "");
      const low = s.toLowerCase();
      const idx = low.indexOf(q);
      if (idx === -1) continue;
      const base = low.split("/").pop() || low;
      const ends = base === q || base.endsWith(q);
      const score = (ends ? 0 : 1000) + idx + low.length / 1000;
      scored.push({ p: s, score });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 60).map((x) => x.p);
  }, [gotoQuery, fileIndex]);

  useEffect(() => {
    if (!gotoOpen) return;
    setGotoIndex(0);
  }, [gotoOpen, gotoQuery]);

  const runFindInFiles = async () => {
    const q = String(findQuery || "").trim();
    if (!q) return;
    try {
      setFindBusy(true);
      const root = normalizeFolder(findRoot);
      const data = await apiPost("/devtools/grep", { query: q, path: root || "", maxResults: Number(findMax || 80) });
      const results = Array.isArray(data?.results) ? data.results : [];
      setFindResults(results);
    } catch (e) {
      setFindResults([]);
      setStatus(e?.message || "Find failed");
      setTimeout(() => setStatus(""), 1200);
    } finally {
      setFindBusy(false);
    }
  };

  const getCursorExcerpt = (text, line, radius = 30, maxChars = 6000) => {
    try {
      const src = String(text || "");
      if (!src) return "";
      const lines = src.split(/\r?\n/);
      const ln = Math.max(1, Number(line || 1));
      const start = Math.max(1, ln - radius);
      const end = Math.min(lines.length, ln + radius);
      const snippet = lines.slice(start - 1, end).join("\n");
      const clipped = snippet.length > maxChars ? snippet.slice(0, maxChars) + "\n\n// ... truncated ..." : snippet;
      return [`Excerpt (L${start}-L${end}, cursor at L${ln}):`, "```", clipped, "```"].join("\n");
    } catch (_) {
      return "";
    }
  };

  const openSymbolPicker = async () => {
    try {
      if (!selectedPath || !isTsLike) return;
      setSymbolOpen(true);
      setHoverOpen(false);
      setSymbolQuery("");
      setSymbolIndex(0);
      setSymbolBusy(true);
      const tsPath = selectedSource === "generated" ? `.zolaid_generated/${normalizePath(selectedPath)}` : selectedPath;
      const data = await apiPost("/devtools/ts/symbols", { path: tsPath, content: String(content || "") });
      const syms = Array.isArray(data?.symbols) ? data.symbols : [];
      setSymbolItems(syms);
      setTimeout(() => symbolRef.current?.focus?.(), 0);
    } catch (e) {
      setSymbolItems([]);
      setStatus(e?.message || "Symbols failed");
      setTimeout(() => setStatus(""), 1200);
    } finally {
      setSymbolBusy(false);
    }
  };

  const filteredSymbols = useMemo(() => {
    const q = String(symbolQuery || "").trim().toLowerCase();
    const all = Array.isArray(symbolItems) ? symbolItems : [];
    if (!q) return all.slice(0, 200);
    const hits = [];
    for (const s of all) {
      const name = String(s?.name || "");
      const container = String(s?.container || "");
      const key = `${container} ${name}`.toLowerCase();
      if (!key.includes(q)) continue;
      hits.push(s);
      if (hits.length >= 200) break;
    }
    return hits;
  }, [symbolItems, symbolQuery]);

  useEffect(() => {
    if (!symbolOpen) return;
    setSymbolIndex(0);
  }, [symbolOpen, symbolQuery]);

  const jumpToSymbol = async (s) => {
    const line = Number(s?.line || 1);
    const col = Number(s?.character || 1);
    setSymbolOpen(false);
    setRevealRequest({ line, column: col, nonce: Date.now() });
  };

  const formatDocument = async () => {
    try {
      if (!selectedPath || !isTsLike) return;
      setStatus("Formattingâ€¦");
      const data = await apiPost("/devtools/ts/format", { path: selectedPath, content: String(content || "") });
      const ch = Array.isArray(data?.changes) ? data.changes[0] : null;
      const tcs = Array.isArray(ch?.textChanges) ? ch.textChanges : [];
      if (!tcs.length) {
        setStatus("Already formatted");
        setTimeout(() => setStatus(""), 900);
        return;
      }
      const next = applyTextEdits(String(content || ""), tcs);
      setContent(next);
      setDirty(true);
      setStatus("Formatted");
      setTimeout(() => setStatus(""), 900);
    } catch (e) {
      setStatus(e?.message || "Format failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const showHover = async () => {
    try {
      if (!selectedPath || !isTsLike) return;
      setHoverOpen(true);
      setSymbolOpen(false);
      setHoverText("Loadingâ€¦");
      const data = await apiPost("/devtools/ts/hover", {
        path: selectedPath,
        line: Number(cursorPos?.line || 1),
        character: Number(cursorPos?.column || 1),
        content: String(content || ""),
      });
      if (!data?.found) {
        setHoverText("No info here.");
        return;
      }
      setHoverText(String(data?.text || "").trim() || "No info here.");
    } catch (e) {
      setHoverText(e?.message || "Hover failed");
    }
  };

  const paletteItems = useMemo(() => {
    const items = [
      { id: "refresh", label: "Refresh files", run: () => refresh("") },
      { id: "build", label: "Run build", run: () => runBuild() },
      { id: "organize_imports", label: "Organize imports", run: () => organizeImports() },
      { id: "format_doc", label: "Format document", run: () => formatDocument() },
      { id: "symbols", label: "Go to symbolâ€¦", run: () => openSymbolPicker() },
      { id: "hover", label: "Show hover info", run: () => showHover() },
      {
        id: "go_to_file",
        label: "Go to fileâ€¦",
        run: async () => {
          setGotoOpen(true);
          setGotoQuery("");
          setGotoIndex(0);
          setTimeout(() => gotoRef.current?.focus?.(), 0);
          try {
            if (!Array.isArray(fileIndex) || !fileIndex.length) {
              setGotoIndexing(true);
              await buildFileIndex();
            }
          } catch (_) {
          } finally {
            setGotoIndexing(false);
          }
        },
      },
      {
        id: "find_in_files",
        label: "Find in filesâ€¦",
        run: () => {
          setFindOpen(true);
          setFindQuery("");
          setFindResults([]);
          setFindRoot("");
          setTimeout(() => document.getElementById("zolaid-find-input")?.focus?.(), 0);
        },
      },
      { id: "focus_ai", label: "Focus AI prompt", run: () => aiPromptRef.current?.focus?.() },
      { id: "toggle_terminal", label: terminalOpen ? "Panel: Collapse" : "Panel: Expand", run: () => toggleBottom("terminal") },
      { id: "term_start", label: "Terminal: Start", run: () => startShell() },
      { id: "term_stop", label: "Terminal: Stop", run: () => stopShell() },
      { id: "save", label: "Save file", run: () => save() },
      { id: "saveas", label: "Save Asâ€¦", run: () => openSaveAs() },
      { id: "patch_apply_now", label: "Patch: Apply now (to disk)", run: () => applyPatchNow({ confirm: true }) },
      { id: "patch_rollback", label: "Patch: Rollback last apply", run: () => rollbackLastPatch() },
      { id: "plan_apply_all", label: "Plan: Apply all steps", run: () => applyAllPlanSteps() },
      { id: "plan_rollback_all", label: "Plan: Rollback all steps", run: () => rollbackAllPlanSteps() },
      { id: "patch_queue", label: "Patch: Queue changes (approval flow)", run: () => queuePatch() },
      { id: "patch_clear", label: "Patch: Clear", run: () => { setPatchFiles(null); setPatchSummary(""); setPatchBaseFiles(null); setPatchExpanded(new Set()); setPatchSelected(new Set()); setLastAiContractMode(null); } },
      { id: "problems_clear", label: "Problems: Clear", run: () => { setTsDiagnostics([]); setTsRefs([]); setMonacoMarkers([]); } },
    ];
    const q = String(paletteQuery || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteQuery, terminalOpen, shellId, dirty, selectedPath, selectedSource]);

  useEffect(() => {
    if (!paletteOpen) return;
    setPaletteIndex(0);
  }, [paletteOpen, paletteQuery]);

  const openFile = async (path) => {
    try {
      await maybeAutoSaveCurrent();
      stashCurrentBuffer();
      setStatus("Openingâ€¦");
      const k = tabKey("repo", path);
      const cached = openBuffersRef.current.get(k);
      const data = cached ? { path, content: cached.content } : await apiGet("/devtools/read", { path });
      const outPath = String(data.path || path);
      setSelectedPath(outPath);
      setSelectedSource("repo");
      const nextText = String(data.content || "");
      suppressMonacoChangeRef.current = { expected: nextText, until: Date.now() + 800 };
      setContent(nextText);
      setDirty(Boolean(cached?.dirty || false));
      ensureTabOpen("repo", outPath);
      try {
        const k2 = tabKey("repo", outPath);
        openBuffersRef.current.set(k2, { content: nextText, dirty: Boolean(cached?.dirty || false) });
      } catch (_) {}
      pushTimeline({ kind: "open", text: `Open repo: ${outPath}`, source: "repo", path: outPath });
      setActiveFolder(folderOfPath(outPath));
      // seed Save As defaults
      try {
        const p = String(outPath).replace(/\\/g, "/");
        const base = p.split("/").pop() || "";
        const dot = base.lastIndexOf(".");
        const nameOnly = dot > 0 ? base.slice(0, dot) : base;
        const ext = dot > 0 ? base.slice(dot + 1) : "";
        setSaveAsName(nameOnly || "file");
        setSaveAsExt(ext || "js");
        setSaveAsFolder(folderOfPath(p));
      } catch (_) {}
      setStatus("");
    } catch (e) {
      setStatus(e?.message || "Failed to open file");
    }
  };

  const openPathFromConversation = async (path) => {
    const p = String(path || "").replace(/\\/g, "/").replace(/^\/+/, "").trim();
    if (!p) return;
    const gen = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
    if (Object.prototype.hasOwnProperty.call(gen, p)) {
      await openGeneratedFile(p);
    } else {
      await openFile(p);
    }
  };

  const openGeneratedFile = async (path) => {
    try {
      const p = String(path || "")
        .replace(/\\/g, "/")
        .replace(/^\/+/, "");
      if (!p) return;
      await maybeAutoSaveCurrent();
      stashCurrentBuffer();
      setStatus("Openingâ€¦");
      const k = tabKey("generated", p);
      const cached = openBuffersRef.current.get(k);
      const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
      setSelectedPath(p);
      setSelectedSource("generated");
      const nextText = String(cached ? cached.content : (src[p] ?? ""));
      suppressMonacoChangeRef.current = { expected: nextText, until: Date.now() + 800 };
      setContent(nextText);
      setDirty(Boolean(cached?.dirty || false));
      ensureTabOpen("generated", p);
      try {
        const k2 = tabKey("generated", p);
        openBuffersRef.current.set(k2, { content: nextText, dirty: Boolean(cached?.dirty || false) });
      } catch (_) {}
      pushTimeline({ kind: "open", text: `Open generated: ${p}`, source: "generated", path: p });
      setActiveFolder(folderOfPath(p));
      // seed Save As defaults from generated path
      try {
        const base = p.split("/").pop() || "";
        const dot = base.lastIndexOf(".");
        const nameOnly = dot > 0 ? base.slice(0, dot) : base;
        const ext = dot > 0 ? base.slice(dot + 1) : "";
        setSaveAsName(nameOnly || "file");
        setSaveAsExt(ext || "js");
        setSaveAsFolder(folderOfPath(p));
      } catch (_) {}
      setStatus("");
    } catch (e) {
      setStatus(e?.message || "Failed to open generated file");
    }
  };

  const expandGeneratedTo = (filePath) => {
    try {
      const p = normalizePath(filePath);
      if (!p) return;
      const parts = p.split("/").filter(Boolean);
      if (parts.length <= 1) return;
      // add each folder segment path: a, a/b, a/b/c ...
      let cur = "";
      const toAdd = [];
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur ? `${cur}/${parts[i]}` : parts[i];
        toAdd.push(cur);
      }
      setGeneratedExpanded((prev) => {
        const next = new Set(prev);
        toAdd.forEach((x) => next.add(x));
        return next;
      });
    } catch (_) {}
  };

  const openSaveAs = () => {
    try {
      // default target: if editing generated, keep generated; else default to generated (scratch)
      setSaveAsTarget(selectedSource === "generated" ? "generated" : "generated");
      // folder hint: current active folder
      const hintFolder = normalizeFolder(activeFolder);
      if (hintFolder) setSaveAsFolder(hintFolder);
      setSaveAsOpen(true);
    } catch (_) {
      setSaveAsOpen(true);
    }
  };

  const doSaveAs = async () => {
    try {
      const target = String(saveAsTarget || "generated");
      const folder = normalizeFolder(saveAsFolder);
      const name = String(saveAsName || "").trim().replace(/[<>:"|?*\x00-\x1F]/g, "");
      const ext = String(saveAsExt || "").trim().replace(/^\./, "").replace(/[^a-z0-9]+/gi, "");
      if (!name) throw new Error("Choose a file name.");
      if (!ext) throw new Error("Choose a file type (extension).");
      const outPath = normalizePath(folder ? `${folder}/${name}.${ext}` : `${name}.${ext}`);

      setStatus("Savingâ€¦");
      if (target === "repo") {
        await apiPost("/devtools/write", { path: outPath, content });
        setSelectedPath(outPath);
        setSelectedSource("repo");
        setActiveFolder(folderOfPath(outPath));
        setStatus(`Saved: ${outPath}`);
        ensureTabOpen("repo", outPath);
        pushTimeline({ kind: "save", text: `Save As repo: ${outPath}`, source: "repo", path: outPath });
        if (typeof onSyncWebPreview === "function" && webPreviewSlotForPath(outPath)) {
          onSyncWebPreview(outPath, String(content || ""));
        }
      } else {
        if (typeof onUpdateGeneratedFile !== "function") throw new Error("Generated files store is not available.");
        onUpdateGeneratedFile(outPath, content);
        expandGeneratedTo(outPath);
        setSelectedPath(outPath);
        setSelectedSource("generated");
        setActiveFolder(folderOfPath(outPath));
        setStatus(`Saved (generated): ${outPath}`);
        ensureTabOpen("generated", outPath);
        pushTimeline({ kind: "save", text: `Save As generated: ${outPath}`, source: "generated", path: outPath });
      }

      setDirty(false);
      try {
        const k = tabKey(target === "repo" ? "repo" : "generated", outPath);
        openBuffersRef.current.set(k, { content: String(content || ""), dirty: false });
      } catch (_) {}
      setSaveAsOpen(false);
      setTimeout(() => setStatus(""), 800);
    } catch (e) {
      setStatus(e?.message || "Save failed");
    }
  };

  const save = async () => {
    try {
      if (!selectedPath) {
        openSaveAs();
        return;
      }
      setStatus("Savingâ€¦");
      if (selectedSource === "generated") {
        if (typeof onUpdateGeneratedFile !== "function") throw new Error("Generated files store is not available.");
        onUpdateGeneratedFile(selectedPath, content);
        expandGeneratedTo(selectedPath);
        setStatus("Saved");
        pushTimeline({ kind: "save", text: `Save generated: ${selectedPath}`, source: "generated", path: selectedPath });
      } else {
        await apiPost("/devtools/write", { path: selectedPath, content });
        setStatus("Saved");
        pushTimeline({ kind: "save", text: `Save repo: ${selectedPath}`, source: "repo", path: selectedPath });
        if (typeof onSyncWebPreview === "function" && webPreviewSlotForPath(selectedPath)) {
          onSyncWebPreview(selectedPath, String(content || ""));
        }
      }
      setDirty(false);
      try {
        const k = tabKey(selectedSource, selectedPath);
        openBuffersRef.current.set(k, { content: String(content || ""), dirty: false });
        ensureTabOpen(selectedSource, selectedPath);
      } catch (_) {}
      setTimeout(() => setStatus(""), 800);
    } catch (e) {
      setStatus(e?.message || "Save failed");
    }
  };

  const runBuild = async () => {
    try {
      setStatus("Running buildâ€¦");
      setBuildOut("");
      setBuildErr("");
      const data = await apiPost("/devtools/run", { task: "frontend_build" });
      setBuildOut(String(data.stdout || ""));
      setBuildErr(String(data.stderr || ""));
      setStatus(data.ok ? "Build OK" : `Build failed (exit ${data.exitCode})`);
    } catch (e) {
      setStatus(e?.message || "Build failed");
    }
  };

  const stopShell = async () => {
    try {
      if (shellEsRef.current) {
        shellEsRef.current.close();
        shellEsRef.current = null;
      }
      if (shellId) {
        // Best-effort: try both same-origin and local backend.
        try {
          await apiPost("/devtools/term/stop", { id: shellId });
        } catch (_) {
          await fetch(`${devtoolsApiRoot()}/devtools/term/stop`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ id: shellId }),
          }).catch(() => {});
        }
      }
    } catch (_) {}
    setShellId("");
    setShellLog("");
    pushTimeline({ kind: "terminal", text: "Terminal stopped" });
  };

  const startShell = async () => {
    try {
      await stopShell();
      setShellLog("");
      setStatus("Starting terminalâ€¦");
      let started = null;
      let baseUsed = apiBase(); // may be "" (relative) or absolute
      try {
        started = await apiPost("/devtools/term/start", { cwd: String(shellCwd || "") });
      } catch (e) {
        // Common: CRA dev server responds with "Cannot POST /devtools/..." HTML (404).
        // Always retry against local backend.
        const resp = await fetch(`${devtoolsApiRoot()}/devtools/term/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ cwd: String(shellCwd || "") }),
        });
        const text = await resp.text().catch(() => "");
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (_) {
          data = {};
        }
        if (!resp.ok) {
          const m = data?.error || (text ? String(text).slice(0, 240) : "") || `Request failed (${resp.status})`;
          throw new Error(`${m} (HTTP ${resp.status})`);
        }
        baseUsed = devtoolsApiRoot();
        started = data;
      }
      const id = String(started?.id || "");
      if (!id) throw new Error("No terminal id");
      setShellId(id);
      setStatus("Terminal running");
      pushTimeline({ kind: "terminal", text: `Terminal started (cwd: ${String(shellCwd || "repo root")})` });
      setTimeout(() => setStatus(""), 800);

      // Ensure the stream connects to the same base we used to start the terminal.
      const streamUrl = (() => {
        const base = baseUsed || apiBase();
        const u = new URL(base ? `${base}/devtools/term/stream` : "/devtools/term/stream", window.location.origin);
        u.searchParams.set("id", String(id));
        return u.toString();
      })();
      // Probe first so we can show a real status code if it fails (EventSource hides it).
      try {
        const probe = await probeSse(streamUrl, { timeoutMs: 1400 });
        if (!probe.ok && probe.status) {
          setStatus(`Terminal stream failed (HTTP ${probe.status}). Check backend logs / CORS.`);
          setTimeout(() => setStatus(""), 1600);
        }
      } catch (_) {}

      const es = new EventSource(streamUrl, { withCredentials: true });
      shellEsRef.current = es;

      const append = (s) => setShellLog((prev) => (prev + String(s || "")).slice(-300_000));
      es.addEventListener("start", () => {
        // Keep terminal id in the UI header, not inside the terminal output (more like a real terminal).
        // No banner line here so the prompt starts at the left edge.
      });
      es.addEventListener("stdout", (e) => { 
        try { 
          append(String(JSON.parse(e.data || "{}")?.chunk || ""));
          // Auto-scroll terminal output to bottom
          setTimeout(() => {
            if (shellLogRef.current) {
              shellLogRef.current.scrollTop = shellLogRef.current.scrollHeight;
            }
          }, 10);
        } catch (_) {}
      });
      es.addEventListener("stderr", (e) => { 
        try { 
          append(String(JSON.parse(e.data || "{}")?.chunk || ""));
          setTimeout(() => {
            if (shellLogRef.current) {
              shellLogRef.current.scrollTop = shellLogRef.current.scrollHeight;
            }
          }, 10);
        } catch (_) {}
      });
      es.addEventListener("exit", (e) => {
        try {
          const d = JSON.parse(e.data || "{}");
          append(`\n# exitCode=${d.exitCode} signal=${d.signal || ""}\n`);
        } catch (_) {
          append(`\n# exited\n`);
        }
        try { es.close(); } catch (_) {}
        shellEsRef.current = null;
        setShellId("");
      });
      let retried = false;
      es.onerror = async () => {
        // EventSource doesn't tell us HTTP status; probe to surface it.
        try {
          const p = await probeSse(streamUrl, { timeoutMs: 1200 });
          const suffix = p.status ? `HTTP ${p.status}` : (p.error ? p.error : "unknown");
          append(`\n# stream error (${suffix})\n`);
        } catch (_) {
          append("\n# stream error\n");
        }
        // One automatic retry (common when backend restarts)
        if (!retried && shellId) {
          retried = true;
          append("# retrying streamâ€¦\n");
          try { es.close(); } catch (_) {}
          try {
            const es2 = new EventSource(streamUrl, { withCredentials: true });
            shellEsRef.current = es2;
            es2.addEventListener("stdout", (e) => {
              try { append(String(JSON.parse(e.data || "{}")?.chunk || "")); } catch (_) {}
            });
            es2.addEventListener("stderr", (e) => {
              try { append(String(JSON.parse(e.data || "{}")?.chunk || "")); } catch (_) {}
            });
            es2.addEventListener("exit", (e) => {
              try {
                const d = JSON.parse(e.data || "{}");
                append(`\n# exitCode=${d.exitCode} signal=${d.signal || ""}\n`);
              } catch (_) {
                append(`\n# exited\n`);
              }
              try { es2.close(); } catch (_) {}
              shellEsRef.current = null;
              setShellId("");
            });
            es2.onerror = () => {
              append("\n# stream still failing â€” click Stop then Start\n");
              try { es2.close(); } catch (_) {}
              shellEsRef.current = null;
            };
          } catch (_) {
            append("# retry failed â€” click Stop then Start\n");
          }
        } else {
          append("# click Stop then Start\n");
        }
      };
      // focus input for typing
      setTimeout(() => shellInputRef.current?.focus?.(), 50);
    } catch (e) {
      const hint =
        String(e?.message || "").toLowerCase().includes("devtools disabled")
          ? " (Enable backend env DEVTOOLS_ENABLED=true and restart backend)"
          : "";
      setStatus((e?.message || "Failed to start terminal") + hint);
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const sendShell = async (data) => {
    try {
      if (!shellId) return;
      try {
        await apiPost("/devtools/term/write", { id: shellId, data: String(data || "") });
      } catch (_) {
        await fetch(`${devtoolsApiRoot()}/devtools/term/write`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id: shellId, data: String(data || "") }),
        }).catch(() => {});
      }
    } catch (_) {}
  };

  const sendShellLine = async (line) => {
    const s = String(line || "");
    if (!s.trim()) return;
    pushTimeline({ kind: "terminal", text: `> ${s.trim()}` });
    await sendShell(s + "\n");
  };

  const organizeImports = async () => {
    try {
      if (!selectedPath || !isTsLike) return;
      setStatus("Organizing importsâ€¦");
      const data = await apiPost("/devtools/ts/organizeImports", { path: selectedPath, content: String(content || "") });
      const changes = Array.isArray(data?.changes) ? data.changes : [];
      if (!changes.length) {
        setStatus("No import changes");
        setTimeout(() => setStatus(""), 900);
        return;
      }
      const byFile = new Map();
      for (const ch of changes) {
        const p = normalizePath(ch?.path || "");
        const tcs = Array.isArray(ch?.textChanges) ? ch.textChanges : [];
        if (!p || !tcs.length) continue;
        byFile.set(
          p,
          tcs.map((tc) => ({ start: Number(tc?.start || 0), length: Number(tc?.length || 0), newText: String(tc?.newText ?? "") }))
        );
      }
      const files = {};
      for (const [p, edits] of byFile.entries()) {
        let base = "";
        try {
          if (p === selectedPath) base = String(content || "");
          else base = String((await apiGet("/devtools/read", { path: p }))?.content ?? "");
        } catch (_) {
          base = "";
        }
        files[p] = applyTextEdits(base, edits);
      }
      await preparePatchFromFiles(files, "TS: Organize imports");
      setStatus("Organize imports patch ready");
      setTimeout(() => setStatus(""), 900);
    } catch (e) {
      setStatus(e?.message || "Organize imports failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const scanProject = async () => {
    try {
      if (scanBusy) return;
      setScanBusy(true);
      setScanSummary("");
      setStatus("Scanningâ€¦");

      // Ensure we have a file index (repo)
      if (!fileIndex.length) await buildFileIndex();

      const paths = Array.isArray(fileIndex) ? fileIndex : [];
      const byExt = new Map();
      const byTop = new Map();
      for (const raw of paths) {
        const p = String(raw || "").replace(/\\/g, "/");
        const top = p.split("/")[0] || "(root)";
        byTop.set(top, (byTop.get(top) || 0) + 1);
        const m = p.toLowerCase().match(/\.([a-z0-9]+)$/);
        const ext = m?.[1] ? `.${m[1]}` : "(noext)";
        byExt.set(ext, (byExt.get(ext) || 0) + 1);
      }

      const topDirs = Array.from(byTop.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12);
      const topExt = Array.from(byExt.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12);

      const genKeys =
        generatedFiles && typeof generatedFiles === "object"
          ? Object.keys(generatedFiles)
              .map((p) => String(p || "").replace(/\\/g, "/"))
              .filter(Boolean)
          : [];
      const gen = genKeys.length;
      const genFrontend = genKeys.filter((p) => p.startsWith("frontend/")).length;
      const genBackend = genKeys.filter((p) => p.startsWith("backend/")).length;
      const genStarterNote = isIdeStarterSiteKit(generatedFiles)
        ? [
            "",
            "Generated workspace (Zolaid built-in starter â€” not on devtools disk index):",
            `- Files: ${gen} (frontend/: ${genFrontend}, backend/: ${genBackend})`,
            "- Entry: frontend/index.html, frontend/styles.css, frontend/app.js; API: backend/server.js + backend/app.js",
            "- IDE AI receives a digest + selected file bodies in each request; repo search alone does not see these paths.",
          ].join("\n")
        : gen
          ? ["", `Generated files in memory (${gen}): not indexed as repo paths â€” use Generated tree + AI context block.`].join("\n")
          : "";

      const summary = [
        `Repo files indexed: ${paths.length}`,
        gen ? `Generated files in memory: ${gen}` : "Generated files in memory: 0",
        genStarterNote,
        "",
        "Top folders:",
        ...topDirs.map(([k, v]) => `- ${k}: ${v}`),
        "",
        "Top extensions:",
        ...topExt.map(([k, v]) => `- ${k}: ${v}`),
      ].join("\n");

      setScanSummary(summary);
      setStatus("");
    } catch (e) {
      setStatus(e?.message || "Scan failed");
    } finally {
      setScanBusy(false);
    }
  };

  // Auto-scan (replaces manual Scan button). Runs once when IDE mounts.
  useEffect(() => {
    if (autoScanOnceRef.current) return;
    autoScanOnceRef.current = true;
    // Fire-and-forget (don't block UI)
    scanProject().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Zolaid Computer loads the built-in starter into `generatedFiles`, open it in the editor (Generated source).
  useEffect(() => {
    if (starterExplorerSeededRef.current) return;
    const gen = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : null;
    if (!gen || !isIdeStarterSiteKit(gen)) return;
    starterExplorerSeededRef.current = true;
    try {
      setLeftView("explorer");
      expandGeneratedTo("frontend/index.html");
      setGeneratedRootExpanded(true);
    } catch (_) {}
    void openGeneratedFile("frontend/index.html");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot when starter kit is present
  }, [generatedFiles]);

  const extractKeywords = (text) => {
    const s = String(text || "").toLowerCase();
    const words = s.split(/[^a-z0-9_./-]+/g).filter(Boolean);
    const stop = new Set(["the", "and", "for", "with", "from", "this", "that", "what", "when", "then", "they", "them", "your", "you"]);
    const uniq = [];
    for (const w of words) {
      if (w.length < 3) continue;
      if (stop.has(w)) continue;
      if (uniq.includes(w)) continue;
      uniq.push(w);
      if (uniq.length >= 12) break;
    }
    return uniq;
  };

  /** Injects digest + real Generated file bodies so AI can refurbish the built-in site (devtools search skips Generated). */
  const buildIdeStarterGeneratedContextBlock = (prompt, genMap) => {
    if (!isIdeStarterSiteKit(genMap)) return "";
    const gen = genMap && typeof genMap === "object" ? genMap : {};
    const keys = Object.keys(gen)
      .map((p) => String(p || "").replace(/\\/g, "/"))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    if (!keys.length) return "";

    const digest = getIdeStarterAiContextDigest();
    const q = String(prompt || "");
    const ql = q.toLowerCase();
    const keywords = extractKeywords(q);
    const norm = (p) => String(p).replace(/\\/g, "/").replace(/^\/+/, "");

    const mentionPaths = [];
    const re = /@([^\s@]+)/g;
    let mm;
    while ((mm = re.exec(q))) {
      const t = norm(mm[1]);
      const hit = keys.find((k) => k === t || k.endsWith("/" + t));
      if (hit) mentionPaths.push(hit);
    }
    const mentions = [...new Set(mentionPaths)];

    const boostSiteWide =
      /\b(refurbish|redesign|rebrand|overhaul|whole\s+site|entire\s+site|marketing\s+site|landing\s*page|polish|refresh|revamp|make\s+it\s+look|visual\s+identity|already\s+built|built-?in\s+site)\b/i.test(
        ql
      );

    const scored = new Map();
    const addScore = (path, delta) => {
      if (!keys.includes(path)) return;
      scored.set(path, (scored.get(path) || 0) + delta);
    };

    for (const p of mentions) addScore(p, 100);
    for (const k of keys) {
      const base = (k.split("/").pop() || k).toLowerCase();
      const kl = k.toLowerCase();
      for (const kw of keywords) {
        if (kw.length < 3) continue;
        if (kl.includes(kw)) addScore(k, 2);
        if (base.includes(kw)) addScore(k, 3);
      }
    }

    const core = ["frontend/index.html", "frontend/styles.css", "frontend/app.js"];
    for (const c of core) addScore(c, boostSiteWide ? 80 : 28);

    if (/\b(pric|stripe|checkout|plan|tier)\b/i.test(ql)) {
      addScore("frontend/pricing.js", 40);
      addScore("frontend/pricing.css", 40);
      addScore("frontend/stripe-integration.js", 38);
      addScore("frontend/stripe-integration.css", 30);
    }
    if (/\b(auth|login|sign\s*in|oauth|facebook|google|microsoft|apple)\b/i.test(ql)) {
      addScore("frontend/auth-integration.js", 40);
      addScore("frontend/auth-integration.css", 35);
    }
    if (/\b(contact|form|message|email)\b/i.test(ql)) {
      addScore("frontend/contact.js", 40);
      addScore("frontend/contact.css", 35);
    }
    if (/\b(api|backend|express|webhook|endpoint)\b/i.test(ql)) {
      addScore("backend/app.js", 45);
      addScore("backend/server.js", 40);
    }

    const ranked = keys.map((k) => [k, scored.get(k) || 0]).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

    const maxFiles = boostSiteWide ? 16 : 11;
    const picked = new Set();
    for (const p of mentions) picked.add(p);
    for (const c of core) if (keys.includes(c)) picked.add(c);
    for (const [k, s] of ranked) {
      if (picked.size >= maxFiles) break;
      if (s >= 4 || boostSiteWide) picked.add(k);
    }
    if (picked.size < 6) {
      for (const [k] of ranked) {
        picked.add(k);
        if (picked.size >= (boostSiteWide ? 12 : 8)) break;
      }
    }

    const ordered = [...picked].sort((a, b) => a.localeCompare(b));
    const MAX_TOTAL = 48_000;
    const MAX_FILE = 12_000;
    let total = digest.length + 400;
    const parts = [digest];
    for (const path of ordered) {
      const raw = String(gen[path] ?? "");
      let body = raw;
      if (body.length > MAX_FILE) body = body.slice(0, MAX_FILE) + "\n\n// ... truncated (file continues) ...\n";
      const chunk = `\n\n--- generated:${path} ---\n\`\`\`\n${body}\n\`\`\``;
      if (total + chunk.length > MAX_TOTAL) break;
      parts.push(chunk);
      total += chunk.length;
    }
    return `\n\nBuilt-in Generated workspace (structure + file contents for this request):\n${parts.join("")}\n`;
  };

  const normalizePath = (p) => String(p || "").replace(/\\/g, "/").replace(/^\/+/, "");

  const onIdeActiveGeneratedPathChangeRef = useRef(onIdeActiveGeneratedPathChange);
  onIdeActiveGeneratedPathChangeRef.current = onIdeActiveGeneratedPathChange;
  useEffect(() => {
    if (selectedSource !== "generated") return;
    const path = normalizePath(selectedPath);
    if (!path) return;
    try {
      onIdeActiveGeneratedPathChangeRef.current?.(path);
    } catch (_) {}
  }, [selectedSource, selectedPath]);

  const renameRepoPath = async (from, to) => {
    const a = normalizePath(from);
    const b = normalizePath(to);
    if (!a || !b) return;
    await apiPost("/devtools/rename", { from: a, to: b });
  };

  const deleteRepoPath = async (p) => {
    const a = normalizePath(p);
    if (!a) return;
    await apiPost("/devtools/delete", { path: a });
  };

  const promptRename = async ({ source, path, isDir }) => {
    const p = normalizePath(path);
    if (!p) return;
    const base = p.split("/").pop() || p;
    const nextBase = window.prompt(`Rename to (${isDir ? "folder" : "filename"} only):`, base);
    if (!nextBase) return;
    const clean = String(nextBase).replace(/[<>:"|?*\x00-\x1F]/g, "").trim();
    if (!clean) return;
    const dir = p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "";
    const nextPath = normalizePath(dir ? `${dir}/${clean}` : clean);
    if (!nextPath || nextPath === p) return;

    try {
      setStatus("Renamingâ€¦");
      if (source === "generated") {
        // Generated folders are virtual; rename must move all nested files.
        const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
        const all = Object.keys(src)
          .map((x) => normalizePath(x))
          .filter(Boolean);
        const prefix = p.endsWith("/") ? p : `${p}/`;
        const nextPrefix = nextPath.endsWith("/") ? nextPath : `${nextPath}/`;
        const targets = isDir ? all.filter((x) => x === p || x.startsWith(prefix)) : [p];

        // update editor + tab state for current file (if needed) during renames
        const updates = [];
        for (const from of targets) {
          const to = isDir
            ? (from === p ? nextPath : normalizePath(nextPrefix + from.slice(prefix.length)))
            : nextPath;
          if (!to || to === from) continue;
          updates.push([from, to]);
        }
        // deterministic order: deeper paths first avoids collisions in some FSes
        updates.sort((a, b) => b[0].localeCompare(a[0]));

        for (const [from, to] of updates) {
          if (typeof onRenameGeneratedFile === "function") onRenameGeneratedFile(from, to);
          // keep open buffers/tabs consistent
          try {
            const kFrom = tabKey("generated", from);
            const kTo = tabKey("generated", to);
            const buf = openBuffersRef.current.get(kFrom);
            if (buf) {
              openBuffersRef.current.delete(kFrom);
              openBuffersRef.current.set(kTo, buf);
            }
            setOpenTabs((prev) => {
              const arr = Array.isArray(prev) ? prev : [];
              return arr.map((t) => (t.key === kFrom ? { ...t, key: kTo, path: to, source: "generated" } : t));
            });
          } catch (_) {}
          if (selectedSource === "generated" && selectedPath === from) setSelectedPath(to);
        }
        pushTimeline({ kind: "rename", text: `Rename generated: ${p} â†’ ${nextPath}`, source: "generated", path: nextPath });
      } else {
        await renameRepoPath(p, nextPath);
        // refresh tree so it shows new name
        refresh("").catch(() => {});
        pushTimeline({ kind: "rename", text: `Rename repo: ${p} â†’ ${nextPath}`, source: "repo", path: nextPath });
      }
      // keep editor pointed at renamed file if it was open
      if (selectedSource === source && selectedPath === p) {
        setSelectedPath(nextPath);
      }
      setStatus("Renamed");
      setTimeout(() => setStatus(""), 800);
    } catch (e) {
      setStatus(e?.message || "Rename failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const confirmDelete = async ({ source, path, isDir }) => {
    const p = normalizePath(path);
    if (!p) return;
    const ok = window.confirm(`Delete ${p}${isDir ? " (folder)" : ""}?`);
    if (!ok) return;
    try {
      setStatus("Deletingâ€¦");
      if (source === "generated") {
        const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
        const all = Object.keys(src)
          .map((x) => normalizePath(x))
          .filter(Boolean);
        const prefix = p.endsWith("/") ? p : `${p}/`;
        const targets = isDir ? all.filter((x) => x === p || x.startsWith(prefix)) : [p];

        // close buffers/tabs under this path
        try {
          const kPrefix = tabKey("generated", prefix);
          const kExact = tabKey("generated", p);
          setOpenTabs((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            return arr.filter((t) => {
              if (t.source !== "generated") return true;
              if (t.key === kExact) return false;
              if (isDir && String(t.key || "").startsWith(kPrefix)) return false;
              return true;
            });
          });
          for (const k of Array.from(openBuffersRef.current.keys())) {
            if (k === kExact) openBuffersRef.current.delete(k);
            else if (isDir && String(k).startsWith(kPrefix)) openBuffersRef.current.delete(k);
          }
        } catch (_) {}

        for (const t of targets) {
          if (typeof onDeleteGeneratedFile === "function") onDeleteGeneratedFile(t);
        }
        pushTimeline({ kind: "delete", text: `Delete generated${isDir ? " folder" : ""}: ${p}`, source: "generated", path: p });
      } else {
        await deleteRepoPath(p);
        refresh("").catch(() => {});
        pushTimeline({ kind: "delete", text: `Delete repo${isDir ? " folder" : ""}: ${p}`, source: "repo", path: p });
      }
      if (selectedSource === source && (selectedPath === p || (isDir && selectedPath.startsWith(p.endsWith("/") ? p : `${p}/`)))) {
        setSelectedPath("");
        setContent("");
        setDirty(false);
      }
      setStatus("Deleted");
      setTimeout(() => setStatus(""), 800);
    } catch (e) {
      setStatus(e?.message || "Delete failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const openContextMenu = (e, { source, path, isDir }) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      const p = normalizePath(path);
      if (!p) return;
      setNavSelection({ source, path: p });
      setCtxMenu({ source, path: p, x: e.clientX, y: e.clientY, isDir: Boolean(isDir) });
    } catch (_) {}
  };

  useEffect(() => {
    if (!ctxMenu) return;
    const onDown = () => setCtxMenu(null);
    const onKey = (ev) => {
      if (ev.key === "Escape") setCtxMenu(null);
    };
    // Use bubble phase so clicks inside the menu can stopPropagation and won't auto-close
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  const readFileAsText = (file, maxChars) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsText(file);
    }).then((text) => {
      const s = String(text || "");
      if (s.length > maxChars) return { text: s.slice(0, maxChars), truncated: true };
      return { text: s, truncated: false };
    });
  };

  const addAttachments = async (fileList) => {
    try {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      setAiAttachBusy(true);
      // caps to keep prompts safe
      const MAX_FILES = 5;
      const MAX_CHARS_PER_FILE = 25_000;
      const MAX_TOTAL_CHARS = 80_000;

      const next = [];
      let total = 0;
      for (const f of files.slice(0, MAX_FILES)) {
        const name = String(f.name || "file");
        const type = String(f.type || "");
        const size = Number(f.size || 0);

        const isTextLike =
          type.startsWith("text/") ||
          type === "application/json" ||
          type === "application/xml" ||
          type === "image/svg+xml" ||
          // treat common â€œtextâ€ extensions as readable (chatbot-like)
          /\.(js|jsx|ts|tsx|json|css|html|md|txt|yml|yaml|csv|log|env|toml|ini|xml|svg|sql|sh|ps1|py|java|go|rs|c|cpp|h|hpp|php|rb)$/i.test(
            name
          );

        if (!isTextLike) {
          next.push({ name, type, size, note: "Binary/non-text file (content not inlined)" });
          continue;
        }

        const remaining = Math.max(0, MAX_TOTAL_CHARS - total);
        if (remaining <= 0) {
          next.push({ name, type, size, note: "Skipped (attachment limit reached)" });
          continue;
        }
        const cap = Math.min(MAX_CHARS_PER_FILE, remaining);
        const r = await readFileAsText(f, cap);
        total += r.text.length;
        next.push({ name, type, size, text: r.text, truncated: r.truncated });
      }

      setAiAttachments((prev) => {
        const merged = [...(Array.isArray(prev) ? prev : []), ...next];
        return merged.slice(-MAX_FILES);
      });

      // Show attachments directly in the prompt (like chatbot UX)
      try {
        const INSERT_MAX_PER_FILE = 5000;
        const blockParts = ["", "Attached (uploaded) files:"];
        for (const a of next) {
          if (!a) continue;
          const name = String(a.name || "file");
          if (a.note && !a.text) {
            blockParts.push(`\n[${name}] (${a.note})\n`);
            continue;
          }
          const body = String(a.text || "");
          const clipped = body.length > INSERT_MAX_PER_FILE ? body.slice(0, INSERT_MAX_PER_FILE) + "\n\n// ... truncated ..." : body;
          blockParts.push(`\n[${name}]\n\`\`\`\n${clipped}\n\`\`\``);
        }
        const block = blockParts.join("\n");
        setAiPrompt((prev) => {
          const p = String(prev || "");
          // keep it from exploding
          const out = (p + block).slice(-120_000);
          return out;
        });
        // auto-grow text area after insertion
        setTimeout(() => {
          try {
            const el = aiPromptRef.current;
            if (!el) return;
            el.style.height = "auto";
            el.style.height = Math.min(320, el.scrollHeight) + "px";
          } catch (_) {}
        }, 0);
      } catch (_) {}
    } catch (e) {
      setAiErr(e?.message || "Failed to attach files");
    } finally {
      setAiAttachBusy(false);
    }
  };

  const removeAttachment = (idx) => {
    setAiAttachments((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
  };

  const clearAttachments = () => setAiAttachments([]);
  const clearPlan = () => {
    setAiPlanOut("");
    setExecutablePlanSteps([]);
    setPlanStepSnapshots([]);
    setPlanStepExpanded(new Set());
  };

  const togglePlanStepExpanded = (stepIndex) => {
    setPlanStepExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stepIndex)) next.delete(stepIndex);
      else next.add(stepIndex);
      return next;
    });
  };
  const clearTrace = () => setAiTrace([]);
  const clearConversation = () => {
    if (streamingIntervalRef.current) clearInterval(streamingIntervalRef.current);
    streamingIntervalRef.current = null;
    setStreamingMessageId(null);
    setStreamingFullText("");
    setStreamingWordIndex(0);
    setAiConversation([]);
    setAiStreamingResponse("");
    setAiOut("");
  };

  const dedupeRepeatedText = (s) => {
    const t = String(s ?? "");
    const trimmed = t.trim();
    if (!trimmed) return "";
    // Fast path: if the whole string is an exact double (with optional whitespace between), collapse it.
    // e.g. "Hello\nHello" or "Hello\n\nHello" or "Hello  \n Hello"
    const normAll = trimmed.replace(/[ \t]+\n/g, "\n").replace(/\n[ \t]+/g, "\n");
    const halves = normAll.split(/\n{1,2}/g);
    if (halves.length === 2 && halves[0].trim() === halves[1].trim()) return halves[0].trim();

    // Paragraph-aware: collapse duplicate consecutive paragraphs (even if separated by 1+ newlines).
    const paras = trimmed
      .split(/\n{1,}/g)
      .map((p) => p.trim())
      .filter(Boolean);

    // If the entire message is a repeated block (e.g. A,B,A,B), collapse it.
    if (paras.length >= 2 && paras.length % 2 === 0) {
      const half = paras.length / 2;
      const a = paras.slice(0, half).join("\n");
      const b = paras.slice(half).join("\n");
      if (a === b) return paras.slice(0, half).join("\n\n");
    }

    const out = [];
    let prev = "";
    for (const p of paras) {
      if (!p) continue;
      if (p === prev) continue;
      out.push(p);
      prev = p;
    }
    // Keep formatting readable
    return out.join("\n\n");
  };

  const outputLogText = useMemo(() => {
    // Show conversation-style assistant output, not internal status logs.
    if (aiStreamingResponse) return String(aiStreamingResponse || "").trim();
    if (aiOut) return String(aiOut || "").trim();
    return "";
  }, [aiStreamingResponse, aiOut]);

  // Auto-scroll Output: stick to bottom unless user scrolls up.
  useEffect(() => {
    try {
      const el = aiOutputRef.current;
      if (!el) return;
      if (!aiOutputStickBottom) return;
      el.scrollTop = el.scrollHeight;
    } catch (_) {}
  }, [outputLogText, aiOutputStickBottom]);

  // Scroll conversation panel to bottom when new messages or streaming
  useEffect(() => {
    try {
      const el = conversationScrollRef.current;
      if (!el || !aiOutputStickBottom) return;
      el.scrollTop = el.scrollHeight;
    } catch (_) {}
  }, [aiConversation, aiStreamingResponse, aiBusy, aiOutputStickBottom, streamingWordIndex]);

  // Word-by-word typewriter for new assistant messages
  useEffect(() => {
    if (!streamingMessageId || !streamingFullText) return;
    const words = streamingFullText.split(/\s+/).filter(Boolean);
    const msgId = streamingMessageId;
    if (words.length === 0) {
      setAiConversation((prev) => prev.map((m) => (m.id === msgId ? { ...m, content: streamingFullText } : m)));
      setStreamingMessageId(null);
      setStreamingFullText("");
      setStreamingWordIndex(0);
      return;
    }
    if (streamingIntervalRef.current) clearInterval(streamingIntervalRef.current);
    setAiConversation((prev) => prev.map((m) => (m.id === msgId ? { ...m, content: words[0] } : m)));
    let wordIdx = 1;
    streamingIntervalRef.current = setInterval(() => {
      if (wordIdx >= words.length) {
        if (streamingIntervalRef.current) clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
        setStreamingMessageId(null);
        setStreamingFullText("");
        setStreamingWordIndex(0);
        return;
      }
      const revealed = words.slice(0, wordIdx + 1).join(" ");
      setAiConversation((prev) => prev.map((m) => (m.id === msgId ? { ...m, content: revealed } : m)));
      wordIdx += 1;
    }, 45);
    return () => {
      if (streamingIntervalRef.current) clearInterval(streamingIntervalRef.current);
    };
  }, [streamingMessageId, streamingFullText]);

  const openAiToolsMenu = () => {
    try {
      const el = aiToolsBtnRef.current;
      if (!el) {
        setAiToolsPos({ x: 18, y: 18 });
        setAiToolsOpen(true);
        return;
      }
      const r = el.getBoundingClientRect();
      // Prefer opening UPWARDS so the menu isn't cut off near the bottom of the sidebar.
      const menuW = 250;
      const menuH = 360; // best-effort estimate; actual menu is scrollable
      const x = Math.max(8, Math.min(r.left, (window.innerWidth || 1200) - (menuW + 8)));
      const yUp = r.top - menuH - 12;
      const y = Math.max(8, Math.min(yUp, (window.innerHeight || 800) - (menuH + 8)));
      setAiToolsPos({ x, y });
      setAiToolsOpen(true);
    } catch (_) {
      setAiToolsOpen(true);
    }
  };

  const openAgentMenu = ({ preferSideBySideWithTools = false } = {}) => {
    try {
      const menuW = 270;
      const menuH = 360;
      let x = 18;
      let y = 18;
      if (preferSideBySideWithTools && aiToolsOpen) {
        x = aiToolsPos.x + 250 + 10;
        y = aiToolsPos.y;
      } else {
        const el = agentMenuBtnRef.current || aiToolsBtnRef.current;
        if (el) {
          const r = el.getBoundingClientRect();
          // open upwards
          const x0 = r.left + r.width + 10;
          const y0 = r.top - menuH - 12;
          x = x0;
          y = y0;
        }
      }
      // Clamp inside viewport, and prefer to show to the RIGHT; if no room, flip left.
      const vw = (typeof window !== "undefined" ? window.innerWidth : 1024) || 1024;
      const vh = (typeof window !== "undefined" ? window.innerHeight : 768) || 768;
      if (x + menuW + 8 > vw) x = Math.max(8, x - (menuW + 260)); // flip left-ish
      x = Math.max(8, Math.min(x, vw - (menuW + 8)));
      y = Math.max(8, Math.min(y, vh - (menuH + 8)));
      setAgentMenuPos({ x, y });
      setAgentMenuOpen(true);
    } catch (_) {
      setAgentMenuOpen(true);
    }
  };

  const closeAllMenus = () => {
    setAiToolsOpen(false);
    setAgentMenuOpen(false);
    setLeftMenuOpen(null);
  };

  const openLeftMenu = (name, ev) => {
    try {
      const r = ev?.currentTarget?.getBoundingClientRect?.();
      const vw = (typeof window !== "undefined" ? window.innerWidth : 1024) || 1024;
      const vh = (typeof window !== "undefined" ? window.innerHeight : 768) || 768;
      const menuW = 220;
      const menuH = 320;
      const x = Math.max(8, Math.min(Number(r?.left || 8), vw - (menuW + 8)));
      const y = Math.max(8, Math.min(Number(r?.bottom || 8) + 6, vh - (menuH + 8)));
      setLeftMenuPos({ x, y });
      setLeftMenuOpen(String(name || ""));
    } catch (_) {
      setLeftMenuPos({ x: 8, y: 8 });
      setLeftMenuOpen(String(name || ""));
    }
  };

  const openNewFile = () => {
    setNewItemKind("file");
    setNewItemFolder("");
    setNewItemName("new-file");
    setNewItemExt("js");
    setNewItemOpen(true);
    setLeftMenuOpen(null);
  };

  const openNewFolder = () => {
    setNewItemKind("folder");
    setNewItemFolder("");
    setNewItemName("new-folder");
    setNewItemExt("js");
    setNewItemOpen(true);
    setLeftMenuOpen(null);
  };

  const createNewItem = async () => {
    try {
      if (typeof onUpdateGeneratedFile !== "function") throw new Error("Generated files store is not available.");
      const folder = normalizeFolder(newItemFolder);
      const base = String(newItemName || "").trim();
      if (!base) throw new Error("Name required");
      if (newItemKind === "folder") {
        const p = folder ? `${folder}/${base}` : base;
        const marker = `${normalizePath(p)}/.keep`;
        onUpdateGeneratedFile(marker, "");
        // Expand the created folder in tree (best-effort)
        setGeneratedRootExpanded(true);
        setGeneratedExpanded((prev) => {
          const next = new Set(prev);
          next.add(normalizePath(p));
          return next;
        });
        setStatus("Folder created");
        setTimeout(() => setStatus(""), 800);
        setNewItemOpen(false);
        return;
      }
      const ext = String(newItemExt || "js").trim().replace(/^\./, "").replace(/[^a-z0-9]+/gi, "") || "js";
      const filePath = normalizePath(folder ? `${folder}/${base}.${ext}` : `${base}.${ext}`);
      onUpdateGeneratedFile(filePath, "");
      try {
        await openGeneratedFile(filePath);
      } catch (_) {}
      setStatus("File created");
      setTimeout(() => setStatus(""), 800);
      setNewItemOpen(false);
    } catch (e) {
      setStatus(e?.message || "Create failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const activityLabel = useMemo(() => {
    if (agentRunning || agentProcId) return "Running";
    if (patchBusy) return "Generating";
    if (aiAttachBusy) return "Thinking";
    if (aiBusy) return aiLastIntent === "plan" ? "Thinking" : "Generating";
    return "";
  }, [agentRunning, agentProcId, patchBusy, aiAttachBusy, aiBusy, aiLastIntent]);

  const activityActive = Boolean(activityLabel);
  useEffect(() => {
    if (!activityActive) {
      setActivityDots(1);
      return;
    }
    setActivityDots(1);
    const id = setInterval(() => {
      setActivityDots((d) => (d >= 3 ? 1 : d + 1));
    }, 420);
    return () => clearInterval(id);
  }, [activityActive]);

  const setActivity = (label, detail = "") => {
    // User-facing progress only (no hidden chain-of-thought).
    if (typeof label === "string" && label) setAiLastIntent(label);
    const d = String(detail || "").slice(0, 180);
    setActivityDetail(d);
    // Mirror into Output panel as a stable activity log (avoid spam duplicates).
    if (d && aiTraceLastRef.current !== d) {
      aiTraceLastRef.current = d;
      setAiTrace((prev) => {
        const next = [...(Array.isArray(prev) ? prev : []), d];
        return next.slice(-120);
      });
    }
  };

  const buildDiagnosticsContext = () => {
    try {
      if (!selectedPath || !isTsLike) return "";
      const diags = Array.isArray(tsDiagnostics) ? tsDiagnostics : [];
      if (!diags.length) return "";
      const top = diags.slice(0, 20);
      const lines = top.map((d) => {
        const cat = String(d?.category || "Error");
        const code = Number(d?.code || 0);
        const ln = Number(d?.start?.line || 1);
        const col = Number(d?.start?.character || 1);
        const msg = String(d?.message || "").slice(0, 240);
        return `- ${cat} TS${code} @ ${ln}:${col} â€” ${msg}`;
      });
      return ["", `TypeScript diagnostics for ${selectedPath}:`, ...lines].join("\n");
    } catch (_) {
      return "";
    }
  };

  const isTsPath = (p) => /\.(ts|tsx|js|jsx)$/i.test(String(p || ""));
  const isTsServiceEligiblePath = (p) => {
    // Backend TS language service intentionally ignores dot-folders (e.g. .zolaid_generated).
    const rel = String(p || "");
    if (!rel) return false;
    if (rel.startsWith(".") || rel.includes("/.")) return false;
    return isTsPath(rel);
  };

  const applyTsTextChangesToContent = (base, changes) => {
    const tcs = Array.isArray(changes?.textChanges) ? changes.textChanges : [];
    if (!tcs.length) return String(base ?? "");
    const edits = tcs
      .map((tc) => ({
        start: Number(tc?.start || 0),
        length: Number(tc?.length || 0),
        newText: String(tc?.newText ?? ""),
      }))
      .filter((e) => Number.isFinite(e.start) && Number.isFinite(e.length));
    if (!edits.length) return String(base ?? "");
    return applyTextEdits(String(base ?? ""), edits);
  };

  const autoOrderCodeIfPossible = async (p, baseContent) => {
    // Keep imports + formatting in a consistent order after edits.
    const rel = normalizePath(p);
    if (!isTsServiceEligiblePath(rel)) return String(baseContent ?? "");
    let next = String(baseContent ?? "");
    try {
      const org = await apiPost("/devtools/ts/organizeImports", { path: rel, content: next });
      const ch = Array.isArray(org?.changes) ? org.changes[0] : null;
      if (ch && normalizePath(ch?.path) === rel) next = applyTsTextChangesToContent(next, ch);
    } catch (_) {}
    try {
      const fmt = await apiPost("/devtools/ts/format", { path: rel, content: next });
      const ch = Array.isArray(fmt?.changes) ? fmt.changes[0] : null;
      if (ch && normalizePath(ch?.path) === rel) next = applyTsTextChangesToContent(next, ch);
    } catch (_) {}
    return next;
  };

  const shouldAutoGenerateWhenNoFile = (prompt) => {
    const s = String(prompt || "").trim().toLowerCase();
    if (!s) return false;
    // Heuristic: if user is asking to create/edit code or a website/page, auto-generate files.
    if (/(create|build|make|generate|write|implement|add|fix|refactor|update)\b/.test(s)) return true;
    if (/(improve|refurbish|polish|enhance|finish|complete|finalize|iterate|tweak|revamp|upgrade|modernize)\b/.test(s))
      return true;
    if (/(website|landing page|homepage|portfolio|app|component|page|html|css|javascript|react|vue|alpine)\b/.test(s)) return true;
    if (/\.(html|css|js|jsx|ts|tsx|json|md)\b/.test(s)) return true;
    if (/(about us|contact us|privacy policy|terms of service|cookie policy|refund policy)\b/.test(s)) return true;
    return false;
  };

  // @-mentions: parse "@path" or "@path/to/file" from prompt and resolve against file index
  const parseMentionPaths = (text) => {
    const tokens = [];
    const re = /@([^\s@]+)/g;
    let m;
    while ((m = re.exec(text))) tokens.push(m[1].trim());
    const uniq = [...new Set(tokens)];
    const index = Array.isArray(fileIndex) ? fileIndex : [];
    const normalized = (p) => String(p).replace(/\\/g, "/").replace(/^\/+/, "");
    const resolved = [];
    for (const token of uniq) {
      const t = normalized(token);
      const exact = index.find((p) => normalized(p) === t || normalized(p).endsWith("/" + t));
      if (exact) {
        resolved.push(normalized(exact));
        continue;
      }
      const endsWith = index.filter((p) => normalized(p).endsWith(t) || normalized(p).endsWith("/" + t));
      if (endsWith.length > 0) resolved.push(normalized(endsWith[0]));
      else if (t.includes("/") || /\.(js|jsx|ts|tsx|css|html|json|md)$/i.test(t)) {
        const partial = index.filter((p) => normalized(p) === t || normalized(p).includes(t));
        if (partial.length > 0) resolved.push(normalized(partial[0]));
      }
    }
    return [...new Set(resolved)];
  };

  const buildRelevantContext = async (prompt, opts = {}) => {
    // Backend uses full project index + symbol/import relationships + @-mentions.
    const q = String(prompt || "").trim();
    if (!q) return "";
    const includePaths = Array.isArray(opts.includePaths) ? opts.includePaths : parseMentionPaths(q);
    try {
      const currentRel = selectedPath ? String(selectedPath).replace(/\\/g, "/").replace(/^\/+/, "") : "";
      const cacheKey = `ctx:${currentRel}:${includePaths.sort().join("|")}:${q.slice(0, 80).toLowerCase()}`;
      const cached = retrievalCacheRef.current.get(cacheKey);
      if (cached && Date.now() - Number(cached.at || 0) < 45_000) return String(cached.value || "");

      try {
        retrievalAbortRef.current.ctx?.abort?.();
      } catch (_) {}
      const ctrl = new AbortController();
      retrievalAbortRef.current.ctx = ctrl;

      const data = await apiPost(
        "/devtools/ai/context",
        {
          prompt: q,
          currentFilePath: currentRel || undefined,
          includePaths: includePaths.length ? includePaths : undefined,
          maxFiles: 8,
          maxCharsPerFile: 10_000,
          maxTotalChars: 45_000,
        },
        { signal: ctrl.signal }
      );
      const files = Array.isArray(data?.files) ? data.files : [];
      if (!files.length) return "";
      const blocks = [];
      for (const f of files) {
        const p = String(f.path || "");
        const reason = f.reason ? ` [${f.reason}]` : "";
        const preview = Array.isArray(f.preview) ? f.preview : [];
        const hitsBlock = preview.length
          ? `\nHits:\n${preview.map((h) => `- L${h.line}: ${String(h.text || "").slice(0, 220)}`).join("\n")}\n`
          : "";
        blocks.push(`File: ${p}${reason}${hitsBlock}\n\`\`\`\n${String(f.content || "")}\n\`\`\``);
      }
      const summary = data?.summary ? `\n${String(data.summary)}\n` : "";
      const symbolOutline = data?.symbolOutline ? `\nSymbol/import relationships (use for cross-file context):\n${String(data.symbolOutline)}\n` : "";
      const out = [
        "",
        "Relevant files (current + imports + referrers + @mentions + query matches):",
        summary.trim() ? summary.trim() : "",
        symbolOutline.trim() || "",
        ...blocks,
      ].filter(Boolean).join("\n");
      retrievalCacheRef.current.set(cacheKey, { at: Date.now(), value: out });
      return out;
    } catch (_) {
      return "";
    }
  };

  // Advanced AI Analysis Functions
  const analyzeCodeWithAI = async () => {
    if (!selectedPath || !content) return;
    
    try {
      setActivity("", "Running advanced code analysis...");
      
      const aiEngine = aiEngineRef.current;
      const analysis = await aiEngine.scanCodeStructure(content, selectedPath);
      const bugs = await aiEngine.analyzeForBugs(content);
      const refactorings = aiEngine.getRefactoringMethods(content);
      const performance = aiEngine.analyzePerformance(content);
      
      setAiCodeAnalysis(analysis);
      setAiBugs(bugs);
      setAiRefactorings(refactorings);
      setAiPerformance(performance);
      setAiShowAdvanced(true);
      
      // Generate detailed analysis message
      const summary = `
ðŸ“Š CODE ANALYSIS for ${selectedPath}
â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”â”

ðŸ“ˆ Metrics:
  â€¢ Lines: ${analysis.complexity.lines}
  â€¢ Functions: ${analysis.functions.length}
  â€¢ Classes: ${analysis.classes.length}
  â€¢ Cyclomatic Complexity: ${analysis.complexity.cyclomatic} (${analysis.complexity.level})
  â€¢ Dependencies: ${analysis.dependencies.length}
  â€¢ Test Coverage: ${analysis.testCoverage}

ðŸ› Issues Found: ${bugs.length}
${bugs.slice(0, 5).map(b => `  â€¢ [${b.severity.toUpperCase()}] ${b.message}`).join('\n')}

ðŸ’¡ Refactoring Suggestions: ${refactorings.length}
${refactorings.slice(0, 3).map(r => `  â€¢ ${r.title} - ${r.description}`).join('\n')}

âš¡ Performance Issues: ${performance.length}
${performance.slice(0, 3).map(p => `  â€¢ ${p.message}`).join('\n')}

ðŸ”’ Security Issues: ${analysis.securityIssues.length}
${analysis.securityIssues.slice(0, 3).map(s => `  â€¢ ${s}`).join('\n')}
`.trim();

      setAiOut(summary);
      setAiPrompt("");
    } catch (e) {
      setAiErr(`Analysis failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setActivityDetail("");
    }
  };

  const generateTestsWithAI = async () => {
    if (!selectedPath || !content) return;
    
    try {
      setActivity("", "Generating tests...");
      
      // Find the first function or class
      const funcMatch = content.match(/(?:function|const\s+\w+\s*=\s*)\w+/);
      if (!funcMatch) {
        setAiErr("No function found to generate tests for");
        return;
      }
      
      const language = selectedPath.endsWith('.ts') ? 'typescript' : 'javascript';
      const aiEngine = aiEngineRef.current;
      const testCode = await aiEngine.generateTests(content, language);
      
      const testFileName = selectedPath.replace(/\.[^.]+$/, '.test.js');
      setAiOut(`Generated test file: ${testFileName}\n\n${testCode}`);
      
      // Optionally save to generated files
      if (typeof onUpdateGeneratedFile === 'function') {
        onUpdateGeneratedFile(testFileName, testCode);
      }
    } catch (e) {
      setAiErr(`Test generation failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setActivityDetail("");
    }
  };

  const generateSmartSuggestions = async () => {
    if (!selectedPath || !content) return;
    
    try {
      setActivity("", "Generating suggestions...");
      
      const aiEngine = aiEngineRef.current;
      const suggestions = aiEngine.generateSuggestions(
        { content, selectedPath },
        cursorPos || { line: 1, column: 1 },
        content
      );
      
      setAiSuggestions(suggestions);
      
      const suggestionsText = suggestions
        .map((s, i) => `${i + 1}. ${s.label} (${s.type})`)
        .join('\n');
      
      setAiOut(`ðŸ’¡ ${suggestions.length} Smart Suggestions:\n\n${suggestionsText}`);
    } catch (e) {
      setAiErr(`Suggestions failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setActivityDetail("");
    }
  };

  const sendAi = async () => {
    try {
      const prompt = String(aiPrompt || "").trim();
      const hasAttachments = Array.isArray(aiAttachments) && aiAttachments.length > 0;
      if (!prompt && !hasAttachments) return;
      if (aiAttachBusy) {
        setAiErr("Still reading attachmentsâ€¦");
        return;
      }

      // Add user message to conversation, then clear input
      setAiConversation((prev) => [...prev, { id: `u_${Date.now()}`, role: "user", content: prompt || "(with attachments)" }]);
      setAiPrompt("");
      clearAttachments();
      
      // Reset reasoning display
      setAiReasoningStages([]);
      setAiCurrentStage(null);
      setAiStreamingResponse("");
      
      // Stage 1: Scanning
      setAiCurrentStage({ stage: "scanning", icon: "ðŸ”", text: "Scanning code structure..." });
      
      setAiBusy(true);
      setAiErr("");
      setAiOut("");

      // If no file is selected and the user is asking for code/website generation,
      // use the strict patch contract so the result always produces real files.
      const noFileSelected = !selectedPath;
      if (noFileSelected && shouldAutoGenerateWhenNoFile(prompt)) {
        const starterKit = isIdeStarterSiteKit(generatedFiles);
        const patch = await requestPatchFromAi(
          starterKit
            ? [
                IDE_STARTER_SITE_AI_GUIDANCE,
                "",
                prompt,
                "",
                "No editor tab is open. The project already contains the paths aboveâ€”return a patch that updates those files (use paths like frontend/index.html, frontend/pricing.css, frontend/auth-integration.js). Put every changed file in artifacts.files with full content. Do not replace the whole product with a minimal 3-file site unless the user explicitly asked to discard the starter.",
              ].join("\n")
            : [
                prompt,
                "",
                "No file is currently open. Create whatever files are needed to satisfy the request.",
                "Prefer producing a runnable website as:",
                "- index.html",
                "- styles.css",
                "- app.js",
              ].join("\n"),
          { userId: "developeride_autogen" }
        );
        const files = patch?.files && typeof patch.files === "object" ? patch.files : null;
        const patchSummaryText = String(patch?.summary || "").trim();
        if (files && typeof onUpdateGeneratedFile === "function") {
          const entries = Object.entries(files)
            .map(([p, c]) => [normalizePath(p), String(c ?? "")])
            .filter(([p]) => !!p);
          if (!entries.length) {
            const errMsg = patchSummaryText || "AI returned no safe files to apply (starter index may be protected).";
            setAiErr(errMsg);
            setAiConversation((prev) => [
              ...prev,
              { id: `a_${Date.now()}`, role: "assistant", content: errMsg, contract: null },
            ]);
            return;
          }
          entries.slice(0, 200).forEach(([p, c]) => {
            try {
              onUpdateGeneratedFile(p, c);
            } catch (_) {}
          });
          const outText = [
            patchSummaryText || "Generated files created.",
            "",
            `Saved ${Math.min(entries.length, 200)} file(s) to Generated files.`,
          ]
            .filter(Boolean)
            .join("\n");
          setAiOut(outText);
          const outId = `a_${Date.now()}`;
          setAiConversation((prev) => [...prev, { id: outId, role: "assistant", content: "", contract: null }]);
          setStreamingWordIndex(0);
          setStreamingMessageId(outId);
          setStreamingFullText(outText);
          return;
        }
        // Fallback: show patch in UI
        if (files) {
          await preparePatchFromFiles(files, String(patch?.summary || "Generated files (patch)"));
          const fallbackMsg = "Generated a patch. Apply it from the Patch panel.";
          setAiOut(fallbackMsg);
          const fallbackId = `a_${Date.now()}`;
          setAiConversation((prev) => [...prev, { id: fallbackId, role: "assistant", content: "", contract: null }]);
          setStreamingWordIndex(0);
          setStreamingMessageId(fallbackId);
          setStreamingFullText(fallbackMsg);
          return;
        }
      }

      const ctxPath = selectedPath ? `${selectedSource}:${selectedPath}` : "(none)";
      const ctxContent = selectedPath ? String(content || "") : "";
      const MAX = 40_000;
      const clipped = ctxContent.length > MAX ? ctxContent.slice(0, MAX) + "\n\n// ... truncated ..." : ctxContent;
      const cursorBlock = selectedPath ? getCursorExcerpt(ctxContent, cursorPos?.line || 1, 30, 6000) : "";

      const attachmentBlock = (() => {
        const arr = Array.isArray(aiAttachments) ? aiAttachments : [];
        if (!arr.length) return "";
        const parts = ["", "User-attached files:"];
        arr.forEach((a) => {
          const header = `- ${a.name} (${a.type || "unknown"}, ${a.size || 0} bytes)`;
          if (a.note && !a.text) {
            parts.push(`${header}\n  Note: ${a.note}`);
            return;
          }
          const body = String(a.text || "");
          const suffix = a.truncated ? "\n\n// ... truncated ..." : "";
          parts.push(`${header}\n\`\`\`\n${body}${suffix}\n\`\`\``);
        });
        return parts.join("\n");
      })();

      const maybeScan = aiUseScan && scanSummary ? `\n\nProject scan summary:\n${scanSummary}\n` : "";
      const diagBlock = buildDiagnosticsContext();
      setActivity("", "Searching repo + picking relevant filesâ€¦");
      const buildProjectGraphBlock = async () => {
        const graph = await getProjectGraph();
        const lines = Object.entries(graph)
          .filter(([, imports]) => Array.isArray(imports) && imports.length > 0)
          .slice(0, 60)
          .map(([file, imports]) => `${file} -> ${imports.join(", ")}`);
        return lines.length ? `\n\nProject graph (imports):\n${lines.join("\n")}\n` : "";
      };
      const [maybeRelevant, searchBlock, projectGraphBlock, semanticBlock] = await Promise.all([
        aiAttachRelevant ? buildRelevantContext(prompt) : Promise.resolve(""),
        buildAiSearchResultsBlock(prompt),
        buildProjectGraphBlock(),
        buildSemanticSearchBlock(prompt),
      ]);
      const generatedPaths = generatedFiles && typeof generatedFiles === "object" ? Object.keys(generatedFiles).map((p) => String(p || "").replace(/\\/g, "/")).sort((a, b) => a.localeCompare(b)) : [];
      const starterSite = isIdeStarterSiteKit(generatedFiles);
      const maybeGeneratedList =
        (aiIncludeGenerated || starterSite) && generatedPaths.length
          ? `\n\nGenerated files list:\n${generatedPaths.slice(0, 300).map((p) => `- ${p}`).join("\n")}\n`
          : "";
      const generatedPathsCompact =
        generatedPaths.length && !aiIncludeGenerated && !starterSite
          ? `\n\nGenerated file paths (${generatedPaths.length}): ${generatedPaths.slice(0, 80).join(", ")}${generatedPaths.length > 80 ? "â€¦" : ""}\n`
          : "";
      const maybeStarterGeneratedDeep = starterSite ? buildIdeStarterGeneratedContextBlock(prompt, generatedFiles) : "";

      const message = [
        "You have full IDE assistant capabilities. The project is fully indexed: you get context-aware suggestions across files and understand relationships between classes, functions, and imports.",
        "When the user works in the Zolaid built-in Generated starter, disk repo search may be empty or unrelated â€” rely on the Generated files list and the Built-in Generated workspace block for real source.",
        "For code changes return mode=patch with artifacts.files: { \"path/to/file\": \"full content\", ... }. Every modified or new file must be in artifacts.files with complete contents. You may instead or in addition use artifacts.textEdits: [ { path, range: { startLine, startColumn, endLine, endColumn }, newText }, ... ] for small, precise edits (1-based line/column). Use the codebase context (snippets, current file, search, symbol outline) to make accurate edits.",
        "Zolaid: For complex or multi-step tasks return mode=plan with artifacts.plan, artifacts.steps, artifacts.filesToInspect, artifacts.filesToEdit, artifacts.commands, artifacts.successCriteria, artifacts.assumptions, artifacts.openQuestions. The user can run the Agent to execute the plan.",
        "If you need a file you don't have, list it in artifacts.filesToInspect. JS/TS: keep imports at top, no duplicate imports, order helpers â†’ state â†’ handlers â†’ render.",
        "",
        isIdeStarterSiteKit(generatedFiles) ? IDE_STARTER_SITE_AI_GUIDANCE : "",
        "",
        maybeScan.trim() ? maybeScan.trim() : "",
        diagBlock.trim() ? diagBlock.trim() : "",
        maybeGeneratedList.trim() ? maybeGeneratedList.trim() : "",
        generatedPathsCompact.trim() ? generatedPathsCompact.trim() : "",
        maybeStarterGeneratedDeep.trim() ? maybeStarterGeneratedDeep.trim() : "",
        projectGraphBlock?.trim() ? projectGraphBlock.trim() : "",
        semanticBlock?.trim() ? semanticBlock.trim() : "",
        searchBlock.trim() ? searchBlock.trim() : "",
        cursorBlock ? cursorBlock : "",
        `Context file: ${ctxPath}`,
        selectedPath ? "```" : "",
        selectedPath ? clipped : "",
        selectedPath ? "```" : "",
        maybeRelevant ? maybeRelevant : "",
        attachmentBlock ? attachmentBlock : "",
        "",
        "User request:",
        prompt,
      ].join("\n");

      // Stage 4: Generating
      setAiCurrentStage({ stage: "generating", icon: "âš¡", text: "Generating response with streaming..." });
      
      const data = await postChatWithFallback({
        userId: "developeride",
        message,
        quality: aiQuality,
        model: ideSelectedModel,
        source: "ide",
        isIDE: true,
      });
      
      let contract = data?.contract && typeof data.contract === "object" ? data.contract : null;
      const reply = String(data?.reply || "");
      // If backend sent contract only inside reply (e.g. stringified), parse it so we can apply patch/plan
      if (!contract && reply.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(reply);
          if (parsed && typeof parsed === "object" && (parsed.mode === "patch" || parsed.mode === "plan")) contract = parsed;
        } catch (_) {}
      }
      // When we have a patch/plan contract, show a short message instead of raw JSON in Output
      const hasPatchArtifacts = contract?.mode === "patch" && contract?.artifacts?.files && Object.keys(contract.artifacts.files).length > 0;
      const hasPlanArtifacts = contract?.mode === "plan" && (Array.isArray(contract?.artifacts?.steps) ? contract.artifacts.steps.length : 0) > 0;
      const conversationalContent = (contract?.content && String(contract.content).trim()) || "";
      const displayReply =
        hasPatchArtifacts || hasPlanArtifacts
          ? conversationalContent
            ? conversationalContent + (hasPatchArtifacts ? " See Patch panel to review and apply." : " See Plan panel.")
            : (contract?.intent || contract?.mode || "Done") + (hasPatchArtifacts ? ". See Patch panel to review and apply." : ". See Plan panel.")
          : reply;
      setAiStreamingResponse(displayReply);
      setAiReasoningStages(prev => [...prev, { stage: "generating", icon: "âš¡", text: "Response generated", duration: 0 }]);

      // Stage 5: Debugging / Verification (no artificial delay)
      if (contract && Object.keys(contract).length > 0) {
        setAiCurrentStage({ stage: "debugging", icon: "ðŸ›", text: "Verifying artifacts..." });
        setAiReasoningStages(prev => [...prev, { stage: "debugging", icon: "ðŸ›", text: "Artifacts verified", duration: 0 }]);
      }
      
      setAiCurrentStage(null);

      if (contract && typeof contract.mode === "string") setLastAiContractMode(contract.mode === "plan" || contract.mode === "patch" ? contract.mode : null);

      // If the AI returned actionable artifacts, surface them into the IDE automatically.
      // This prevents the UX where the prompt clears but "nothing happens".
      try {
        const filesFromAi = contract?.artifacts?.files && typeof contract.artifacts.files === "object" ? contract.artifacts.files : null;
        const textEditsFromAi = Array.isArray(contract?.artifacts?.textEdits) ? contract.artifacts.textEdits : [];
        const html = typeof contract?.artifacts?.html === "string" ? contract.artifacts.html : "";
        const css = typeof contract?.artifacts?.css === "string" ? contract.artifacts.css : "";
        const js = typeof contract?.artifacts?.js === "string" ? contract.artifacts.js : "";
        const planText = typeof contract?.artifacts?.plan === "string" ? String(contract.artifacts.plan || "").trim() : "";
        const stepsArr = Array.isArray(contract?.artifacts?.steps) ? contract.artifacts.steps : [];
        const assumptionsArr = Array.isArray(contract?.artifacts?.assumptions) ? contract.artifacts.assumptions : [];
        const openQsArr = Array.isArray(contract?.artifacts?.openQuestions) ? contract.artifacts.openQuestions : [];
        const structureText =
          typeof contract?.artifacts?.projectStructure === "string" ? String(contract.artifacts.projectStructure || "").trim() : "";
        const depsArr = Array.isArray(contract?.artifacts?.dependencies) ? contract.artifacts.dependencies : [];
        const docsArr = Array.isArray(contract?.artifacts?.documentation) ? contract.artifacts.documentation : [];
        const filesToEditList = Array.isArray(contract?.artifacts?.filesToEdit) ? contract.artifacts.filesToEdit : [];
        const commandsArr = Array.isArray(contract?.artifacts?.commands) ? contract.artifacts.commands : [];
        const successCriteriaArr = Array.isArray(contract?.artifacts?.successCriteria) ? contract.artifacts.successCriteria : [];
        const hasPlanArtifacts = stepsArr.length > 0 || filesToEditList.length > 0 || commandsArr.length > 0;
        const planBlock = (() => {
          const parts = [];
          if (planText) parts.push(`Plan:\n${planText}`);
          if (assumptionsArr.length) parts.push(`Assumptions:\n${assumptionsArr.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (stepsArr.length) parts.push(`Steps:\n${stepsArr.slice(0, 12).map((s, i) => `${i + 1}. ${String(s || "").trim()}`).join("\n")}`);
          if (commandsArr.length) parts.push(`Commands (run in terminal):\n${commandsArr.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (successCriteriaArr.length) parts.push(`Success criteria:\n${successCriteriaArr.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (structureText) parts.push(`Project structure:\n${structureText}`);
          if (depsArr.length) parts.push(`Dependencies:\n${depsArr.slice(0, 20).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (docsArr.length) parts.push(`Documentation:\n${docsArr.slice(0, 12).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (openQsArr.length) parts.push(`Open questions:\n${openQsArr.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (filesToEditList.length) parts.push(`Files to edit:\n${filesToEditList.slice(0, 20).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
          if (hasPlanArtifacts) parts.push("â†’ Use the Agent (ðŸ¤–) button to execute this plan step-by-step.");
          if (!parts.length) return "";
          return parts.join("\n\n").trim();
        })();
        const actions = Array.isArray(contract?.actions) ? contract.actions : [];

        // If we got plan artifacts, keep them in a dedicated Plan panel above the typing box.
        if (planBlock) setAiPlanOut(planBlock);

        // Build executable plan steps when steps have patches (autonomous execution).
        const rawSteps = Array.isArray(contract?.artifacts?.steps) ? contract.artifacts.steps : [];
        const hasStepPatches = rawSteps.some(
          (s) =>
            (typeof s === "object" && s !== null && (Object.keys(s?.files || {}).length > 0 || (Array.isArray(s?.textEdits) && s.textEdits.length > 0)))
        );
        if (hasStepPatches && rawSteps.length) {
          const steps = rawSteps.map((s, i) => {
            if (typeof s === "object" && s !== null) {
              const desc = String(s?.description ?? s?.step ?? "").trim() || `Step ${i + 1}`;
              const conf = String(s?.confidence ?? "medium").toLowerCase();
              const confidence = ["high", "medium", "low"].includes(conf) ? conf : "medium";
              const files = s?.files && typeof s.files === "object" ? s.files : null;
              const textEdits = Array.isArray(s?.textEdits) ? s.textEdits : null;
              return { id: `step_${i}`, description: desc, confidence, files, textEdits, applied: false };
            }
            return { id: `step_${i}`, description: String(s || "").trim() || `Step ${i + 1}`, confidence: "medium", files: null, textEdits: null, applied: false };
          });
          setExecutablePlanSteps(steps);
          setPlanStepSnapshots([]);
        } else if (rawSteps.length && planBlock) {
          setExecutablePlanSteps([]);
          setPlanStepSnapshots([]);
        }

        // Resolve artifacts.textEdits into full-file contents (read current, apply edits, then treat as patch).
        let filesFromTextEdits = null;
        if (textEditsFromAi.length) {
          const byPath = new Map();
          for (const te of textEditsFromAi) {
            const path = normalizePath(te?.path ?? "");
            if (!path) continue;
            const list = byPath.get(path) || [];
            list.push(te);
            byPath.set(path, list);
          }
          if (byPath.size) {
            filesFromTextEdits = {};
            for (const [p, edits] of byPath) {
              let base = "";
              try {
                if (p === selectedPath && selectedSource === "repo") base = String(content ?? "");
                else base = String((await apiGet("/devtools/read", { path: p }))?.content ?? "");
              } catch (_) {
                base = "";
              }
              filesFromTextEdits[p] = applyTextEditsByRange(base, edits);
            }
          }
        }

        let mergedFiles = remapAiOutputFilesForKit(
          { ...(filesFromAi || {}), ...(filesFromTextEdits || {}) },
          generatedFiles
        );
        let mergedIndexWarning = "";
        if (mergedFiles) {
          const gm = filterIdeStarterIndexRegression(mergedFiles, generatedFiles);
          mergedFiles = gm.files;
          if (gm.warning) mergedIndexWarning = gm.warning;
        }
        const hasFiles = mergedFiles && Object.keys(mergedFiles).length > 0;
        const starterKitActive = isIdeStarterSiteKit(generatedFiles);

        // 1) If AI provided files/artifacts, write them into Generated files (safe default).
        if (typeof onUpdateGeneratedFile === "function") {
          const writeMap = {};
          if (mergedFiles) {
            for (const [p, c] of Object.entries(mergedFiles)) {
              const path = normalizePath(p);
              if (!path) continue;
              writeMap[path] = String(c ?? "");
            }
          }
          const prevIdx = String(generatedFiles?.["frontend/index.html"] ?? "");
          if (html) {
            const h = String(html);
            if (
              starterKitActive &&
              wouldDestroyIdeStarterIndex(prevIdx, h)
            ) {
              mergedIndexWarning =
                (mergedIndexWarning ? mergedIndexWarning + "\n\n" : "") +
                "Skipped artifacts.html: it would replace the starter fragment with a full or minimal page. Edit sections inside frontend/index.html instead.";
            } else {
              writeMap[starterKitActive ? "frontend/index.html" : "index.html"] = h;
            }
          }
          if (css) writeMap[starterKitActive ? "frontend/styles.css" : "styles.css"] = css;
          if (js) writeMap[starterKitActive ? "frontend/app.js" : "app.js"] = js;

          const entries = Object.entries(writeMap);
          if (entries.length) {
            entries.slice(0, 200).forEach(([p, c]) => {
              try {
                onUpdateGeneratedFile(p, c);
              } catch (_) {}
            });
            setAiOut(
              [
                mergedIndexWarning || "",
                dedupeRepeatedText(reply || String(contract?.content || "").trim() || "(AI responded)"),
                "",
                `Saved ${Math.min(entries.length, 200)} file(s) to Generated files.`,
              ]
                .filter(Boolean)
                .join("\n")
            );
          } else {
            setAiOut(
              [
                mergedIndexWarning || "",
                dedupeRepeatedText(reply || String(contract?.content || "").trim() || "(AI responded)"),
                entries.length === 0 && mergedIndexWarning ? "\n\nNo files were applied." : "",
              ]
                .filter(Boolean)
                .join("\n")
            );
          }
        } else if (hasFiles) {
          // Fallback: show as a patch preview in the Patch panel
          await preparePatchFromFiles(mergedFiles, String(contract?.content || "AI provided files"));
          setAiOut(
            dedupeRepeatedText(reply || String(contract?.content || "").trim() || "(AI responded)") + "\n\nPatch prepared (apply from Patch panel)."
          );
        } else {
          setAiOut(dedupeRepeatedText(reply || String(contract?.content || "").trim() || "(AI responded)"));
        }

        // 2) If AI included sandbox-style actions (createFile/updatePreview), queue them.
        if (typeof onQueueActions === "function" && actions.length) {
          const safe = actions
            .map((a) => (a && typeof a === "object" ? { ...a } : null))
            .filter(Boolean)
            .filter((a) => ["createFile", "updatePreview", "moveCursor", "click", "type", "run_command"].includes(String(a.type || "")));
          if (safe.length) onQueueActions(safe);
        }
        // Append assistant message and reveal word-by-word (typewriter)
        const newId = `a_${Date.now()}`;
        setAiConversation((prev) => [...prev, { id: newId, role: "assistant", content: "", contract: contract || null }]);
        setAiStreamingResponse("");
        setStreamingWordIndex(0);
        setStreamingMessageId(newId);
        setStreamingFullText(displayReply);
      } catch (_) {
        setAiOut(dedupeRepeatedText(reply || "(AI responded)"));
        const newId = `a_${Date.now()}`;
        const fallbackText = reply || "(AI responded)";
        setAiConversation((prev) => [...prev, { id: newId, role: "assistant", content: "", contract: null }]);
        setAiStreamingResponse("");
        setStreamingWordIndex(0);
        setStreamingMessageId(newId);
        setStreamingFullText(fallbackText);
      }
    } catch (e) {
      setAiErr(e?.message || "AI request failed");
      setAiConversation((prev) => [...prev, { id: `a_${Date.now()}`, role: "assistant", content: `Error: ${e?.message || "AI request failed"}`, contract: null }]);
    } finally {
      setAiBusy(false);
      setActivityDetail("");
    }
  };

  const requestPatchFromAi = async (promptText, { userId = "developeride_patch" } = {}) => {
    const prompt = String(promptText || "").trim();
    const hasAttachments = Array.isArray(aiAttachments) && aiAttachments.length > 0;
    if (!prompt && !hasAttachments) return null;
    if (aiAttachBusy) throw new Error("Still reading attachmentsâ€¦");
    setActivity("", "Preparing patch requestâ€¦");

    // Ensure we have a scan ready if enabled
    if (aiUseScan && !scanSummary && !scanBusy) {
      scanProject().catch(() => {});
    }

    const ctxPath = selectedPath ? `${selectedSource}:${selectedPath}` : "(none)";
    const ctxContent = selectedPath ? String(content || "") : "";
    const MAX = 30_000;
    const clipped = ctxContent.length > MAX ? ctxContent.slice(0, MAX) + "\n\n// ... truncated ..." : ctxContent;
    const cursorBlock = selectedPath ? getCursorExcerpt(ctxContent, cursorPos?.line || 1, 30, 6000) : "";

    const attachmentBlock = (() => {
      const arr = Array.isArray(aiAttachments) ? aiAttachments : [];
      if (!arr.length) return "";
      const parts = ["", "User-attached files:"];
      arr.forEach((a) => {
        const header = `- ${a.name} (${a.type || "unknown"}, ${a.size || 0} bytes)`;
        if (a.note && !a.text) {
          parts.push(`${header}\n  Note: ${a.note}`);
          return;
        }
        const body = String(a.text || "");
        const suffix = a.truncated ? "\n\n// ... truncated ..." : "";
        parts.push(`${header}\n\`\`\`\n${body}${suffix}\n\`\`\``);
      });
      return parts.join("\n");
    })();

    const scanCtx = aiUseScan && scanSummary ? `\n\nProject scan summary:\n${scanSummary}\n` : "";
    const diagBlock = buildDiagnosticsContext();
    const genList =
      (aiIncludeGenerated || isIdeStarterSiteKit(generatedFiles)) && generatedFiles && typeof generatedFiles === "object"
        ? `\n\nGenerated files list:\n${Object.keys(generatedFiles)
            .map((p) => String(p || "").replace(/\\/g, "/"))
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 300)
            .map((p) => `- ${p}`)
            .join("\n")}\n`
        : "";
    const starterGenDeep = isIdeStarterSiteKit(generatedFiles) ? buildIdeStarterGeneratedContextBlock(prompt, generatedFiles) : "";
    const [maybeRelevant, searchBlock, semanticBlock] = await Promise.all([
      aiAttachRelevant ? buildRelevantContext(prompt) : Promise.resolve(""),
      buildAiSearchResultsBlock(prompt),
      buildSemanticSearchBlock(prompt),
    ]);
    setActivity("", "Generating patchâ€¦");

    // Optimize prompt: prioritize most relevant context first
    const promptParts = [
      "Return ONLY a JSON object with this shape:",
      '{ "mode":"patch", "intent":"edit_code", "content":"short summary", "artifacts": { "files": { "<path>": "<full new file content>", "...": "..." } } }',
      "",
      "CRITICAL RULES FOR CODE QUALITY:",
      "- Put ALL modified/created files in artifacts.files (full file content).",
      "- Use forward-slash paths like frontend/src/App.js",
      "- For JS/TS edits: keep imports at the top, avoid duplicate imports, and preserve file structure (helpers â†’ state â†’ handlers â†’ JSX).",
      "- Follow existing code style, naming conventions, and patterns.",
      "- Generate complete, runnable code (not snippets).",
      "- Include proper error handling and type safety where applicable.",
      "- Consider edge cases and provide robust implementations.",
      "- Do not include explanations outside the JSON.",
      "",
    ];

    if (isIdeStarterSiteKit(generatedFiles)) {
      promptParts.push(IDE_STARTER_SITE_AI_GUIDANCE, "");
    }

    // Add context in priority order (most relevant first)
    if (cursorBlock) promptParts.push("Relevant code context:", cursorBlock, "");
    if (selectedPath) {
      promptParts.push(`Current file: ${ctxPath}`, "```", clipped, "```", "");
    }
    if (diagBlock.trim()) promptParts.push("TypeScript diagnostics:", diagBlock.trim(), "");
    if (maybeRelevant) promptParts.push("Relevant files:", maybeRelevant, "");
    if (attachmentBlock) promptParts.push(attachmentBlock, "");
    if (searchBlock.trim()) promptParts.push("Search results:", searchBlock.trim(), "");
    if (semanticBlock?.trim()) promptParts.push("Semantic search:", semanticBlock.trim(), "");
    if (scanCtx.trim()) promptParts.push("Project overview:", scanCtx.trim(), "");
    if (genList.trim()) promptParts.push("Generated files:", genList.trim(), "");
    if (starterGenDeep.trim()) promptParts.push(starterGenDeep.trim(), "");

    promptParts.push("User request:", prompt);

    const message = promptParts.join("\n");

    const data = await postChatWithFallback({
      userId,
      message,
      quality: aiQuality,
      source: "ide",
      isIDE: true,
    });

    const contract = data?.contract && typeof data.contract === "object" ? data.contract : null;
    const rawFiles = contract?.artifacts?.files && typeof contract.artifacts.files === "object" ? contract.artifacts.files : null;
    if (!rawFiles || !Object.keys(rawFiles).length) throw new Error("AI returned no patch files (artifacts.files).");
    let outFiles = remapPatchFilesForIdeStarterKit({ ...rawFiles }, generatedFiles);
    const idxGuard = filterIdeStarterIndexRegression(outFiles, generatedFiles);
    outFiles = idxGuard.files;
    if (!outFiles || !Object.keys(outFiles).length) {
      throw new Error(idxGuard.warning || "After validation, no files remain to apply.");
    }
    let patchSummary = String(contract?.content || "Patch ready");
    if (idxGuard.warning) patchSummary = `${patchSummary}\n\n${idxGuard.warning}`;
    return { files: outFiles, summary: patchSummary };
  };

  const requestPlanFromAi = async (promptText, { userId = "developeride_plan" } = {}) => {
    const prompt = String(promptText || "").trim();
    const hasAttachments = Array.isArray(aiAttachments) && aiAttachments.length > 0;
    if (!prompt && !hasAttachments) return null;
    if (aiAttachBusy) throw new Error("Still reading attachmentsâ€¦");

    // Ensure we have a scan ready if enabled
    if (aiUseScan && !scanSummary && !scanBusy) {
      scanProject().catch(() => {});
    }

    const ctxPath = selectedPath ? `${selectedSource}:${selectedPath}` : "(none)";
    const ctxContent = selectedPath ? String(content || "") : "";
    const MAX = 25_000;
    const clipped = ctxContent.length > MAX ? ctxContent.slice(0, MAX) + "\n\n// ... truncated ..." : ctxContent;
    const cursorBlock = selectedPath ? getCursorExcerpt(ctxContent, cursorPos?.line || 1, 30, 4500) : "";

    const maybeScan = aiUseScan && scanSummary ? `\n\nProject scan summary:\n${scanSummary}\n` : "";
    const diagBlock = buildDiagnosticsContext();
    const [maybeRelevant, searchBlock, semanticBlock] = await Promise.all([
      aiAttachRelevant ? buildRelevantContext(prompt) : Promise.resolve(""),
      buildAiSearchResultsBlock(prompt),
      buildSemanticSearchBlock(prompt),
    ]);
    const maybeGeneratedList =
      (aiIncludeGenerated || isIdeStarterSiteKit(generatedFiles)) && generatedFiles && typeof generatedFiles === "object"
        ? `\n\nGenerated files list:\n${Object.keys(generatedFiles)
            .map((p) => String(p || "").replace(/\\/g, "/"))
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 250)
            .map((p) => `- ${p}`)
            .join("\n")}\n`
        : "";
    const planStarterDeep = isIdeStarterSiteKit(generatedFiles) ? buildIdeStarterGeneratedContextBlock(prompt, generatedFiles) : "";

    // Optimize plan prompt: prioritize context strategically
    const planParts = [
      "Return ONLY one JSON object.",
      "Mode must be plan.",
      "Goal: create a strategic multi-step plan with deep reasoning and clear task breakdown.",
      "",
      "Output requirements:",
      '- mode: "plan"',
      '- intent: "agent_plan"',
      "- content: short summary (1-3 sentences)",
      "- artifacts.plan: strategic plan paragraph (explain WHY and WHAT, not just steps)",
      "- artifacts.steps: ordered steps (5-12). Each step can be a string or object: { description, confidence?: \"high\"|\"medium\"|\"low\", files?: { \"path\": \"full content\" }, textEdits?: [{ path, range: { startLine, startColumn, endLine, endColumn }, newText }] }. Include files or textEdits when the step is a concrete edit.",
      "- artifacts.projectStructure: recommended folder/module structure (short, concrete)",
      "- artifacts.dependencies: key dependencies to add (0-20), with brief notes if needed",
      "- artifacts.documentation: docs to create/update (0-12) with filenames (e.g., docs/DEPLOY.md)",
      "- artifacts.assumptions: key assumptions that affect correctness (0-8)",
      "- artifacts.openQuestions: questions that block correctness (0-8)",
      "- artifacts.filesToInspect: file paths you want to read to understand context (0-12)",
      "- artifacts.filesToEdit: file paths you expect to modify/create (0-20)",
      "- artifacts.commands: commands to run/verify (0-10)",
      "- artifacts.successCriteria: what 'done' means (0-8) - be specific",
      "- actions: []",
      "",
      "MULTI-STEP REASONING:",
      "- Think strategically: what needs to happen first? What depends on what?",
      "- Consider complexity: break down large tasks into manageable steps",
      "- Identify risks: what could go wrong? What assumptions are you making?",
      "- Define success clearly: how will you know it's done correctly?",
      "",
    ];

    if (isIdeStarterSiteKit(generatedFiles)) {
      planParts.push(
        "EXISTING STARTER PRODUCT: The repo is already the Zolaid IDE marketing starter. Plans should refine/extend those files (frontend/*, backend/*), not prescribe a greenfield layout unless the user wants a new app from scratch.",
        "",
      );
    }

    // Add context in priority order
    if (cursorBlock) planParts.push("Code context:", cursorBlock, "");
    if (selectedPath) {
      planParts.push(`Current file: ${ctxPath}`, "```", clipped, "```", "");
    }
    if (diagBlock.trim()) planParts.push("TypeScript diagnostics:", diagBlock.trim(), "");
    if (maybeRelevant) planParts.push("Relevant files:", maybeRelevant, "");
    if (semanticBlock?.trim()) planParts.push("Semantic search:", semanticBlock.trim(), "");
    if (searchBlock.trim()) planParts.push("Search results:", searchBlock.trim(), "");
    if (maybeScan.trim()) planParts.push("Project overview:", maybeScan.trim(), "");
    if (maybeGeneratedList.trim()) planParts.push("Generated files:", maybeGeneratedList.trim(), "");
    if (planStarterDeep.trim()) planParts.push(planStarterDeep.trim(), "");
    const graph = await getProjectGraph();
    const graphEntries = Object.entries(graph)
      .filter(([, imports]) => Array.isArray(imports) && imports.length > 0)
      .slice(0, 80)
      .map(([file, imports]) => `${file} -> ${imports.join(", ")}`);
    if (graphEntries.length) planParts.push("Project graph (imports, for dependency-aware planning):", graphEntries.join("\n"), "");

    planParts.push("User request:", prompt);

    const message = planParts.join("\n");

    const data = await postChatWithFallback({
      userId,
      message,
      quality: aiQuality,
      model: ideSelectedModel,
      source: "ide",
      isIDE: true,
    });

    const contract = data?.contract && typeof data.contract === "object" ? data.contract : null;
    if (!contract) return null;
    return contract;
  };

  const planAi = async () => {
    try {
      const prompt = String(aiPrompt || "").trim();
      const hasAttachments = Array.isArray(aiAttachments) && aiAttachments.length > 0;
      if (!prompt && !hasAttachments) return;
      if (aiAttachBusy) {
        setAiErr("Still reading attachmentsâ€¦");
        return;
      }
      setAiLastIntent("plan");
      setActivity("", "Planning (structure, steps, dependencies)â€¦");
      if (aiUseScan && !scanSummary && !scanBusy) {
        scanProject().catch(() => {});
      }
      setAiBusy(true);
      setAiErr("");
      setAiOut("");
      const plan = await requestPlanFromAi(prompt, { userId: "developeride_plan_only" });
      const summary = String(plan?.content || "").trim();
      const planText = String(plan?.artifacts?.plan || "").trim();
      const steps = Array.isArray(plan?.artifacts?.steps) ? plan.artifacts.steps : [];
      const structureText = typeof plan?.artifacts?.projectStructure === "string" ? String(plan.artifacts.projectStructure || "").trim() : "";
      const deps = Array.isArray(plan?.artifacts?.dependencies) ? plan.artifacts.dependencies : [];
      const docs = Array.isArray(plan?.artifacts?.documentation) ? plan.artifacts.documentation : [];
      const assumptions = Array.isArray(plan?.artifacts?.assumptions) ? plan.artifacts.assumptions : [];
      const openQs = Array.isArray(plan?.artifacts?.openQuestions) ? plan.artifacts.openQuestions : [];
      const commands = Array.isArray(plan?.artifacts?.commands) ? plan.artifacts.commands : [];
      const success = Array.isArray(plan?.artifacts?.successCriteria) ? plan.artifacts.successCriteria : [];

      const blocks = [];
      if (summary) blocks.push(summary);
      if (planText) blocks.push(`Plan:\n${planText}`);
      if (steps.length) blocks.push(`Steps:\n${steps.slice(0, 14).map((s, i) => `${i + 1}. ${String(typeof s === "object" && s !== null ? (s?.description ?? s?.step ?? "") : s ?? "").trim() || "Step " + (i + 1)}`).join("\n")}`);
      if (structureText) blocks.push(`Project structure:\n${structureText}`);
      if (deps.length) blocks.push(`Dependencies:\n${deps.slice(0, 25).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      if (docs.length) blocks.push(`Documentation:\n${docs.slice(0, 15).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      if (assumptions.length) blocks.push(`Assumptions:\n${assumptions.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      if (commands.length) blocks.push(`Commands:\n${commands.slice(0, 12).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      if (success.length) blocks.push(`Success criteria:\n${success.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      if (openQs.length) blocks.push(`Open questions:\n${openQs.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
      setAiPlanOut(blocks.filter(Boolean).join("\n\n"));
      const hasStepPatches = steps.some(
        (s) => typeof s === "object" && s !== null && (Object.keys(s?.files || {}).length > 0 || (Array.isArray(s?.textEdits) && s.textEdits.length > 0))
      );
      if (hasStepPatches && steps.length) {
        const execSteps = steps.map((s, i) => {
          if (typeof s === "object" && s !== null) {
            const desc = String(s?.description ?? s?.step ?? "").trim() || `Step ${i + 1}`;
            const conf = String(s?.confidence ?? "medium").toLowerCase();
            const confidence = ["high", "medium", "low"].includes(conf) ? conf : "medium";
            return { id: `step_${i}`, description: desc, confidence, files: s?.files && typeof s.files === "object" ? s.files : null, textEdits: Array.isArray(s?.textEdits) ? s.textEdits : null, applied: false };
          }
          return { id: `step_${i}`, description: String(s || "").trim() || `Step ${i + 1}`, confidence: "medium", files: null, textEdits: null, applied: false };
        });
        setExecutablePlanSteps(execSteps);
        setPlanStepSnapshots([]);
      } else {
        setExecutablePlanSteps([]);
        setPlanStepSnapshots([]);
      }
      // Do NOT clear the prompt/attachments: planning is usually followed by a patch request.
    } catch (e) {
      setAiErr(e?.message || "Plan failed");
    } finally {
      setAiBusy(false);
      setActivityDetail("");
    }
  };

  const proposePatch = async () => {
    try {
      const prompt = String(aiPrompt || "").trim();
      setAiLastIntent("patch");
      setActivity("", "Generating patchâ€¦");
      setPatchBusy(true);
      setAiErr("");
      setPatchFiles(null);
      setPatchSummary("");
      setPatchBaseFiles(null);
      setPatchExpanded(new Set());

      const r = await requestPatchFromAi(prompt);
      if (!r) return;

      setPatchFiles(r.files);
      setPatchSummary(r.summary);
      setPatchSelected(new Set(Object.keys(r.files).map((p) => normalizePath(p)).filter(Boolean)));

      // Load "before" content for diff preview (best-effort)
      try {
        const base = {};
        const entries = Object.entries(r.files)
          .map(([p]) => normalizePath(p))
          .filter(Boolean)
          .slice(0, 60); // cap for safety

        for (const p of entries) {
          const gen = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : null;
          if (gen && Object.prototype.hasOwnProperty.call(gen, p)) {
            base[p] = String(gen[p] ?? "");
            continue;
          }
          try {
            const data = await apiGet("/devtools/read", { path: p });
            base[p] = String(data?.content ?? "");
          } catch (_) {
            base[p] = "";
          }
        }
        setPatchBaseFiles(base);
      } catch (_) {
        setPatchBaseFiles({});
      }
    } catch (e) {
      setAiErr(e?.message || "Patch failed");
    } finally {
      setPatchBusy(false);
      setActivityDetail("");
    }
  };

  const queuePatch = () => {
    if (!patchFiles || typeof patchFiles !== "object") return;
    const entries = Object.entries(patchFiles)
      .map(([p, c]) => [normalizePath(p), String(c ?? "")])
      .filter(([p]) => !!p && patchSelected.has(p));
    if (!entries.length) return;
    const actions = entries.map(([path, content]) => ({
      type: "createFile",
      path,
      content,
      delay: 250,
      description: `Apply patch: ${path}`,
    }));
    if (typeof onQueueActions === "function") onQueueActions(actions);
    setPatchFiles(null);
    setPatchSummary("");
    setPatchBaseFiles(null);
    setPatchExpanded(new Set());
    setPatchSelected(new Set());
    setLastAiContractMode(null);
    setAiOut("Queued patch as createFile actions. Use the approval bar above to approve/apply.");
  };

  const applyPatchNow = async ({ confirm = true, filesOverride = null, selectedOverride = null } = {}) => {
    try {
      const filesObj = filesOverride && typeof filesOverride === "object" ? filesOverride : patchFiles;
      const selectedSet = selectedOverride instanceof Set ? selectedOverride : patchSelected;
      if (!filesObj || typeof filesObj !== "object") return;
      const entries = Object.entries(filesObj)
        .map(([p, c]) => [normalizePath(p), String(c ?? "")])
        .filter(([p]) => !!p && (!selectedSet || !selectedSet.size || selectedSet.has(p)));
      if (!entries.length) return;
      entries.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
      if (confirm) {
        const ok = window.confirm(`Apply ${entries.length} file(s) to disk now?`);
        if (!ok) return;
      }
      setStatus("Applyingâ€¦");
      const beforeContents = {};
      for (const [p] of entries) {
        try {
          const data = await apiGet("/devtools/read", { path: p });
          beforeContents[p] = String(data?.content ?? "");
        } catch (_) {
          beforeContents[p] = "";
        }
      }
      setLastAppliedPatchSnapshot({ paths: entries.map(([p]) => p), before: beforeContents });
      for (const [p, c] of entries) {
        let next = c;
        try {
          next = await autoOrderCodeIfPossible(p, c);
        } catch (_) {
          next = c;
        }
        await apiPost("/devtools/write", { path: p, content: next });
        if (p === selectedPath && selectedSource === "repo") {
          setContent(next);
          setDirty(false);
        }
      }
      setStatus(`Applied ${entries.length} file(s) to disk`);
      setTimeout(() => setStatus(""), 2200);
      return { ok: true, applied: entries.map(([p]) => p) };
    } catch (e) {
      setStatus(e?.message || "Apply failed");
      setTimeout(() => setStatus(""), 1200);
      return { ok: false, error: e?.message || "Apply failed" };
    }
  };

  const rollbackLastPatch = async () => {
    const snap = lastAppliedPatchSnapshot;
    if (!snap || !snap.paths?.length || !snap.before || typeof snap.before !== "object") {
      setStatus("No patch to roll back");
      setTimeout(() => setStatus(""), 1200);
      return;
    }
    try {
      setStatus("Rolling backâ€¦");
      for (const p of snap.paths) {
        const content = snap.before[p];
        if (content === undefined) continue;
        await apiPost("/devtools/write", { path: p, content });
        if (p === selectedPath && selectedSource === "repo") {
          setContent(content);
          setDirty(false);
        }
      }
      setLastAppliedPatchSnapshot(null);
      setStatus("Rolled back");
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e?.message || "Rollback failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const getStepPaths = (step) => {
    if (step.files && typeof step.files === "object") return Object.keys(step.files).map((p) => normalizePath(p)).filter(Boolean);
    if (Array.isArray(step.textEdits)) return [...new Set(step.textEdits.map((te) => normalizePath(te?.path ?? "")).filter(Boolean))];
    return [];
  };

  const simpleLineDiff = (beforeStr, afterStr, maxLines = 200) => {
    const b = String(beforeStr ?? "").split("\n");
    const a = String(afterStr ?? "").split("\n");
    const out = [];
    const max = Math.max(b.length, a.length);
    for (let i = 0; i < Math.min(max, maxLines); i++) {
      const bl = b[i];
      const al = a[i];
      const nBefore = i + 1;
      const nAfter = i + 1;
      if (bl !== al) {
        if (bl !== undefined) out.push({ type: "-", line: bl, nBefore, nAfter: al !== undefined ? nAfter : "" });
        if (al !== undefined) out.push({ type: "+", line: al, nBefore: bl !== undefined ? nBefore : "", nAfter });
      } else if (bl !== undefined) out.push({ type: " ", line: bl, nBefore, nAfter });
    }
    if (max > maxLines) out.push({ type: " ", line: `... ${max - maxLines} more lines`, nBefore: "", nAfter: "" });
    return out;
  };

  const patchDiffByPath = useMemo(() => {
    if (!patchShowUnifiedDiff || !patchFiles) return {};
    const out = {};
    for (const [path, content] of Object.entries(patchFiles)) {
      if (!patchExpanded.has(path)) continue;
      const before = (patchBaseFiles && patchBaseFiles[path]) != null ? String(patchBaseFiles[path]) : "";
      out[path] = simpleLineDiff(before, String(content ?? ""));
    }
    return out;
  }, [patchShowUnifiedDiff, patchFiles, patchBaseFiles, patchExpanded]);

  const getStepFiles = async (step) => {
    if (step.files && typeof step.files === "object" && Object.keys(step.files).length > 0) {
      return step.files;
    }
    if (Array.isArray(step.textEdits) && step.textEdits.length > 0) {
      const byPath = new Map();
      for (const te of step.textEdits) {
        const path = normalizePath(te?.path ?? "");
        if (!path) continue;
        const list = byPath.get(path) || [];
        list.push(te);
        byPath.set(path, list);
      }
      const out = {};
      for (const [p, edits] of byPath) {
        let base = "";
        try {
          if (p === selectedPath && selectedSource === "repo") base = String(content ?? "");
          else base = String((await apiGet("/devtools/read", { path: p }))?.content ?? "");
        } catch (_) {
          base = "";
        }
        out[p] = applyTextEditsByRange(base, edits);
      }
      return out;
    }
    return null;
  };

  const applyPlanStep = async (stepIndex) => {
    const steps = executablePlanSteps;
    if (!steps?.length || stepIndex < 0 || stepIndex >= steps.length) return { ok: false };
    const step = steps[stepIndex];
    if (step.applied) return { ok: true };
    try {
      setStatus(`Applying plan step ${stepIndex + 1}â€¦`);
      const files = await getStepFiles(step);
      if (!files || !Object.keys(files).length) {
        setStatus("Step has no edits");
        setTimeout(() => setStatus(""), 1200);
        return { ok: false };
      }
      const snapshot = {};
      for (const p of Object.keys(files)) {
        try {
          const data = await apiGet("/devtools/read", { path: p });
          snapshot[p] = String(data?.content ?? "");
        } catch (_) {
          snapshot[p] = "";
        }
      }
      for (const [p, c] of Object.entries(files)) {
        await apiPost("/devtools/write", { path: p, content: c });
        if (p === selectedPath && selectedSource === "repo") {
          setContent(c);
          setDirty(false);
        }
      }
      let validationFailed = false;
      try {
        for (const [p, expected] of Object.entries(files)) {
          const data = await apiGet("/devtools/read", { path: p });
          const actual = String(data?.content ?? "");
          if (actual !== expected) {
            validationFailed = true;
            setStatus(`Validation failed: ${p} changed after write (conflict?). Plan paused.`);
            setTimeout(() => setStatus(""), 4000);
            break;
          }
        }
      } catch (_) {
        validationFailed = true;
        setStatus("Validation check failed. Plan paused.");
        setTimeout(() => setStatus(""), 4000);
      }
      if (validationFailed) return { ok: false, validationFailed: true };
      setPlanStepSnapshots((prev) => {
        const next = [...prev];
        next[stepIndex] = snapshot;
        return next;
      });
      setExecutablePlanSteps((prev) =>
        prev.map((s, i) => (i === stepIndex ? { ...s, applied: true } : s))
      );
      setStatus(`Applied plan step ${stepIndex + 1} â€” ${Object.keys(files).length} file(s)`);
      setTimeout(() => setStatus(""), 2200);
      return { ok: true };
    } catch (e) {
      setStatus(e?.message || "Step apply failed");
      setTimeout(() => setStatus(""), 1200);
      return { ok: false };
    }
  };

  const applyAllPlanSteps = async () => {
    const steps = executablePlanSteps;
    if (!steps?.length) return;
    let appliedCount = 0;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].applied) continue;
      const step = steps[i];
      if (step.confidence === "medium" || step.confidence === "low") {
        const msg = `Apply step ${i + 1}: ${step.description}?\n\nConfidence: ${step.confidence}`;
        if (!window.confirm(msg)) break;
      }
      const result = await applyPlanStep(i);
      if (result?.ok) appliedCount += 1;
      if (result?.validationFailed) break;
      if (!result?.ok) break;
    }
    if (appliedCount > 0) {
      setStatus(`Applied ${appliedCount} plan step(s) to disk`);
      setTimeout(() => setStatus(""), 2200);
    }
  };

  const rollbackPlanStep = async (stepIndex) => {
    const steps = executablePlanSteps;
    const snapshots = planStepSnapshots;
    if (!steps?.length || stepIndex < 0 || stepIndex >= steps.length) return;
    const snapshot = snapshots[stepIndex];
    if (!snapshot || typeof snapshot !== "object") return;
    try {
      setStatus(`Rolling back step ${stepIndex + 1}â€¦`);
      for (const [p, content] of Object.entries(snapshot)) {
        await apiPost("/devtools/write", { path: p, content });
        if (p === selectedPath && selectedSource === "repo") {
          setContent(content);
          setDirty(false);
        }
      }
      setPlanStepSnapshots((prev) => {
        const next = [...prev];
        next[stepIndex] = undefined;
        return next;
      });
      setExecutablePlanSteps((prev) =>
        prev.map((s, i) => (i === stepIndex ? { ...s, applied: false } : s))
      );
      setStatus("Step rolled back");
      setTimeout(() => setStatus(""), 800);
    } catch (e) {
      setStatus(e?.message || "Rollback failed");
      setTimeout(() => setStatus(""), 1200);
    }
  };

  const rollbackAllPlanSteps = async () => {
    const steps = executablePlanSteps;
    if (!steps?.length) return;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].applied && planStepSnapshots[i]) await rollbackPlanStep(i);
    }
  };

  const runAgent = async () => {
    try {
      const prompt = String(aiPrompt || "").trim();
      if (!prompt) return;
      setAiLastIntent("agent");
      setAgentRunning(true);
      setAgentLog("");
      const append = (s) => setAgentLog((prev) => (prev + String(s || "")).slice(-200_000));

      const maxIters = agentAutoFixOnce ? Math.max(1, Math.min(5, Number(agentMaxFixIters || 2))) : 1;
      let lastRun = null;
      agentStopRef.current = false;

      // Complexity / plan state for this run (user-facing, no hidden chain-of-thought).
      const complexity = {
        goal: prompt,
        constraints: [],
        assumptions: [],
        successCriteria: [],
        decisions: [],
        planText: "",
        steps: [],
        filesToInspect: [],
        filesToEdit: [],
        commands: [],
        inspected: [], // [{path, content}]
      };

      const complexityHeader = () => {
        const parts = [];
        parts.push(`# Managing complexity`);
        parts.push(`Goal: ${String(complexity.goal || "").trim()}`);
        if (complexity.constraints.length)
          parts.push(`Constraints:\n${complexity.constraints.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
        if (complexity.assumptions.length)
          parts.push(`Assumptions:\n${complexity.assumptions.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
        if (complexity.successCriteria.length)
          parts.push(`Success criteria:\n${complexity.successCriteria.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
        if (complexity.decisions.length)
          parts.push(`Decisions (so far):\n${complexity.decisions.slice(-10).map((s) => `- ${String(s || "").trim()}`).join("\n")}`);
        return parts.join("\n") + "\n";
      };

      const buildInspectedFilesContext = () => {
        if (!complexity.inspected.length) return "";
        const blocks = [];
        for (const f of complexity.inspected.slice(0, 12)) {
          const p = String(f?.path || "").trim();
          const c = String(f?.content || "");
          if (!p) continue;
          blocks.push(`File: ${p}\n\`\`\`\n${c}\n\`\`\``);
        }
        return ["", "Inspected files (auto-read):", ...blocks].join("\n");
      };

      const readInspectFiles = async (paths) => {
        const list = Array.isArray(paths) ? paths : [];
        if (!list.length) return;
        // Cap to keep requests fast.
        const unique = Array.from(new Set(list.map((p) => normalizePath(p)).filter(Boolean))).slice(0, 10);
        if (!unique.length) return;
        append(`\n# Inspecting files (${unique.length})\n`);
        for (const p of unique) {
          try {
            const data = await apiGet("/devtools/read", { path: p });
            const raw = String(data?.content ?? "");
            // Keep inspected snippets bounded; enough for reasoning, not giant files.
            const clipped = raw.length > 18_000 ? raw.slice(0, 18_000) + "\n\n// ... truncated ..." : raw;
            complexity.inspected.push({ path: p, content: clipped });
            append(`- Read ${p} (${raw.length} chars)\n`);
          } catch (e) {
            append(`- Failed to read ${p}: ${e?.message || "error"}\n`);
          }
        }
      };

      // 0) Plan first: understand goal, propose steps, identify blockers.
      try {
        append(`# Planning\n`);
        const plan = await requestPlanFromAi(prompt, { userId: "developeride_agent_plan" });
        const content = String(plan?.content || "").trim();
        const planText = String(plan?.artifacts?.plan || "").trim();
        const steps = Array.isArray(plan?.artifacts?.steps) ? plan.artifacts.steps : [];
        const assumptions = Array.isArray(plan?.artifacts?.assumptions) ? plan.artifacts.assumptions : [];
        const openQs = Array.isArray(plan?.artifacts?.openQuestions) ? plan.artifacts.openQuestions : [];
        const filesToInspect = Array.isArray(plan?.artifacts?.filesToInspect) ? plan.artifacts.filesToInspect : [];
        const filesToEdit = Array.isArray(plan?.artifacts?.filesToEdit) ? plan.artifacts.filesToEdit : [];
        const commands = Array.isArray(plan?.artifacts?.commands) ? plan.artifacts.commands : [];
        const success = Array.isArray(plan?.artifacts?.successCriteria) ? plan.artifacts.successCriteria : [];

        complexity.planText = planText;
        complexity.steps = steps.map((s) => String(s || "").trim()).filter(Boolean);
        complexity.assumptions = assumptions.map((s) => String(s || "").trim()).filter(Boolean);
        complexity.filesToInspect = filesToInspect.map((s) => String(s || "").trim()).filter(Boolean);
        complexity.filesToEdit = filesToEdit.map((s) => String(s || "").trim()).filter(Boolean);
        complexity.commands = commands.map((s) => String(s || "").trim()).filter(Boolean);
        complexity.successCriteria = success.map((s) => String(s || "").trim()).filter(Boolean);
        // lightweight constraints from known toggles/limits
        complexity.constraints = [
          `Agent max iterations: ${maxIters}`,
          `Task to run: ${agentTask}`,
          `Auto-apply: ${agentAutoApply ? "on" : "off"}`,
        ];

        if (content) append(`${content}\n`);
        if (planText) append(`\nPlan:\n${planText}\n`);
        if (assumptions.length) append(`\nAssumptions:\n${assumptions.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);
        if (steps.length) append(`\nSteps:\n${steps.slice(0, 12).map((s, i) => `${i + 1}. ${String(s || "").trim()}`).join("\n")}\n`);
        if (filesToInspect.length) append(`\nFiles to inspect:\n${filesToInspect.slice(0, 12).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);
        if (filesToEdit.length) append(`\nFiles to edit:\n${filesToEdit.slice(0, 20).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);
        if (commands.length) append(`\nCommands:\n${commands.slice(0, 10).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);
        if (success.length) append(`\nSuccess criteria:\n${success.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);

        // If the agent identifies blocking questions, stop before editing to avoid wrong changes.
        if (openQs.length) {
          append(`\nOpen questions (answer these and run Agent again):\n${openQs.slice(0, 8).map((s) => `- ${String(s || "").trim()}`).join("\n")}\n`);
          return;
        }

        // Auto-inspect requested files (repo understanding) before patching.
        await readInspectFiles(filesToInspect);
        if (complexity.inspected.length) {
          // Optional: re-plan once with the inspected context (improves multi-step correctness).
          try {
            append(`\n# Re-planning with inspected files\n`);
            const replanned = await requestPlanFromAi(
              [
                prompt,
                "",
                "We already inspected some files. Update the plan/steps/filesToEdit accordingly.",
                buildInspectedFilesContext(),
              ].join("\n"),
              { userId: "developeride_agent_plan_v2" }
            );
            const planText2 = String(replanned?.artifacts?.plan || "").trim();
            const steps2 = Array.isArray(replanned?.artifacts?.steps) ? replanned.artifacts.steps : [];
            const assumptions2 = Array.isArray(replanned?.artifacts?.assumptions) ? replanned.artifacts.assumptions : [];
            const filesToEdit2 = Array.isArray(replanned?.artifacts?.filesToEdit) ? replanned.artifacts.filesToEdit : [];
            const commands2 = Array.isArray(replanned?.artifacts?.commands) ? replanned.artifacts.commands : [];
            const success2 = Array.isArray(replanned?.artifacts?.successCriteria) ? replanned.artifacts.successCriteria : [];
            if (planText2) complexity.planText = planText2;
            if (steps2.length) complexity.steps = steps2.map((s) => String(s || "").trim()).filter(Boolean);
            if (assumptions2.length) complexity.assumptions = assumptions2.map((s) => String(s || "").trim()).filter(Boolean);
            if (filesToEdit2.length) complexity.filesToEdit = filesToEdit2.map((s) => String(s || "").trim()).filter(Boolean);
            if (commands2.length) complexity.commands = commands2.map((s) => String(s || "").trim()).filter(Boolean);
            if (success2.length) complexity.successCriteria = success2.map((s) => String(s || "").trim()).filter(Boolean);
            if (complexity.planText) append(`\nUpdated plan:\n${complexity.planText}\n`);
            if (complexity.steps.length) append(`\nUpdated steps:\n${complexity.steps.slice(0, 12).map((s, i) => `${i + 1}. ${s}`).join("\n")}\n`);
          } catch (e) {
            append(`# Re-plan failed (continuing): ${e?.message || "unknown"}\n`);
          }
          append(`\n# Complexity manager snapshot\n${complexityHeader()}\n`);
        }
      } catch (e) {
        append(`# Plan step failed (continuing anyway): ${e?.message || "unknown"}\n`);
      }

      for (let iter = 1; iter <= maxIters; iter++) {
        const isFirst = iter === 1;
        append(`\n# Agent iteration ${iter}/${maxIters}\n`);

        // 1) Get patch (first prompt or fix prompt)
        const ask =
          isFirst || !lastRun
            ? [
                prompt,
                "",
                complexityHeader(),
                complexity.planText ? `Plan:\n${complexity.planText}\n` : "",
                complexity.steps.length ? `Steps:\n${complexity.steps.map((s, i) => `${i + 1}. ${s}`).join("\n")}\n` : "",
                buildInspectedFilesContext(),
                "",
                "Now produce a patch that moves us toward the success criteria.",
              ]
                .filter(Boolean)
                .join("\n")
            : [
                prompt,
                "",
                complexityHeader(),
                complexity.planText ? `Plan:\n${complexity.planText}\n` : "",
                complexity.steps.length ? `Steps:\n${complexity.steps.map((s, i) => `${i + 1}. ${s}`).join("\n")}\n` : "",
                buildInspectedFilesContext(),
                "",
                "The build/test failed. Fix the errors and return a patch JSON only.",
                `Task: ${agentTask}`,
                "STDOUT:",
                String(lastRun?.stdout || ""),
                "",
                "STDERR:",
                String(lastRun?.stderr || ""),
              ].join("\n");

        append(isFirst ? `# Proposing patch\n` : `# Proposing fix patch\n`);
        const patch = await requestPatchFromAi(ask, { userId: isFirst ? "developeride_agent" : `developeride_agent_fix_${iter}` });
        if (!patch?.files) {
          append(`# No patch returned\n`);
          return;
        }

        // Show it in UI for transparency (but apply deterministically with locals)
        const localSelected = new Set(Object.keys(patch.files).map((p) => normalizePath(p)).filter(Boolean));
        setPatchFiles(patch.files);
        setPatchSummary(patch.summary || (isFirst ? "Patch ready" : "Fix patch ready"));
        setPatchSelected(localSelected);

        // 2) Apply patch now (required for loop-to-green)
        if (!agentAutoApply) {
          append(`# Patch ready (not applied). Enable "Auto-apply" to run agent loop.\n`);
          return;
        }
        append(`# Applying patch to disk\n`);
        const applied = await applyPatchNow({ confirm: false, filesOverride: patch.files, selectedOverride: localSelected });
        if (!applied?.ok) {
          append(`# âŒ Patch apply failed: ${applied?.error || "unknown"}\n`);
          return;
        }
        // tiny buffer for FS flush on some platforms
        await new Promise((r) => setTimeout(r, 120));

        // 3) Run task
        append(`\n# Running ${agentTask}\n`);
        const run = await runTaskStreamed(agentTask, append);
        lastRun = run;
        append(`\n# exitCode=${run?.exitCode}\n`);
        if (run?.ok) {
          append(`# âœ… Task OK\n`);
          return;
        }

        if (!agentAutoFixOnce) {
          append(`# âŒ Failed (auto-fix disabled)\n`);
          return;
        }
        append(`# âŒ Failed â€” will attempt next fix iteration\n`);
      }

      append(`\n# âŒ Gave up after ${maxIters} iteration(s)\n`);
    } catch (e) {
      setAgentLog((prev) => prev + `\n# Agent error: ${e?.message || "failed"}\n`);
    } finally {
      setAgentRunning(false);
    }
  };

  const stopAgentTask = async () => {
    try {
      agentStopRef.current = true;
      const id = String(agentProcId || "").trim();
      if (id) {
        try {
          await apiPost("/devtools/stop", { id });
        } catch (_) {}
      }
      try {
        agentEsRef.current?.close?.();
      } catch (_) {}
      agentEsRef.current = null;
      setAgentProcId("");
    } catch (_) {}
  };

  const runTaskStreamed = async (task, append) => {
    const t = String(task || "").trim();
    if (!t) throw new Error("task required");
    // Start a task, then stream logs via SSE
    const started = await apiPost("/devtools/exec", { task: t });
    const id = String(started?.id || "").trim();
    if (!id) throw new Error("Failed to start task");
    setAgentProcId(id);
    agentStopRef.current = false;

    return await new Promise((resolve, reject) => {
      let stdout = "";
      let stderr = "";
      let done = false;

      const url = sseUrl("/devtools/stream", { id });
      const es = new EventSource(url);
      agentEsRef.current = es;

      const safeAppend = (s) => {
        try {
          if (typeof append === "function" && s) append(s);
        } catch (_) {}
      };

      es.addEventListener("start", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          safeAppend(`# stream started (${data?.task || t})\n`);
        } catch (_) {
          safeAppend(`# stream started (${t})\n`);
        }
      });
      es.addEventListener("stdout", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          const chunk = String(data?.chunk || "");
          stdout += chunk;
          safeAppend(chunk);
        } catch (_) {}
      });
      es.addEventListener("stderr", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          const chunk = String(data?.chunk || "");
          stderr += chunk;
          safeAppend(chunk);
        } catch (_) {}
      });
      es.addEventListener("exit", (e) => {
        done = true;
        try {
          const data = JSON.parse(e.data || "{}");
          const code = Number(data?.exitCode);
          const ok = code === 0;
          try { es.close(); } catch (_) {}
          agentEsRef.current = null;
          setAgentProcId("");
          resolve({ ok, exitCode: code, stdout, stderr, signal: data?.signal || null });
        } catch (_) {
          try { es.close(); } catch (_) {}
          agentEsRef.current = null;
          setAgentProcId("");
          resolve({ ok: false, exitCode: 1, stdout, stderr });
        }
      });
      es.onerror = () => {
        // EventSource can fire error on normal close; avoid spurious rejects if we already got exit.
        if (done) return;
        if (agentStopRef.current) {
          done = true;
          try { es.close(); } catch (_) {}
          agentEsRef.current = null;
          setAgentProcId("");
          resolve({ ok: false, exitCode: 1, stdout, stderr, stopped: true });
          return;
        }
        done = true;
        try { es.close(); } catch (_) {}
        agentEsRef.current = null;
        setAgentProcId("");
        reject(new Error("Task stream disconnected"));
      };
    });
  };

  const runAgentFixTask = async ({ task, maxIters }) => {
    try {
      const t = String(task || agentTask || "").trim();
      if (!t) return;
      setAiLastIntent("agent");
      setAgentRunning(true);
      const append = (s) => setAgentLog((prev) => (prev + String(s || "")).slice(-200_000));
      setAgentLog("");
      const max = Math.max(1, Math.min(5, Number(maxIters || agentMaxFixIters || 2)));
      agentStopRef.current = false;

      append(`# Agent: fix task\nTask: ${t}\nMax iterations: ${max}\n\n`);

      // 0) Run task first (take action first, then ask AI only if it fails)
      append(`# Running ${t}\n`);
      let run = await runTaskStreamed(t, append);
      append(`\n# exitCode=${run?.exitCode}\n`);
      if (run?.ok) {
        append(`# âœ… Task OK (nothing to fix)\n`);
        return;
      }

      for (let iter = 1; iter <= max; iter++) {
        append(`\n# Fix iteration ${iter}/${max}\n`);
        const ask = [
          "Fix the errors and return a patch JSON only.",
          "You may edit multiple files.",
          `Task: ${t}`,
          "",
          "STDOUT:",
          String(run?.stdout || ""),
          "",
          "STDERR:",
          String(run?.stderr || ""),
        ].join("\n");

        append(`# Proposing fix patch\n`);
        const patch = await requestPatchFromAi(ask, { userId: `developeride_agent_fix_${t}_${iter}` });
        if (!patch?.files) {
          append(`# âŒ No patch returned\n`);
          return;
        }

        const localSelected = new Set(Object.keys(patch.files).map((p) => normalizePath(p)).filter(Boolean));
        setPatchFiles(patch.files);
        setPatchSummary(patch.summary || "Fix patch ready");
        setPatchSelected(localSelected);

        append(`# Applying patch to disk\n`);
        const applied = await applyPatchNow({ confirm: false, filesOverride: patch.files, selectedOverride: localSelected });
        if (!applied?.ok) {
          append(`# âŒ Patch apply failed: ${applied?.error || "unknown"}\n`);
          return;
        }
        await new Promise((r) => setTimeout(r, 140));

        append(`\n# Re-running ${t}\n`);
        run = await apiPost("/devtools/run", { task: t });
        append(run?.stdout ? String(run.stdout) : "");
        append(run?.stderr ? "\n" + String(run.stderr) : "");
        append(`\n# exitCode=${run?.exitCode}\n`);
        if (run?.ok) {
          append(`# âœ… Task OK\n`);
          return;
        }
      }

      append(`\n# âŒ Gave up after ${max} iteration(s)\n`);
    } catch (e) {
      setAgentLog((prev) => prev + `\n# Agent error: ${e?.message || "failed"}\n`);
    } finally {
      setAgentRunning(false);
    }
  };

  const togglePatchSelected = (p) => {
    const path = normalizePath(p);
    if (!path) return;
    setPatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const selectAllPatch = () => {
    if (!patchFiles) return;
    setPatchSelected(new Set(Object.keys(patchFiles).map((p) => normalizePath(p)).filter(Boolean)));
  };

  const selectNonePatch = () => setPatchSelected(new Set());

  const togglePatchFile = (p) => {
    const path = String(p || "");
    if (!path) return;
    setPatchExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const buildFileIndex = async () => {
    try {
      if (indexing) return;
      setIndexing(true);
      setStatus("Indexing filesâ€¦");
      const results = [];
      const seenDirs = new Set();
      const queue = [""];
      const maxFiles = 6000;
      const maxDirs = 2000;

      while (queue.length && results.length < maxFiles && seenDirs.size < maxDirs) {
        const dir = queue.shift();
        const key = normalizeFolder(dir);
        if (seenDirs.has(key)) continue;
        seenDirs.add(key);
        const data = await apiGet("/devtools/list", { path: dir || "" });
        const items = Array.isArray(data.items) ? data.items : [];
        for (const it of items) {
          if (results.length >= maxFiles) break;
          if (it.type === "dir") queue.push(it.path);
          if (it.type === "file") results.push(String(it.path || ""));
        }
      }

      setFileIndex(results);
      setStatus("");
    } catch (e) {
      setStatus(e?.message || "Failed to index files");
    } finally {
      setIndexing(false);
    }
  };

  const breadcrumbParts = useMemo(() => {
    const rel = normalizeFolder(activeFolder);
    if (!rel) return [];
    return rel.split("/").filter(Boolean);
  }, [activeFolder]);

  const setFolderFromBreadcrumb = async (idx) => {
    const parts = breadcrumbParts.slice(0, idx + 1);
    const p = parts.join("/");
    // Expand each segment progressively so the tree reveals the folder
    let cur = "";
    for (const seg of parts) {
      cur = cur ? `${cur}/${seg}` : seg;
      await loadChildren(cur);
      setExpandedDirs((prev) => {
        const next = new Set(prev);
        next.add(cur);
        return next;
      });
    }
    setActiveFolder(p);
  };

  const aiPromptRef = useRef(null);

  const fetchOutlineSymbols = async () => {
    try {
      if (!selectedPath) {
        setOutlineSymbols([]);
        setOutlineErr("");
        return;
      }
      // Only try for TS/JS-like files (cheap guard)
      if (!isTsLike) {
        setOutlineSymbols([]);
        setOutlineErr("");
        return;
      }
      setOutlineBusy(true);
      setOutlineErr("");
      try {
        outlineAbortRef.current?.abort?.();
      } catch (_) {}
      const ctrl = new AbortController();
      outlineAbortRef.current = ctrl;

      const tsPath = selectedSource === "generated" ? `.zolaid_generated/${normalizePath(selectedPath)}` : selectedPath;
      const data = await apiPost("/devtools/ts/symbols", { path: tsPath, content: String(content || "") }, { signal: ctrl.signal });
      const syms = Array.isArray(data?.symbols) ? data.symbols : [];
      setOutlineSymbols(syms.slice(0, 300));
    } catch (e) {
      if (String(e?.name || "") === "AbortError") return;
      setOutlineSymbols([]);
      setOutlineErr(String(e?.message || "Outline failed"));
    } finally {
      setOutlineBusy(false);
    }
  };

  // Keep Outline fresh (debounced)
  useEffect(() => {
    try {
      if (outlineTimerRef.current) clearTimeout(outlineTimerRef.current);
      outlineTimerRef.current = setTimeout(() => {
        fetchOutlineSymbols().catch(() => {});
      }, 350);
    } catch (_) {}
    return () => {
      try {
        if (outlineTimerRef.current) clearTimeout(outlineTimerRef.current);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, selectedSource, content, isTsLike]);

  const buildAiSearchResultsBlock = async (prompt) => {
    try {
      const q = String(prompt || "").trim();
      if (!q) return "";
      const cacheKey = `search:${q.toLowerCase()}`;
      const cached = retrievalCacheRef.current.get(cacheKey);
      if (cached && Date.now() - Number(cached.at || 0) < 45_000) return String(cached.value || "");

      try {
        retrievalAbortRef.current.search?.abort?.();
      } catch (_) {}
      const ctrl = new AbortController();
      retrievalAbortRef.current.search = ctrl;

      const data = await apiPost("/devtools/search", { query: q, maxResults: 8 }, { signal: ctrl.signal });
      const results = Array.isArray(data?.results) ? data.results : [];
      if (!results.length) return "";
      const lines = [];
      for (const r of results) {
        const p = String(r.path || "");
        if (!p) continue;
        const prev = Array.isArray(r.preview) ? r.preview : [];
        const hitLines = prev
          .slice(0, 2)
          .map((h) => `  - L${h.line}: ${String(h.text || "").slice(0, 220)}`)
          .join("\n");
        lines.push(`- ${p}${prev.length ? ` (score ${Number(r.score || 0).toFixed(2)})` : ""}\n${hitLines}`);
      }
      if (!lines.length) return "";
      const out = ["", "Repo search results (auto):", ...lines].join("\n");
      retrievalCacheRef.current.set(cacheKey, { at: Date.now(), value: out });
      return out;
    } catch (_) {
      return "";
    }
  };

  const buildSemanticSearchBlock = async (prompt) => {
    try {
      const q = String(prompt || "").trim();
      if (!q) return "";
      const data = await apiPost("/devtools/semantic-search", { query: q, maxResults: 6 });
      const results = Array.isArray(data?.results) ? data.results : [];
      if (!results.length) return "";
      const lines = results.map((r) => {
        const p = String(r.path || "");
        const prev = Array.isArray(r.preview) ? r.preview : [];
        const hitLines = prev.slice(0, 2).map((h) => `  L${h.line}: ${String(h.text || "").slice(0, 180)}`).join("\n");
        return `- ${p}${prev.length ? ` (semantic)` : ""}\n${hitLines}`;
      });
      return ["", "Semantic search (embeddings):", ...lines].join("\n");
    } catch (_) {
      return "";
    }
  };

  const TreeRow = ({ item, depth }) => {
    const isDir = item?.type === "dir";
    const p = String(item?.path || "");
    const expanded = isDir && expandedDirs.has(p);
    const children = isDir ? childrenByDir.get(p) : null;
    const isFile = item?.type === "file";
    const isNavSelected = navSelection && navSelection.source === "repo" && navSelection.path === p;
    const isOpenSelected = selectedSource === "repo" && selectedPath === p;

    return (
      <div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "6px 6px",
            borderRadius: 10,
            marginLeft: depth * 12,
            background: isOpenSelected ? "var(--z-row-active)" : isNavSelected ? "var(--z-row-selected)" : "transparent",
            color: "var(--z-text)",
          }}
          onDoubleClick={() => {
            if (!isDir) openFile(p);
          }}
          onContextMenu={(e) => {
            openContextMenu(e, { source: "repo", path: p, isDir });
          }}
        >
          <button
            type="button"
            className="export-btn"
            style={{ padding: "4px 8px", minWidth: 28 }}
            onClick={() => (isDir ? toggleDir(p) : openFile(p))}
            title={isDir ? (expanded ? "Collapse folder" : "Expand folder") : "Open file"}
          >
            {isDir ? (expanded ? "â–¾" : "â–¸") : " "}
          </button>
          <div
            style={{ cursor: "pointer", flex: 1, userSelect: "none" }}
            onClick={() => {
              if (isDir) {
                setActiveFolder(normalizeFolder(p));
                toggleDir(p);
              } else {
                openFile(p);
              }
            }}
            title={p}
          >
            {item?.name || String(p || "").split("/").filter(Boolean).pop() || "(unnamed)"}
          </div>
        </div>

        {isDir && expanded && Array.isArray(children) && children.length > 0 && (
          <div>
            {children.map((ch) => (
              <TreeRow key={ch.path} item={ch} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const generatedTree = useMemo(() => {
    const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : null;
    if (!src) return null;
    const paths = Object.keys(src)
      .map((p) => String(p || "").replace(/\\/g, "/").replace(/^\/+/, ""))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const root = { name: "(generated)", path: "", type: "dir", children: new Map() };
    for (const full of paths) {
      const isKeep = full.endsWith("/.keep");
      const parts = full.split("/").filter(Boolean);
      if (isKeep) parts.pop(); // folder marker: don't render the .keep file node
      let cur = root;
      let curPath = "";
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        curPath = curPath ? `${curPath}/${part}` : part;
        const isLeaf = i === parts.length - 1;
        if (isLeaf) {
          if (isKeep) {
            // marker: ensure folder exists, but no leaf file
            if (!cur.children.has(part)) {
              cur.children.set(part, { name: part, path: curPath, type: "dir", children: new Map() });
            }
            continue;
          }
          if (!cur.children.has(part)) {
            cur.children.set(part, { name: part, path: curPath, type: "file", children: null });
          }
        } else {
          if (!cur.children.has(part)) {
            cur.children.set(part, { name: part, path: curPath, type: "dir", children: new Map() });
          }
          cur = cur.children.get(part);
        }
      }
    }

    const toArray = (node) => {
      if (!node || node.type !== "dir") return node;
      const items = Array.from(node.children.values());
      items.sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === "dir" ? -1 : 1));
      return {
        ...node,
        children: items.map((ch) => (ch.type === "dir" ? toArray(ch) : ch)),
      };
    };
    return toArray(root);
  }, [generatedFiles]);

  const toggleGeneratedDir = (dirPath) => {
    const p = String(dirPath || "");
    if (!p) {
      setGeneratedRootExpanded((v) => !v);
      return;
    }
    setGeneratedExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const collapseAllGenerated = () => {
    try {
      setGeneratedRootExpanded(false);
      setGeneratedExpanded(new Set());
    } catch (_) {}
  };

  const openBottom = (tab) => {
    try {
      setTerminalOpen(true);
      if (tab) setBottomTab(String(tab));
    } catch (_) {}
  };

  const pushTimeline = (ev) => {
    try {
      const e = ev && typeof ev === "object" ? ev : { text: String(ev || "") };
      const item = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        at: Date.now(),
        kind: String(e.kind || "info"),
        text: String(e.text || "").slice(0, 220),
        source: e.source ? String(e.source) : undefined,
        path: e.path ? String(e.path) : undefined,
        line: e.line != null ? Number(e.line) : undefined,
        column: e.column != null ? Number(e.column) : undefined,
      };
      setTimelineEvents((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return [...arr, item].slice(-200);
      });
    } catch (_) {}
  };

  // Seed timeline once
  useEffect(() => {
    pushTimeline({ kind: "info", text: "IDE opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBottom = (tab) => {
    try {
      setTerminalOpen((v) => {
        const next = !v;
        if (next && tab) setBottomTab(String(tab));
        return next;
      });
    } catch (_) {}
  };

  const openPort = (port) => {
    try {
      const p = Number(port);
      if (!p || p < 1 || p > 65535) return;
      const proto = String(window?.location?.protocol || "http:");
      const host = String(window?.location?.hostname || "localhost");
      const url = `${proto}//${host}:${p}/`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {}
  };

  const GeneratedRow = ({ node, depth }) => {
    if (!node) return null;
    if (node.type === "file") {
      const full = node.path;
      const active = selectedSource === "generated" && selectedPath === full;
      const isNavSelected = navSelection && navSelection.source === "generated" && navSelection.path === full;
      return (
        <div
          style={{
            marginLeft: depth * 12,
            padding: "4px 6px",
            borderRadius: 8,
            fontSize: 11,
            cursor: "pointer",
            background: active ? "var(--z-row-active)" : isNavSelected ? "var(--z-row-selected)" : "transparent",
            whiteSpace: "nowrap",
            color: "var(--z-text)",
          }}
          title={full}
          onClick={() => openGeneratedFile(full)}
          onContextMenu={(e) => openContextMenu(e, { source: "generated", path: full, isDir: false })}
        >
          {node.name || String(full || "").split("/").filter(Boolean).pop() || "(unnamed)"}
        </div>
      );
    }

    const expanded = node.path === "" ? generatedRootExpanded : generatedExpanded.has(node.path);
    return (
      <div>
        <div
          style={{
            marginLeft: depth * 12,
            padding: "4px 6px",
            borderRadius: 8,
            fontSize: 11,
            cursor: "pointer",
            fontWeight: 900,
            userSelect: "none",
            whiteSpace: "nowrap",
            color: "var(--z-text)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
          title={node.path || "(root)"}
          onContextMenu={(e) => {
            const p = String(node.path || "");
            // don't allow deleting the virtual "(generated)" root
            if (!p) return;
            openContextMenu(e, { source: "generated", path: p, isDir: true });
          }}
          onClick={() => {
            toggleGeneratedDir(node.path);
          }}
        >
          <span style={{ width: 16, display: "inline-flex", justifyContent: "center", opacity: 0.85 }}>
            {expanded ? "â–¾" : "â–¸"}
          </span>
          <span
            aria-hidden="true"
            style={{
              width: 16,
              height: 12,
              borderRadius: 3,
              background: "#facc15",
              border: "1px solid #eab308",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35)",
              flex: "0 0 auto",
            }}
          />
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {node.name || String(node.path || "").split("/").filter(Boolean).pop() || "(folder)"}
          </span>
        </div>
        {expanded && Array.isArray(node.children) && node.children.length > 0 && (
          <div>
            {node.children.map((ch) => (
              <GeneratedRow key={`${ch.type}:${ch.path}`} node={ch} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (enabled === false) {
    const isNetwork = status && /backend not running|backend unreachable|failed to fetch|network error|ECONNREFUSED|proxy error/i.test(String(status));
    const onLocalhost =
      typeof window !== "undefined" &&
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(String(window.location?.origin || ""));
    const devtoolsOffHint = onLocalhost
      ? "In backend/.env set DEVTOOLS_ENABLED=true and restart the backend, then Retry. Set DEVTOOLS_ENABLED=false to disable again."
      : "The API must have DEVTOOLS_ENABLED=true. On Cloud Run: edit the backend service â†’ Variables â†’ add DEVTOOLS_ENABLED=true â†’ redeploy â€” or run: gcloud run services update YOUR-BACKEND-SERVICE --region REGION --update-env-vars DEVTOOLS_ENABLED=true. Rebuild the backend image if your Dockerfile does not set it yet.";
    return (
      <div style={{ padding: 14, fontSize: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>
          {isNetwork ? "Cannot reach backend" : "Developer tools disabled"}
        </div>
        <div style={{ color: "rgba(0,0,0,0.70)" }}>
          {isNetwork
            ? "Backend is not running. In the project root (folder with backend/ and frontend/) run: npm run start:backend â€” or npm run dev for both. Open http://localhost:8080/health in the browser (or :8081 if the server chose another port). On Windows you can use start-backend.bat."
            : devtoolsOffHint}
        </div>
        {status && !isNetwork ? <div style={{ marginTop: 10, color: "#b91c1c" }}>{status}</div> : null}
        <button type="button" onClick={() => { clearBackendBackoff(); refresh(""); }} style={{ marginTop: 12, padding: "6px 12px", cursor: "pointer" }}>Retry</button>
      </div>
    );
  }

  return (
    <div
      className={`zolaid-ide-root ${uiTheme === "vscode" ? "zolaid-ide-vscode" : ""}`}
      style={{
        height: "100%",
        display: "grid",
        gridTemplateColumns: "52px 320px 1fr 360px",
        gridTemplateRows: "auto 1fr",
        gap: 10,
        background: "var(--z-ide-bg)",
        color: "var(--z-text)",
        minHeight: 0,
      }}
    >
      {/* Top menu bar (pinned like VS Code) */}
      <div className="z-menubar" role="menubar" aria-label="Menu Bar" style={{ gridColumn: "1 / -1", gridRow: 1 }}>
        {["File", "Edit", "Selection", "View", "Go", "Run", "Terminal", "Help"].map((m) => (
          <button key={m} type="button" className="z-menubar-btn" onClick={(e) => openLeftMenu(m, e)} title={m} role="menuitem">
            {m}
          </button>
        ))}
      </div>

      {/* Symbols modal (Go to Symbol) */}
      {symbolOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 25530,
            background: "rgba(0,0,0,0.18)",
            backdropFilter: "blur(2px)",
          }}
          onMouseDown={() => setSymbolOpen(false)}
        >
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: 90,
              transform: "translateX(-50%)",
              width: 820,
              maxWidth: "96vw",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              color: "rgba(255,255,255,0.92)",
              padding: 12,
              maxHeight: "70vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 12, opacity: 0.9 }}>Go to symbol</div>
              <button className="export-btn" type="button" onClick={() => setSymbolOpen(false)}>
                Close
              </button>
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
              <input
                ref={symbolRef}
                value={symbolQuery}
                onChange={(e) => setSymbolQuery(e.target.value)}
                placeholder={symbolBusy ? "Loading symbolsâ€¦" : "Type to filter symbolsâ€¦"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.92)",
                  outline: "none",
                }}
                onKeyDown={(e) => {
                  const key = String(e.key || "");
                  if (key === "Escape") {
                    e.preventDefault();
                    setSymbolOpen(false);
                    return;
                  }
                  if (key === "ArrowDown") {
                    e.preventDefault();
                    setSymbolIndex((i) => Math.min((filteredSymbols.length || 1) - 1, i + 1));
                    return;
                  }
                  if (key === "ArrowUp") {
                    e.preventDefault();
                    setSymbolIndex((i) => Math.max(0, i - 1));
                    return;
                  }
                  if (key === "Enter") {
                    e.preventDefault();
                    const s = filteredSymbols[symbolIndex];
                    if (s) jumpToSymbol(s);
                  }
                }}
              />
            </div>
            <div style={{ marginTop: 10, overflow: "auto", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)" }}>
              {symbolBusy ? (
                <div style={{ padding: 12, fontSize: 12, opacity: 0.8 }}>Loadingâ€¦</div>
              ) : !filteredSymbols.length ? (
                <div style={{ padding: 12, fontSize: 12, opacity: 0.8 }}>No symbols.</div>
              ) : (
                filteredSymbols.slice(0, 200).map((s, idx) => (
                  <div
                    key={`${s.container || ""}:${s.name || ""}:${idx}`}
                    style={{
                      padding: "8px 10px",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                      background: idx === symbolIndex ? "rgba(59,130,246,0.18)" : "transparent",
                      cursor: "pointer",
                      display: "flex",
                      gap: 10,
                      alignItems: "baseline",
                    }}
                    onMouseEnter={() => setSymbolIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      jumpToSymbol(s);
                    }}
                    title={`${s.kind || "symbol"} @ L${s.line}:${s.character}`}
                  >
                    <div style={{ fontWeight: 900, minWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.75, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.container || ""}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.8, whiteSpace: "nowrap" }}>
                      L{s.line}:{s.character}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Hover panel (on-demand) */}
      {hoverOpen ? (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 25510,
            width: "min(520px, calc(100vw - 24px))",
            maxHeight: "40vh",
            overflow: "auto",
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 12,
            boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
            padding: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, fontSize: 12 }}>Hover info</div>
            <button className="export-btn" type="button" onClick={() => setHoverOpen(false)} style={{ padding: "2px 8px" }}>
              âœ•
            </button>
          </div>
          <pre style={{ margin: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{hoverText || ""}</pre>
        </div>
      ) : null}

      {gotoOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 25520,
            background: "rgba(0,0,0,0.18)",
            backdropFilter: "blur(2px)",
          }}
          onMouseDown={() => setGotoOpen(false)}
        >
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: 90,
              transform: "translateX(-50%)",
              width: 760,
              maxWidth: "96vw",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              color: "rgba(255,255,255,0.92)",
              padding: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 12, opacity: 0.9 }}>Go to file</div>
              <button className="export-btn" type="button" onClick={() => setGotoOpen(false)}>
                Close
              </button>
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
              <input
                ref={gotoRef}
                value={gotoQuery}
                onChange={(e) => setGotoQuery(e.target.value)}
                placeholder="Type a filename or pathâ€¦"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.92)",
                  outline: "none",
                }}
                onKeyDown={async (e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setGotoIndex((i) => Math.min(i + 1, Math.max(0, gotoItems.length - 1)));
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setGotoIndex((i) => Math.max(0, i - 1));
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const p = gotoItems[gotoIndex];
                    if (!p) return;
                    setGotoOpen(false);
                    await openFile(p);
                  }
                }}
                autoFocus
              />
            </div>
            <div style={{ marginTop: 10, maxHeight: 420, overflow: "auto" }}>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 8 }}>
                {gotoIndexing ? "Indexingâ€¦" : `${gotoItems.length} item(s) shown`}
              </div>
              {gotoItems.map((p, idx) => {
                const active = idx === gotoIndex;
                return (
                  <div
                    key={`${p}-${idx}`}
                    style={{
                      padding: "10px 10px",
                      borderRadius: 12,
                      cursor: "pointer",
                      background: active ? "rgba(59,130,246,0.25)" : "rgba(255,255,255,0.06)",
                      border: active ? "1px solid rgba(59,130,246,0.35)" : "1px solid rgba(255,255,255,0.10)",
                      marginBottom: 8,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                    }}
                    onMouseEnter={() => setGotoIndex(idx)}
                    onClick={async () => {
                      setGotoOpen(false);
                      await openFile(p);
                    }}
                    title="Click to open"
                  >
                    {p}
                  </div>
                );
              })}
              {!gotoIndexing && !gotoItems.length ? <div style={{ opacity: 0.75, padding: "10px 0" }}>No matches.</div> : null}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, opacity: 0.75 }}>
              <span>â†‘â†“ to navigate</span>
              <span style={{ marginLeft: 10 }}>Enter to open</span>
              <span style={{ marginLeft: 10 }}>Esc to close</span>
            </div>
          </div>
        </div>
      ) : null}
      {findOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 25500,
            background: "rgba(0,0,0,0.18)",
            backdropFilter: "blur(2px)",
          }}
          onMouseDown={() => setFindOpen(false)}
        >
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: 90,
              transform: "translateX(-50%)",
              width: 760,
              maxWidth: "96vw",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              color: "rgba(255,255,255,0.92)",
              padding: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 12, opacity: 0.9 }}>Find in files</div>
              <button className="export-btn" type="button" onClick={() => setFindOpen(false)}>
                Close
              </button>
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 220px 90px", gap: 8 }}>
              <input
                id="zolaid-find-input"
                value={findQuery}
                onChange={(e) => setFindQuery(e.target.value)}
                placeholder="Search textâ€¦"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.92)",
                  outline: "none",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runFindInFiles();
                  }
                }}
                autoFocus
              />
              <input
                value={findRoot}
                onChange={(e) => setFindRoot(e.target.value)}
                placeholder="Folder (optional)"
                title="Limit search to a folder (e.g. frontend/src)"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.92)",
                  outline: "none",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runFindInFiles();
                  }
                }}
              />
              <button className="export-btn" type="button" onClick={runFindInFiles} disabled={findBusy}>
                {findBusy ? "â€¦" : "Find"}
              </button>
            </div>
            <div style={{ marginTop: 10, maxHeight: 420, overflow: "auto" }}>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 8 }}>
                {findBusy ? "Searchingâ€¦" : `${findResults.length} result(s)`}
              </div>
              {findResults.map((r, idx) => (
                <div
                  key={`${r.path}:${r.line}:${idx}`}
                  style={{
                    padding: "10px 10px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.10)",
                    background: "rgba(255,255,255,0.06)",
                    marginBottom: 8,
                    cursor: "pointer",
                  }}
                  onClick={async () => {
                    const p = String(r.path || "");
                    const ln = Number(r.line || 1);
                    if (!p) return;
                    setFindOpen(false);
                    pendingRevealRef.current = { path: p, line: ln, column: 1 };
                    await openFile(p);
                  }}
                  title="Click to open"
                >
                  <div style={{ fontWeight: 900, fontSize: 12 }}>
                    {r.path} : {r.line}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.9, whiteSpace: "pre-wrap" }}>{String(r.text || "")}</div>
                </div>
              ))}
              {!findBusy && !findResults.length ? <div style={{ opacity: 0.75, padding: "10px 0" }}>No matches.</div> : null}
            </div>
          </div>
        </div>
      ) : null}
      {paletteOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 26000,
            background: "rgba(0,0,0,0.18)",
            backdropFilter: "blur(2px)",
          }}
          onMouseDown={() => setPaletteOpen(false)}
        >
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: 90,
              transform: "translateX(-50%)",
              width: 640,
              maxWidth: "96vw",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              color: "rgba(255,255,255,0.92)",
              padding: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, fontSize: 12, marginBottom: 8, opacity: 0.9 }}>Command Palette</div>
            <input
              ref={paletteRef}
              value={paletteQuery}
              onChange={(e) => setPaletteQuery(e.target.value)}
              placeholder="Type a commandâ€¦"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.92)",
                outline: "none",
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setPaletteOpen(false);
                }
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setPaletteIndex((i) => Math.min(i + 1, Math.max(0, paletteItems.length - 1)));
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setPaletteIndex((i) => Math.max(0, i - 1));
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  const item = paletteItems[paletteIndex];
                  if (!item) return;
                  setPaletteOpen(false);
                  try { item.run?.(); } catch (_) {}
                }
              }}
              autoFocus
            />
            <div style={{ marginTop: 10, maxHeight: 320, overflow: "auto" }}>
              {paletteItems.length ? (
                paletteItems.slice(0, 40).map((it, idx) => {
                  const active = idx === paletteIndex;
                  return (
                    <div
                      key={it.id}
                      style={{
                        padding: "10px 10px",
                        borderRadius: 12,
                        cursor: "pointer",
                        background: active ? "rgba(59,130,246,0.25)" : "transparent",
                        border: active ? "1px solid rgba(59,130,246,0.35)" : "1px solid transparent",
                      }}
                      onMouseEnter={() => setPaletteIndex(idx)}
                      onClick={() => {
                        setPaletteOpen(false);
                        try { it.run?.(); } catch (_) {}
                      }}
                    >
                      {it.label}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "10px 10px", opacity: 0.75 }}>No commands found.</div>
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, opacity: 0.75 }}>
              <span>â†‘â†“ to navigate</span>
              <span style={{ marginLeft: 10 }}>Enter to run</span>
              <span style={{ marginLeft: 10 }}>Esc to close</span>
            </div>
          </div>
        </div>
      ) : null}
      {(aiToolsOpen || agentMenuOpen) ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 25540 }} onMouseDown={closeAllMenus}>
          {aiToolsOpen ? (
            <div
              style={{
                position: "fixed",
                left: Math.max(
                  8,
                  Math.min(aiToolsPos.x, ((typeof window !== "undefined" ? window.innerWidth : 1024) || 1024) - 260)
                ),
                top: Math.max(
                  8,
                  Math.min(aiToolsPos.y, ((typeof window !== "undefined" ? window.innerHeight : 768) || 768) - 240)
                ),
                width: 250,
                padding: 10,
                borderRadius: 14,
                background: "rgba(15, 23, 42, 0.55)",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                color: "rgba(255,255,255,0.92)",
                fontSize: 12,
                maxHeight: "min(70vh, 360px)",
                overflow: "auto",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Tools</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ fontSize: 11, opacity: 0.85 }}>Quality</div>
                <select
                  value={aiQuality}
                  onChange={(e) => setAiQuality(String(e.target.value || "fast"))}
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    padding: "4px 8px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.92)",
                    outline: "none",
                  }}
                >
                  <option value="fast">Fast</option>
                  <option value="best">Best (2-pass)</option>
                </select>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                <input type="checkbox" checked={aiUseScan} onChange={(e) => setAiUseScan(e.target.checked)} />
                Use scan
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                <input type="checkbox" checked={aiAttachRelevant} onChange={(e) => setAiAttachRelevant(e.target.checked)} />
                Attach relevant files
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                <input type="checkbox" checked={aiIncludeGenerated} onChange={(e) => setAiIncludeGenerated(e.target.checked)} />
                Include generated list
              </label>

              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setAiToolsOpen(false);
                  aiFileInputRef.current?.click();
                }}
              >
                Upload{Array.isArray(aiAttachments) && aiAttachments.length ? ` (${aiAttachments.length})` : ""}
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                disabled={aiBusy || aiAttachBusy}
                onClick={() => {
                  setAiToolsOpen(false);
                  planAi();
                }}
              >
                Plan
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                disabled={patchBusy || aiBusy || aiAttachBusy}
                onClick={() => {
                  setAiToolsOpen(false);
                  proposePatch();
                }}
              >
                Propose patch
              </button>
              <button
                type="button"
                className="export-btn"
                style={{
                  textAlign: "left",
                  background: "rgba(255,255,255,0.08)",
                  color: "inherit",
                }}
                onClick={() => {
                  openAgentMenu({ preferSideBySideWithTools: true });
                }}
              >
                Agentâ€¦
              </button>
              <div style={{ marginTop: 6, opacity: 0.8, fontSize: 11, whiteSpace: "pre-wrap" }}>
                Context: {selectedPath ? `${selectedSource}:${selectedPath}` : "no file selected"}
              </div>
              <div style={{ marginTop: 8, opacity: 0.85, fontSize: 11, whiteSpace: "pre-wrap" }}>
                {scanBusy ? "Scanningâ€¦" : scanSummary ? "Scanned" : "Auto-scan pending"}
              </div>
              <div style={{ opacity: 0.85, fontSize: 11, whiteSpace: "pre-wrap" }}>
                Repo files indexed: {Array.isArray(fileIndex) ? fileIndex.length : 0}
              </div>
              <div style={{ opacity: 0.85, fontSize: 11, whiteSpace: "pre-wrap" }}>
                Generated files in memory: {generatedFiles && typeof generatedFiles === "object" ? Object.keys(generatedFiles).length : 0}
              </div>
              </div>
            </div>
          ) : null}

          {agentMenuOpen ? (
            <div
              style={{
                position: "fixed",
                left: agentMenuPos.x,
                top: agentMenuPos.y,
                width: 270,
                padding: 10,
                borderRadius: 14,
                background: "rgba(15, 23, 42, 0.55)",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                color: "rgba(255,255,255,0.92)",
                fontSize: 12,
                maxHeight: "min(70vh, 360px)",
                overflow: "auto",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 900 }}>Agent</div>
                <div style={{ flex: 1 }} />
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                  <input type="checkbox" checked={agentMode} onChange={(e) => setAgentMode(e.target.checked)} />
                  Enabled
                </label>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <select
                  value={agentTask}
                  onChange={(e) => setAgentTask(e.target.value)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.92)",
                    fontSize: 11,
                  }}
                  title="Task to run after applying patch"
                >
                  <option value="frontend_build">frontend build</option>
                  <option value="frontend_test">frontend test</option>
                  <option value="frontend_lint">frontend lint</option>
                </select>

                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                  <input type="checkbox" checked={agentAutoApply} onChange={(e) => setAgentAutoApply(e.target.checked)} />
                  Auto-apply
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, opacity: 0.9 }}>
                  <input type="checkbox" checked={agentAutoFixOnce} onChange={(e) => setAgentAutoFixOnce(e.target.checked)} />
                  Auto-fix
                </label>

                <select
                  value={String(agentMaxFixIters)}
                  onChange={(e) => setAgentMaxFixIters(Math.max(1, Math.min(5, Number(e.target.value || 2))))}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.92)",
                    fontSize: 11,
                  }}
                  title="Max fix iterations"
                  disabled={!agentAutoFixOnce}
                >
                  <option value="1">x1</option>
                  <option value="2">x2</option>
                  <option value="3">x3</option>
                  <option value="4">x4</option>
                  <option value="5">x5</option>
                </select>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="export-btn"
                    type="button"
                    onClick={() => runAgentFixTask({ task: agentTask, maxIters: agentMaxFixIters })}
                    disabled={aiBusy || patchBusy || aiAttachBusy}
                    title="Run task then auto-fix until it passes (bounded retries)"
                  >
                    Fix
                  </button>
                  <button className="export-btn" type="button" onClick={stopAgentTask} disabled={!agentProcId}>
                    Stop
                  </button>
                  <button
                    className="export-btn"
                    type="button"
                    onClick={() => {
                      setAgentTask("frontend_build");
                      setTimeout(() => runAgentFixTask({ task: "frontend_build", maxIters: agentMaxFixIters }), 0);
                    }}
                    disabled={aiBusy || patchBusy || aiAttachBusy}
                    title="Fix frontend build"
                  >
                    Fix build
                  </button>
                  <button
                    className="export-btn"
                    type="button"
                    onClick={() => {
                      setAgentTask("frontend_test");
                      setTimeout(() => runAgentFixTask({ task: "frontend_test", maxIters: agentMaxFixIters }), 0);
                    }}
                    disabled={aiBusy || patchBusy || aiAttachBusy}
                    title="Fix frontend tests"
                  >
                    Fix tests
                  </button>
                  <button className="export-btn" type="button" onClick={runAgent} disabled={aiBusy || patchBusy || aiAttachBusy} title="Run agent">
                    Run
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {leftMenuOpen ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 25541 }} onMouseDown={() => setLeftMenuOpen(null)}>
          <div
            style={{
              position: "fixed",
              left: leftMenuPos.x,
              top: leftMenuPos.y,
              width: 220,
              padding: 10,
              borderRadius: 14,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              color: "rgba(255,255,255,0.92)",
              fontSize: 12,
              maxHeight: "min(70vh, 320px)",
              overflow: "auto",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, marginBottom: 8 }}>{leftMenuOpen}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {leftMenuOpen === "File" ? (
                <>
                  <button type="button" className="export-btn z-menuitem" onClick={openNewFile}>
                    <span>New Fileâ€¦</span>
                    <span className="z-menuitem-kbd">Alt+N</span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={openNewFolder}>
                    <span>New Folderâ€¦</span>
                    <span className="z-menuitem-kbd">Alt+Shift+N</span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); setGotoOpen(true); }}>
                    <span>Open Fileâ€¦</span>
                    <span className="z-menuitem-kbd">Ctrl+P</span>
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    onClick={() => {
                      setLeftMenuOpen(null);
                      setAutoSave((v) => !v);
                      setTimeout(() => setStatus(""), 800);
                    }}
                  >
                    <span>Autosave: {autoSave ? "On" : "Off"}</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    onClick={() => { setLeftMenuOpen(null); save(); }}
                    disabled={!hasActiveEditor && false}
                    title={!hasActiveEditor ? "Opens Save As if no file is selected" : ""}
                  >
                    <span>Save</span>
                    <span className="z-menuitem-kbd">Ctrl+S</span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); openSaveAs(); }}>
                    <span>Save Asâ€¦</span>
                    <span className="z-menuitem-kbd">Ctrl+Shift+S</span>
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    disabled={!hasActiveEditor}
                    onClick={() => { const k = tabKey(selectedSource, selectedPath); setLeftMenuOpen(null); closeTab(k); }}
                  >
                    <span>Close Editor</span>
                    <span className="z-menuitem-kbd">Ctrl+W</span>
                  </button>
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    disabled={!hasActiveEditor || !canCloseOthersEditors}
                    onClick={() => { const k = tabKey(selectedSource, selectedPath); setLeftMenuOpen(null); closeOtherTabs(k); }}
                  >
                    <span>Close Other Editors</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); closeAllTabs(); }}>
                    <span>Close All Editors</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); setStatus("Preferences: not implemented yet"); setTimeout(() => setStatus(""), 900); }}>
                    <span>Preferences</span>
                    <span className="z-menuitem-kbd">Ctrl+,</span>
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Edit" ? (
                <>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Undo: use Ctrl+Z"); setTimeout(() => setStatus(""), 900); }}>
                    Undo
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Redo: use Ctrl+Y"); setTimeout(() => setStatus(""), 900); }}>
                    Redo
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Cut/Copy/Paste: use Ctrl+X/C/V inside editor"); setTimeout(() => setStatus(""), 1100); }}>
                    Cut / Copy / Paste
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Find: use Ctrl+F"); setTimeout(() => setStatus(""), 900); }}>
                    Find
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Replace: use Ctrl+H"); setTimeout(() => setStatus(""), 900); }}>
                    Replace
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setFindOpen(true); }}>
                    Find in Filesâ€¦
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Selection" ? (
                <>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Use Ctrl+A"); setTimeout(() => setStatus(""), 900); }}>
                    Select All
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Multi-cursor not implemented yet"); setTimeout(() => setStatus(""), 900); }}>
                    Add caret above/below
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Expand selection not implemented yet"); setTimeout(() => setStatus(""), 900); }}>
                    Expand Selection
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "View" ? (
                <>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left" }}
                    onClick={() => {
                      setLeftMenuOpen(null);
                      setUiTheme((v) => (v === "vscode" ? "glass" : "vscode"));
                    }}
                  >
                    Theme: {uiTheme === "vscode" ? "VS Code" : "Glass"}
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); toggleBottom("terminal"); }}>
                    Toggle Panel
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setTsPanel("problems"); }}>
                    Show Problems
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); openBottom("output"); }}>
                    Show Output
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setPaletteOpen(true); setPaletteQuery(""); }}>
                    Command Paletteâ€¦
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Go" ? (
                <>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); setGotoOpen(true); }}>
                    <span>Go to Fileâ€¦</span>
                    <span className="z-menuitem-kbd">Ctrl+P</span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); openSymbolPicker(); }}>
                    <span>Go to Symbolâ€¦</span>
                    <span className="z-menuitem-kbd">Ctrl+Shift+O</span>
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Go to line: use Ctrl+G in the editor"); setTimeout(() => setStatus(""), 1100); }}>
                    Go to lineâ€¦
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); setFindOpen(true); }}>
                    <span>Find in Filesâ€¦</span>
                    <span className="z-menuitem-kbd">Ctrl+Shift+F</span>
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Run" ? (
                <>
                  <button type="button" className="export-btn z-menuitem" disabled={true} onClick={() => { setLeftMenuOpen(null); setStatus("Debugging not implemented yet"); setTimeout(() => setStatus(""), 900); }}>
                    <span>Start Debugging</span>
                    <span className="z-menuitem-kbd">F5</span>
                  </button>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    disabled={!canRunTerminalTasks}
                    onClick={() => {
                      setLeftMenuOpen(null);
                      if (!shellId) return setStatus("Start terminal first");
                      sendShellLine("npm run build");
                    }}
                  >
                    <span>Run Build Task</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button
                    type="button"
                    className="export-btn z-menuitem"
                    disabled={!canRunTerminalTasks}
                    onClick={() => {
                      setLeftMenuOpen(null);
                      if (!shellId) return setStatus("Start terminal first");
                      sendShellLine("npm test");
                    }}
                  >
                    <span>Run Test Task</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); runAgent(); }}>
                    <span>Run Agent</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Terminal" ? (
                <>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); startShell(); }}>
                    <span>New Terminal</span>
                    <span className="z-menuitem-kbd">Ctrl+`</span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" disabled={!shellId} onClick={() => { setLeftMenuOpen(null); stopShell(); }}>
                    <span>Kill Terminal</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); openBottom("terminal"); }}>
                    <span>Show Terminal</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setShellLog(""); setStatus("Terminal cleared"); setTimeout(() => setStatus(""), 800); }}>
                    Clear
                  </button>
                  <button type="button" className="export-btn z-menuitem" onClick={() => { setLeftMenuOpen(null); openBottom("ports"); }}>
                    <span>Portsâ€¦</span>
                    <span className="z-menuitem-kbd"></span>
                  </button>
                </>
              ) : null}

              {leftMenuOpen === "Help" ? (
                <>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); window.open("/docs", "_blank"); }}>
                    View Docs
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); window.open("/TermsOfService", "_blank"); }}>
                    Terms
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("Keyboard shortcuts: Ctrl+Shift+P (palette), Ctrl+Shift+F (find), Ctrl+` (panel)"); setTimeout(() => setStatus(""), 1400); }}>
                    Keyboard Shortcuts
                  </button>
                  <button type="button" className="export-btn" style={{ textAlign: "left" }} onClick={() => { setLeftMenuOpen(null); setStatus("About: Zolaid IDE â€” editor and AI assistant"); setTimeout(() => setStatus(""), 900); }}>
                    About
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {newItemOpen ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 25560, background: "rgba(0,0,0,0.18)" }} onMouseDown={() => setNewItemOpen(false)}>
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: 120,
              transform: "translateX(-50%)",
              width: 520,
              maxWidth: "96vw",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              color: "rgba(255,255,255,0.92)",
              padding: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 12 }}>{newItemKind === "folder" ? "New Folder" : "New File"}</div>
              <button className="export-btn" type="button" onClick={() => setNewItemOpen(false)}>
                Close
              </button>
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: newItemKind === "folder" ? "1fr 1fr" : "1fr 1fr 100px", gap: 8 }}>
              <input
                value={newItemFolder}
                onChange={(e) => setNewItemFolder(e.target.value)}
                placeholder="Folder (optional) e.g. frontend/src"
                style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.92)" }}
              />
              <input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={newItemKind === "folder" ? "Folder name" : "File name"}
                style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.92)" }}
              />
              {newItemKind === "file" ? (
                <select
                  value={newItemExt}
                  onChange={(e) => setNewItemExt(e.target.value)}
                  style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.92)" }}
                >
                  <option value="js">.js</option>
                  <option value="html">.html</option>
                  <option value="css">.css</option>
                  <option value="json">.json</option>
                  <option value="md">.md</option>
                </select>
              ) : null}
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="export-btn" type="button" onClick={() => setNewItemOpen(false)}>
                Cancel
              </button>
              <button className="export-btn" type="button" onClick={createNewItem} disabled={!String(newItemName || "").trim()}>
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {ctxMenu ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 30000,
          }}
        >
          {/*
            Guard: avoid direct window usage during render in edge environments.
            (Still a client-only component, but this prevents hard crashes.)
          */}
          <div
            style={{
              position: "fixed",
              left: Math.max(
                8,
                Math.min(ctxMenu.x, ((typeof window !== "undefined" ? window.innerWidth : 1024) || 1024) - 260)
              ),
              top: Math.max(
                8,
                Math.min(ctxMenu.y, ((typeof window !== "undefined" ? window.innerHeight : 768) || 768) - 180)
              ),
              width: 240,
              padding: 10,
              borderRadius: 14,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              color: "rgba(255,255,255,0.92)",
              fontSize: 12,
              maxHeight: "min(70vh, 520px)",
              overflow: "auto",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, marginBottom: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {ctxMenu.path}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Open/Toggle */}
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={async () => {
                  const p = ctxMenu.path;
                  const src = ctxMenu.source;
                  const isDir = Boolean(ctxMenu.isDir);
                  setCtxMenu(null);
                  if (isDir) {
                    if (src === "generated") toggleGeneratedDir(p);
                    else toggleDir(p);
                  } else {
                    if (src === "generated") await openGeneratedFile(p);
                    else await openFile(p);
                  }
                }}
              >
                {ctxMenu.isDir ? "Toggle" : "Open"}
              </button>

              {/* Folder actions */}
              {ctxMenu.isDir ? (
                <>
                  {ctxMenu.source === "generated" ? (
                    <>
                      <button
                        type="button"
                        className="export-btn"
                        style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                        onClick={() => {
                          const p = ctxMenu.path;
                          setCtxMenu(null);
                          openNewFileInFolder(p);
                        }}
                      >
                        New Fileâ€¦
                      </button>
                      <button
                        type="button"
                        className="export-btn"
                        style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                        onClick={() => {
                          const p = ctxMenu.path;
                          setCtxMenu(null);
                          openNewFolderInFolder(p);
                        }}
                      >
                        New Folderâ€¦
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      setCtxMenu(null);
                      clipboardWrite(p);
                    }}
                  >
                    Copy Relative Path
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      const prefix = ctxMenu.source === "generated" ? ".zolaid_generated/" : "";
                      setCtxMenu(null);
                      clipboardWrite(prefix ? `${prefix}${p}` : p);
                    }}
                  >
                    Copy Path (project)
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      setCtxMenu(null);
                      if (ctxMenu.source === "generated") collapseAllGenerated();
                    }}
                    disabled={ctxMenu.source !== "generated"}
                    title={ctxMenu.source === "generated" ? "Collapse all generated folders" : "Not available for repo here"}
                  >
                    Collapse All
                  </button>
                </>
              ) : (
                <>
                  {/* File actions */}
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={async () => {
                      const t = ctxMenu;
                      setCtxMenu(null);
                      await duplicatePath({ source: t.source, path: t.path });
                    }}
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      const base = String(p || "").replace(/\\/g, "/").split("/").pop() || p;
                      setCtxMenu(null);
                      clipboardWrite(base);
                    }}
                  >
                    Copy File Name
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      setCtxMenu(null);
                      clipboardWrite(p);
                    }}
                  >
                    Copy Relative Path
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      const prefix = ctxMenu.source === "generated" ? ".zolaid_generated/" : "";
                      setCtxMenu(null);
                      clipboardWrite(prefix ? `${prefix}${p}` : p);
                    }}
                  >
                    Copy Path (project)
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const p = ctxMenu.path;
                      const src = ctxMenu.source;
                      let body = "";
                      if (src === "generated") {
                        const map = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
                        body = String(map[p] ?? "");
                      } else {
                        // best-effort: use current editor content if it's this file, otherwise skip
                        body = selectedSource === "repo" && selectedPath === p ? String(content || "") : "";
                      }
                      setCtxMenu(null);
                      downloadTextFile(String(p || "").split("/").pop() || "file.txt", body);
                    }}
                    title="Downloads current content (repo downloads only work if the file is open)"
                  >
                    Download
                  </button>

                  {/* Tab actions */}
                  <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const k = tabKey(ctxMenu.source, ctxMenu.path);
                      setCtxMenu(null);
                      closeTab(k);
                    }}
                  >
                    Close Tab
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      const k = tabKey(ctxMenu.source, ctxMenu.path);
                      setCtxMenu(null);
                      closeOtherTabs(k);
                    }}
                  >
                    Close Others
                  </button>
                  <button
                    type="button"
                    className="export-btn"
                    style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                    onClick={() => {
                      setCtxMenu(null);
                      closeAllTabs();
                    }}
                  >
                    Close All
                  </button>
                </>
              )}

              <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={async () => {
                  const t = ctxMenu;
                  setCtxMenu(null);
                  await promptRename({ source: t.source, path: t.path, isDir: Boolean(t.isDir) });
                }}
              >
                Renameâ€¦
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(239,68,68,0.18)", color: "inherit", borderColor: "rgba(239,68,68,0.35)" }}
                onClick={async () => {
                  const t = ctxMenu;
                  setCtxMenu(null);
                  await confirmDelete({ source: t.source, path: t.path, isDir: Boolean(t.isDir) });
                }}
              >
                Deleteâ€¦
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {tabCtxMenu ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 30002 }} onMouseDown={() => setTabCtxMenu(null)}>
          <div
            style={{
              position: "fixed",
              left: Math.max(8, Math.min(tabCtxMenu.x, ((typeof window !== "undefined" ? window.innerWidth : 1024) || 1024) - 260)),
              top: Math.max(8, Math.min(tabCtxMenu.y, ((typeof window !== "undefined" ? window.innerHeight : 768) || 768) - 220)),
              width: 240,
              padding: 10,
              borderRadius: 14,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              color: "rgba(255,255,255,0.92)",
              fontSize: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, marginBottom: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {tabCtxMenu.path || "(tab)"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  const k = String(tabCtxMenu.key || "");
                  setTabCtxMenu(null);
                  closeTab(k);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  const k = String(tabCtxMenu.key || "");
                  setTabCtxMenu(null);
                  closeOtherTabs(k);
                }}
              >
                Close Others
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setTabCtxMenu(null);
                  closeAllTabs();
                }}
              >
                Close All
              </button>
              <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "4px 0" }} />
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  const p = String(tabCtxMenu.path || "");
                  const src = String(tabCtxMenu.source || "repo");
                  const prefix = src === "generated" ? ".zolaid_generated/" : "";
                  setTabCtxMenu(null);
                  clipboardWrite(prefix ? `${prefix}${p}` : p);
                }}
              >
                Copy Path
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {explorerMoreOpen ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 30001 }} onMouseDown={() => setExplorerMoreOpen(null)}>
          <div
            style={{
              position: "fixed",
              left: Math.max(
                8,
                Math.min(explorerMoreOpen.x, ((typeof window !== "undefined" ? window.innerWidth : 1024) || 1024) - 240)
              ),
              top: Math.max(
                8,
                Math.min(explorerMoreOpen.y, ((typeof window !== "undefined" ? window.innerHeight : 768) || 768) - 220)
              ),
              width: 220,
              padding: 10,
              borderRadius: 14,
              background: "rgba(15, 23, 42, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              color: "rgba(255,255,255,0.92)",
              fontSize: 12,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Explorer</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setExplorerMoreOpen(null);
                  openNewFile();
                }}
              >
                New Fileâ€¦
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setExplorerMoreOpen(null);
                  openNewFolder();
                }}
              >
                New Folderâ€¦
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setExplorerMoreOpen(null);
                  refresh("");
                }}
              >
                Refresh
              </button>
              <button
                type="button"
                className="export-btn"
                style={{ textAlign: "left", background: "rgba(255,255,255,0.08)", color: "inherit" }}
                onClick={() => {
                  setExplorerMoreOpen(null);
                  collapseAllGenerated();
                }}
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {/* Activity Bar (VS Code-like) */}
      <div className="z-activitybar" aria-label="Activity Bar" style={{ gridRow: 2 }}>
        {[
          {
            id: "explorer",
            label: "Explorer",
            icon: (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 2h12v12H2V2zm1 1v10h10V3H3zm1 1h8v1H4V4zm0 2h8v6H4V6z" />
              </svg>
            ),
          },
          {
            id: "search",
            label: "Search",
            icon: (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            ),
          },
          {
            id: "scm",
            label: "Source Control",
            icon: (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.13 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.11.16 1.93.08 2.13.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
              </svg>
            ),
          },
          {
            id: "run",
            label: "Run",
            icon: (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M6.271 5.055a.5.5 0 0 1 .554 0l3.5 2.5a.5.5 0 0 1 0 .89l-3.5 2.5a.5.5 0 0 1-.554 0l-3.5-2.5a.5.5 0 0 1 0-.89l3.5-2.5z" />
              </svg>
            ),
          },
          {
            id: "extensions",
            label: "Extensions",
            icon: (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2 .666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2h-11z" />
              </svg>
            ),
          },
        ].map((it) => (
          <button
            key={it.id}
            type="button"
            className={`z-activitybar-btn ${leftView === it.id ? "z-active" : ""}`}
            title={it.label}
            aria-label={it.label}
            onClick={() => setLeftView(it.id)}
          >
            <span aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16 }}>
              {it.icon}
            </span>
          </button>
        ))}
      </div>

      {/* Explorer/Search/etc panel */}
      <div style={{ border: "1px solid var(--z-border)", background: "var(--z-panel-bg)", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, gridRow: 2 }}>
        <div style={{ padding: 10, borderBottom: "1px solid var(--z-border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontWeight: 900, fontSize: 11, letterSpacing: 0.6, opacity: 0.9 }}>
              {leftView === "explorer"
                ? "EXPLORER"
                : leftView === "search"
                  ? "SEARCH"
                  : leftView === "scm"
                    ? "SOURCE CONTROL"
                    : leftView === "run"
                      ? "RUN"
                      : "EXTENSIONS"}
            </div>
            <button
              type="button"
              className="export-btn export-btn-icon"
              title="More actionsâ€¦"
              aria-label="More actionsâ€¦"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExplorerMoreOpen({ x: e.clientX, y: e.clientY });
              }}
            >
              â‹¯
            </button>
          </div>
          {leftView === "explorer" ? (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 900, opacity: 0.95 }}>GENERATED</div>
          ) : null}
        </div>

        <div
          style={{
            padding: "0 10px 10px 10px",
            flex: 1,
            minHeight: 0,
            overflowY: leftView === "explorer" ? "hidden" : "auto",
            overflowX: leftView === "explorer" ? "hidden" : "auto",
            overscrollBehavior: "contain",
            scrollbarGutter: "stable",
          }}
        >
          {leftView === "explorer" ? (
            <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ flex: 1, minHeight: 0, overflow: "auto", overflowX: "auto", paddingTop: 4 }}>
                {generatedTree ? (
                  <GeneratedRow node={generatedTree} depth={0} />
                ) : (
                  <div style={{ fontSize: 12, color: "var(--z-muted)", padding: 10 }}>No generated files yet.</div>
                )}
              </div>

              {/* Outline */}
              <div className="z-explorer-section" style={{ marginTop: 6, borderRadius: 8, overflow: "hidden" }}>
                <div className="z-explorer-section-header">
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button
                      type="button"
                      className="export-btn export-btn-icon"
                      style={{ width: 22, height: 22 }}
                      title={outlineCollapsed ? "Expand" : "Collapse"}
                      onClick={() => setOutlineCollapsed((v) => !v)}
                    >
                      {outlineCollapsed ? "â–¸" : "â–¾"}
                    </button>
                    <div>OUTLINE</div>
                  </div>
                  <button
                    type="button"
                    className="export-btn export-btn-icon"
                    style={{ width: 22, height: 22 }}
                    title="Refresh outline"
                    onClick={() => fetchOutlineSymbols()}
                  >
                    â†»
                  </button>
                </div>
                {!outlineCollapsed ? (
                  <div className="z-explorer-section-body" style={{ paddingTop: 0 }}>
                    <div style={{ maxHeight: 100, overflow: "auto" }}>
                      {outlineBusy ? (
                        <div style={{ padding: "4px 0", color: "var(--z-muted)", fontSize: 11 }}>Loadingâ€¦</div>
                      ) : outlineErr ? (
                        <div style={{ padding: "4px 0", color: "var(--z-muted)", fontSize: 11 }}>{outlineErr}</div>
                      ) : !selectedPath ? (
                        <div style={{ padding: "4px 0", color: "var(--z-muted)", fontSize: 11 }}>Open a file to see its outline.</div>
                      ) : !outlineSymbols.length ? (
                        <div style={{ padding: "4px 0", color: "var(--z-muted)", fontSize: 11 }}>No symbols.</div>
                      ) : (
                        outlineSymbols.slice(0, 120).map((s, idx) => (
                          <div
                            key={`${s.container || ""}:${s.name || ""}:${idx}`}
                            style={{
                              padding: "2px 6px",
                              borderRadius: 8,
                              cursor: "pointer",
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                            onClick={() => {
                              setRevealRequest({
                                line: Number(s?.line || 1),
                                column: Number(s?.character || 1),
                                nonce: Date.now(),
                              });
                            }}
                            title={`${String(s?.container || "") ? `${s.container} â€¢ ` : ""}${String(s?.name || "")}`}
                          >
                            <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                              {String(s?.container || "") ? (
                                <span style={{ opacity: 0.75 }}>{String(s.container)} â€¢ </span>
                              ) : null}
                              {String(s?.name || "")}
                            </div>
                            <div style={{ opacity: 0.6, fontSize: 10 }}>L{Number(s?.line || 1)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Timeline */}
              <div className="z-explorer-section" style={{ marginTop: 6, borderRadius: 8, overflow: "hidden" }}>
                <div className="z-explorer-section-header">
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button
                      type="button"
                      className="export-btn export-btn-icon"
                      style={{ width: 22, height: 22 }}
                      title={timelineCollapsed ? "Expand" : "Collapse"}
                      onClick={() => setTimelineCollapsed((v) => !v)}
                    >
                      {timelineCollapsed ? "â–¸" : "â–¾"}
                    </button>
                    <div>TIMELINE</div>
                  </div>
                  <button
                    type="button"
                    className="export-btn export-btn-icon"
                    style={{ width: 22, height: 22 }}
                    title="Clear timeline"
                    onClick={() => setTimelineEvents([])}
                  >
                    âœ•
                  </button>
                </div>
                {!timelineCollapsed ? (
                  <div className="z-explorer-section-body" style={{ paddingTop: 0 }}>
                    <div style={{ maxHeight: 100, overflow: "auto" }}>
                      {!timelineEvents.length ? (
                        <div style={{ padding: "4px 0", color: "var(--z-muted)", fontSize: 11 }}>No events yet.</div>
                      ) : (
                        timelineEvents
                          .slice(-60)
                          .reverse()
                          .map((e) => (
                            <div
                              key={e.id}
                              style={{ padding: "2px 6px", borderRadius: 6, cursor: e.path ? "pointer" : "default", fontSize: 11 }}
                              onClick={() => {
                                if (!e.path) return;
                                if (e.source === "generated") openGeneratedFile(e.path);
                                else if (e.source === "repo") openFile(e.path);
                                if (e.line) setRevealRequest({ line: Number(e.line), column: Number(e.column || 1), nonce: Date.now() });
                              }}
                              title={e.text}
                            >
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <div style={{ opacity: 0.7, fontSize: 10, flex: "0 0 auto" }}>
                                  {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {e.text}
                                </div>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : leftView === "search" ? (
            <div style={{ padding: 10, fontSize: 12, color: "var(--z-muted)" }}>
              <div style={{ fontWeight: 900, color: "var(--z-text)", marginBottom: 6 }}>Find in files</div>
              <div style={{ marginBottom: 10 }}>Use <code>Ctrl+Shift+F</code> or open the Command Palette.</div>
              <button className="export-btn" type="button" onClick={() => setFindOpen(true)}>
                Open Find in filesâ€¦
              </button>
            </div>
          ) : leftView === "run" ? (
            <div style={{ padding: 10, fontSize: 12, color: "var(--z-muted)" }}>
              <div style={{ fontWeight: 900, color: "var(--z-text)", marginBottom: 6 }}>Run</div>
              <div style={{ marginBottom: 10 }}>Open Terminal and run commands like <code>npm run build</code>.</div>
              <button className="export-btn" type="button" onClick={() => setTerminalOpen(true)}>
                Show Terminal
              </button>
            </div>
          ) : (
            <div style={{ padding: 10, fontSize: 12, color: "var(--z-muted)" }}>
              <div style={{ fontWeight: 900, color: "var(--z-text)", marginBottom: 6 }}>Coming soon</div>
              <div>We can wire this to real Git/SCM and Extensions later.</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ border: "1px solid var(--z-border)", background: "var(--z-panel-bg)", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", gridRow: 2 }}>
        {/* Tabs (VS Code-like) */}
        {openTabs.length > 0 ? (
          <div className="z-tabstrip">
            {openTabs.map((t) => {
              const key = String(t.key || "");
              const src = t.source === "generated" ? "generated" : "repo";
              const p = String(t.path || "");
              const active = selectedSource === src && selectedPath === p;
              const buf = openBuffersRef.current.get(key);
              const isDirty = Boolean(buf?.dirty);
              return (
                <div
                  key={key}
                  title={`${src}:${p}`}
                  className={`z-tab ${active ? "z-active" : ""}`}
                  onContextMenu={(e) => openTabContextMenu(e, { key, source: src, path: p })}
                  onMouseDown={(e) => {
                    // prevent dragging selection in surrounding UI
                    e.preventDefault();
                  }}
                  onClick={() => {
                    if (src === "generated") openGeneratedFile(p);
                    else openFile(p);
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{tabLabel(src, p)}</span>
                  {isDirty ? <span title="Unsaved changes">â—</span> : null}
                  <button
                    type="button"
                    className="export-btn export-btn-icon z-tab-close"
                    style={{ width: 24, height: 24 }}
                    title="Close"
                    aria-label="Close"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      closeTab(key);
                    }}
                  >
                    Ã—
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Breadcrumbs (VS Code-like) */}
        {selectedPath ? (
          <div
            className="z-breadcrumbs"
            title={`${selectedSource}:${selectedPath}`}
            onDoubleClick={() => {
              const src = selectedSource === "generated" ? "generated" : "repo";
              const prefix = src === "generated" ? ".zolaid_generated/" : "";
              clipboardWrite(prefix ? `${prefix}${selectedPath}` : selectedPath);
            }}
          >
            <span style={{ opacity: 0.7 }}>{selectedSource}</span>
            <span className="z-breadcrumb-sep">â€º</span>
            {breadcrumbs.map((seg, idx) => (
              <React.Fragment key={`${seg}-${idx}`}>
                <span
                  className="z-breadcrumb"
                  onClick={() => {
                    // Click just copies that segment (cheap + safe). Double-click copies full path.
                    clipboardWrite(seg);
                  }}
                  title={seg}
                >
                  {seg}
                </span>
                {idx < breadcrumbs.length - 1 ? <span className="z-breadcrumb-sep">â€º</span> : null}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <div style={{ padding: 10, borderBottom: "1px solid var(--z-border)", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
            <div style={{ fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedPath || "Open a fileâ€¦"}
            </div>
            <div style={{ opacity: 0.7 }}>{dirty ? "Unsaved changes" : ""}</div>
          </div>
          <button
            className="export-btn"
            type="button"
            onClick={() => refresh("")}
            title="Refresh files"
            aria-label="Refresh files"
            style={{ padding: "6px 10px" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className="export-btn"
            type="button"
            onClick={save}
            disabled={!dirty}
            title="Save (Ctrl+S)"
            aria-label="Save"
            style={{ padding: "6px 10px" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M17 21v-8H7v8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path d="M7 3v4h8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className="export-btn"
            type="button"
            onClick={openSaveAs}
            title="Save Asâ€¦ (Ctrl+Shift+S)"
            aria-label="Save As"
            style={{ padding: "6px 10px" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 5v14m-7-7h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 3h10a2 2 0 0 1 2 2v4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div style={{ fontSize: 12, color: "var(--z-muted)" }}>{status}</div>
        </div>

        {quickFixOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 22000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setQuickFixOpen(false)}
          >
            <div
              style={{ width: 640, maxWidth: "96vw", background: "#fff", borderRadius: 14, padding: 14 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ fontWeight: 900 }}>Quick Fix</div>
                <button className="export-btn" type="button" onClick={() => setQuickFixOpen(false)}>
                  Close
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                {selectedPath ? (
                  <span>
                    {selectedPath} â€” TS{Number(quickFixDiag?.code || 0)} @ {quickFixDiag?.start?.line}:{quickFixDiag?.start?.character}
                  </span>
                ) : null}
              </div>
              <div style={{ marginTop: 12, maxHeight: 340, overflow: "auto" }}>
                {quickFixBusy ? (
                  <div style={{ fontSize: 12, opacity: 0.75, padding: "10px 0" }}>Loading fixesâ€¦</div>
                ) : Array.isArray(quickFixFixes) && quickFixFixes.length ? (
                  quickFixFixes.slice(0, 20).map((f, idx) => (
                    <button
                      key={idx}
                      className="export-btn"
                      type="button"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 10px",
                        borderRadius: 12,
                        marginBottom: 8,
                      }}
                      onClick={async () => {
                        setQuickFixOpen(false);
                        await applyCodeFix(f);
                      }}
                      title="Preview patch"
                    >
                      {String(f?.description || `Quick fix ${idx + 1}`)}
                    </button>
                  ))
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.75, padding: "10px 0" }}>No fixes available.</div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {renameOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 22500,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              setRenameOpen(false);
              setRenameCtx(null);
              setRenameValue("");
            }}
          >
            <div
              style={{
                width: 560,
                maxWidth: "96vw",
                background: "#fff",
                borderRadius: 14,
                padding: 14,
                marginTop: 60,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ fontWeight: 900 }}>Rename</div>
                <button
                  className="export-btn"
                  type="button"
                  onClick={() => {
                    setRenameOpen(false);
                    setRenameCtx(null);
                    setRenameValue("");
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                {renameCtx?.selectedPath ? (
                  <span>
                    {renameCtx.selectedPath} â€” rename <strong>{String(renameCtx.oldName || "") || "(symbol)"}</strong>
                  </span>
                ) : null}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setRenameOpen(false);
                      setRenameCtx(null);
                      setRenameValue("");
                      return;
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      confirmRename();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid rgba(0,0,0,0.15)",
                    fontSize: 13,
                  }}
                  placeholder="New name"
                />
                <button className="export-btn" type="button" onClick={confirmRename} disabled={!String(renameValue || "").trim()}>
                  Rename
                </button>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>Enter to rename â€¢ Esc to cancel</div>
            </div>
          </div>
        ) : null}

        {saveAsOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 20000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => setSaveAsOpen(false)}
          >
            <div
              style={{ width: 560, maxWidth: "96vw", background: "#fff", borderRadius: 14, padding: 14 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ fontWeight: 900 }}>Save Asâ€¦</div>
                <button className="export-btn" type="button" onClick={() => setSaveAsOpen(false)}>
                  Close
                </button>
              </div>

              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, alignItems: "center" }}>
                <div style={{ fontSize: 12, fontWeight: 900 }}>Destination</div>
                <select
                  value={saveAsTarget}
                  onChange={(e) => setSaveAsTarget(e.target.value)}
                  style={{ padding: 10, borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)" }}
                >
                  <option value="generated">Generated files</option>
                  <option value="repo">Repo (writes to disk)</option>
                </select>

                <div style={{ fontSize: 12, fontWeight: 900 }}>Folder</div>
                <input
                  value={saveAsFolder}
                  onChange={(e) => setSaveAsFolder(e.target.value)}
                  placeholder="e.g. frontend/src"
                  style={{ padding: 10, borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)" }}
                />

                <div style={{ fontSize: 12, fontWeight: 900 }}>File name</div>
                <input
                  value={saveAsName}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  placeholder="e.g. chatbot"
                  style={{ padding: 10, borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)" }}
                />

                <div style={{ fontSize: 12, fontWeight: 900 }}>Type</div>
                <select
                  value={saveAsExt}
                  onChange={(e) => setSaveAsExt(e.target.value)}
                  style={{ padding: 10, borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)" }}
                >
                  <option value="js">.js</option>
                  <option value="html">.html</option>
                  <option value="css">.css</option>
                  <option value="json">.json</option>
                  <option value="ts">.ts</option>
                  <option value="tsx">.tsx</option>
                  <option value="jsx">.jsx</option>
                  <option value="md">.md</option>
                  <option value="yml">.yml</option>
                  <option value="yaml">.yaml</option>
                  <option value="txt">.txt</option>
                </select>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                Path:{" "}
                <code>
                  {normalizePath(
                    normalizeFolder(saveAsFolder)
                      ? `${normalizeFolder(saveAsFolder)}/${String(saveAsName || "").trim()}.${String(saveAsExt || "").trim().replace(/^\./, "")}`
                      : `${String(saveAsName || "").trim()}.${String(saveAsExt || "").trim().replace(/^\./, "")}`
                  )}
                </code>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="export-btn" type="button" onClick={() => setSaveAsOpen(false)}>
                  Cancel
                </button>
                <button
                  className="export-btn"
                  type="button"
                  onClick={doSaveAs}
                  disabled={!String(saveAsName || "").trim() || !String(saveAsExt || "").trim()}
                  title="Save"
                  aria-label="Save"
                  style={{ padding: "6px 10px" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path d="M7 3v4h8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* Editor: Monaco */}
          <MonacoIframe
            value={content}
            language={language}
            onChange={(v) => {
              setContent(v);
              setDirty(true);
              // Live preview: debounce .html/.css/.js edits into Zolaid Computer preview (generated + repo).
              if (!selectedPath || !webPreviewSlotForPath(selectedPath)) return;
              if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
              livePreviewDebounceRef.current = setTimeout(() => {
                livePreviewDebounceRef.current = null;
                if (selectedSource === "generated" && typeof onUpdateGeneratedFile === "function") {
                  onUpdateGeneratedFile(selectedPath, v);
                } else if (selectedSource === "repo" && typeof onSyncWebPreview === "function") {
                  onSyncWebPreview(selectedPath, v);
                }
              }, 350);
            }}
            onCommand={handleMonacoCommand}
            revealRequest={revealRequest}
            markers={monacoMarkers}
          />
        </div>

        {/* Bottom panel (VS Code-like): Problems / Output / Debug Console / Terminal / Ports */}
        {terminalOpen ? (
          <div className="z-bottompanel" style={{ flex: "0 0 auto" }}>
            <div className="z-bottomtabs">
              <button className={`z-bottomtab ${bottomTab === "problems" ? "z-active" : ""}`} type="button" onClick={() => setBottomTab("problems")}>
                Problems ({tsDiagnostics.length})
              </button>
              <button className={`z-bottomtab ${bottomTab === "output" ? "z-active" : ""}`} type="button" onClick={() => setBottomTab("output")}>
                Output
              </button>
              <button className={`z-bottomtab ${bottomTab === "debug" ? "z-active" : ""}`} type="button" onClick={() => setBottomTab("debug")}>
                Debug Console
              </button>
              <button className={`z-bottomtab ${bottomTab === "terminal" ? "z-active" : ""}`} type="button" onClick={() => setBottomTab("terminal")}>
                Terminal
              </button>
              <button className={`z-bottomtab ${bottomTab === "ports" ? "z-active" : ""}`} type="button" onClick={() => setBottomTab("ports")}>
                Ports ({ports.length})
              </button>
              <div style={{ flex: 1 }} />
              <button className="export-btn export-btn-icon" type="button" title="Close panel" aria-label="Close panel" onClick={() => setTerminalOpen(false)}>
                âœ•
              </button>
            </div>

            <div style={{ padding: 10, maxHeight: "clamp(180px, 26vh, 360px)", overflow: "auto" }}>
              {bottomTab === "problems" ? (
                <>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                    <button className="export-btn" type="button" onClick={() => setTsPanel("problems")} style={{ background: tsPanel === "problems" ? "rgba(59,130,246,0.12)" : "var(--z-btn-bg)" }}>
                      Problems ({tsDiagnostics.length})
                    </button>
                    <button className="export-btn" type="button" onClick={() => setTsPanel("references")} style={{ background: tsPanel === "references" ? "rgba(59,130,246,0.12)" : "var(--z-btn-bg)" }}>
                      References ({tsRefs.length})
                    </button>
                    <div style={{ flex: 1 }} />
                    <button
                      className="export-btn"
                      type="button"
                      onClick={() => {
                        setTsDiagnostics([]);
                        setTsRefs([]);
                        setMonacoMarkers([]);
                      }}
                      title="Clear"
                    >
                      Clear
                    </button>
                  </div>
                  {tsPanel === "problems" ? (
                    tsDiagnostics.length ? (
                      tsDiagnostics.map((d, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "6px 8px",
                            borderRadius: 10,
                            border: "1px solid var(--z-border)",
                            marginBottom: 6,
                            cursor: "pointer",
                            fontSize: 12,
                            background: "var(--z-panel-bg)",
                          }}
                          onClick={() => {
                            setRevealRequest({
                              line: Number(d.start?.line || 1),
                              column: Number(d.start?.character || 1),
                              nonce: Date.now(),
                            });
                          }}
                          title="Click to jump"
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                            <div style={{ fontWeight: 900 }}>
                              {String(d.category || "Error")} TS{d.code} @ {d.start?.line}:{d.start?.character}
                            </div>
                            <button
                              className="export-btn"
                              type="button"
                              style={{ padding: "2px 8px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuickFixPicker(d);
                              }}
                              title="Quick fix"
                            >
                              Quick fix
                            </button>
                          </div>
                          <div style={{ opacity: 0.8, whiteSpace: "pre-wrap" }}>{String(d.message || "")}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: 12, color: "var(--z-muted)", padding: "6px 0" }}>No problems.</div>
                    )
                  ) : tsRefs.length ? (
                    tsRefs.map((r, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "6px 8px",
                          borderRadius: 10,
                          border: "1px solid var(--z-border)",
                          marginBottom: 6,
                          cursor: "pointer",
                          fontSize: 12,
                          background: r.isDefinition ? "rgba(16,185,129,0.10)" : "var(--z-panel-bg)",
                        }}
                        onClick={async () => {
                          const p = String(r.path || "");
                          const ln = Number(r.start?.line || 1);
                          const col = Number(r.start?.character || 1);
                          if (!p) return;
                          if (p !== selectedPath) {
                            pendingRevealRef.current = { path: p, line: ln, column: col };
                            await openFile(p);
                            return;
                          }
                          setRevealRequest({ line: ln, column: col, nonce: Date.now() });
                        }}
                        title="Click to open"
                      >
                        <div style={{ fontWeight: 900 }}>
                          {r.isDefinition ? "Definition" : "Ref"} â€¢ {r.path} @ {r.start?.line}:{r.start?.character}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--z-muted)", padding: "6px 0" }}>No references.</div>
                  )}
                </>
              ) : bottomTab === "terminal" ? (
                <>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 11, opacity: 0.75 }}>CWD</div>
                    <select
                      value={shellCwd}
                      onChange={(e) => setShellCwd(String(e.target.value || ""))}
                      disabled={!!shellId}
                      style={{ padding: "6px 8px", borderRadius: 10, border: "1px solid var(--z-border)", fontSize: 11, background: "var(--z-panel-bg)", color: "var(--z-text)" }}
                      title="Working directory for the terminal session"
                    >
                      <option value="">repo root</option>
                      <option value=".zolaid_generated">generated files (.zolaid_generated)</option>
                    </select>
                    <button className="export-btn" type="button" onClick={startShell} disabled={!!shellId}>
                      {shellId ? "Running" : "Start"}
                    </button>
                    <button className="export-btn" type="button" onClick={stopShell} disabled={!shellId}>
                      Stop
                    </button>
                    {shellId ? <div style={{ fontSize: 11, opacity: 0.75 }}>id: {shellId}</div> : null}
                    <div style={{ flex: 1 }} />
                    <button className="export-btn" type="button" disabled={!shellId} onClick={() => sendShellLine("npm run build")} title="npm run build" aria-label="npm run build">
                      Build
                    </button>
                    <button className="export-btn" type="button" disabled={!shellId} onClick={() => sendShellLine("npm test")} title="npm test" aria-label="npm test">
                      Test
                    </button>
                    <button className="export-btn" type="button" disabled={!shellId} onClick={() => sendShellLine("npm start")} title="npm start" aria-label="npm start">
                      Start
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontFamily: 'monospace', color: '#4ec9b0', fontWeight: 700, fontSize: 13, marginRight: 2 }}>
                      {shellId ? (shellCwd === '' ? 'PS C:> ' : `PS ${shellCwd}> `) : ''}
                    </span>
                    <input
                      ref={shellInputRef}
                      value={shellInput}
                      onChange={(e) => setShellInput(e.target.value)}
                      onKeyDown={async (e) => {
                        if (!shellId) return;
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const line = shellInput;
                          setShellInput("");
                          setShellHistory((h) => [...h, line]);
                          setShellHistoryIdx(-1);
                          await sendShell(line + "\n");
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setShellHistoryIdx((idx) => {
                            const newIdx = idx < 0 ? shellHistory.length - 1 : Math.max(0, idx - 1);
                            setShellInput(shellHistory[newIdx] || "");
                            return newIdx;
                          });
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setShellHistoryIdx((idx) => {
                            const newIdx = idx < 0 ? -1 : Math.min(shellHistory.length - 1, idx + 1);
                            setShellInput(newIdx >= 0 ? shellHistory[newIdx] : "");
                            return newIdx;
                          });
                        } else if (e.ctrlKey && e.key.toLowerCase() === "c") {
                          e.preventDefault();
                          await sendShell("\x03");
                        }
                      }}
                      placeholder={shellId ? "Type a command and press Enterâ€¦" : "Click Start to open a terminalâ€¦"}
                      style={{ flex: 1, padding: 10, borderRadius: 10, border: "1px solid var(--z-border)", fontSize: 12, background: "var(--z-panel-bg)", color: "var(--z-text)", fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                      autoComplete="off"
                    />
                    <button className="export-btn" type="button" onClick={() => sendShell("\n")} disabled={!shellId} title="Send newline">
                      â†µ
                    </button>
                    <button className="export-btn" type="button" onClick={() => sendShell("\x03")} disabled={!shellId} title="Ctrl+C">
                      Ctrl+C
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      height: "clamp(160px, 18vh, 260px)",
                      overflow: "auto",
                      background: "#0b1020",
                      color: "#e5e7eb",
                      borderRadius: 12,
                      padding: "10px 0",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      scrollbarGutter: "stable",
                    }}
                    ref={shellLogRef}
                  >
                    {shellLog || "# Terminal outputâ€¦"}
                  </div>
                </>
              ) : bottomTab === "output" ? (
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontWeight: 900 }}>Output</div>
                    <div style={{ flex: 1 }} />
                    <button className="export-btn" type="button" onClick={() => { setBuildOut(""); setBuildErr(""); }} title="Clear build output">
                      Clear build
                    </button>
                  </div>
                  {buildOut || buildErr ? (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 900, marginBottom: 6 }}>Build output</div>
                      {buildOut ? <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{buildOut}</pre> : null}
                      {buildErr ? <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0 0", color: "#b91c1c" }}>{buildErr}</pre> : null}
                    </div>
                  ) : null}
                  <div>
                    <div style={{ fontWeight: 900, marginBottom: 6 }}>AI / Agent output</div>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 11, opacity: 0.95 }}>
                      {outputLogText || "(no output yet)"}
                    </pre>
                  </div>
                </div>
              ) : bottomTab === "debug" ? (
                <div style={{ fontSize: 12, color: "var(--z-muted)" }}>
                  Debug Console isnâ€™t wired to a JS debugger yet. Next step would be integrating a real debug adapter / console stream.
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                    <input
                      value={portDraft}
                      onChange={(e) => setPortDraft(e.target.value)}
                      placeholder="Add port (e.g. 5173)"
                      style={{ flex: "0 0 180px", padding: "8px 10px", borderRadius: 10, border: "1px solid var(--z-border)", fontSize: 12, background: "var(--z-panel-bg)", color: "var(--z-text)" }}
                    />
                    <button
                      className="export-btn"
                      type="button"
                      onClick={() => {
                        const p = Number(String(portDraft || "").trim());
                        if (!p || p < 1 || p > 65535) return;
                        setPorts((prev) => {
                          const arr = Array.isArray(prev) ? prev : [];
                          if (arr.some((x) => Number(x.port) === p)) return arr;
                          return [...arr, { port: p, label: `Port ${p}` }];
                        });
                        setPortDraft("");
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {ports.map((p, idx) => (
                      <div key={`${p.port}-${idx}`} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 10px", borderRadius: 12, border: "1px solid var(--z-border)" }}>
                        <div style={{ fontWeight: 900 }}>{p.label}</div>
                        <div style={{ opacity: 0.75 }}>:{p.port}</div>
                        <div style={{ flex: 1 }} />
                        <button className="export-btn" type="button" onClick={() => openPort(p.port)}>
                          Open
                        </button>
                        <button
                          className="export-btn"
                          type="button"
                          onClick={() => setPorts((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []))}
                          title="Remove"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* VS Code-like status bar */}
        <div
          style={{
            borderTop: "1px solid var(--z-border)",
            background: "var(--z-status-bg)",
            color: "var(--z-status-text)",
            padding: "6px 10px",
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontSize: 11,
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedSource}:{selectedPath || "(none)"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", whiteSpace: "nowrap" }}>
            {cursorQuickInfo ? (
              <div
                title={cursorQuickInfo}
                style={{
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  opacity: 0.85,
                }}
              >
                {cursorQuickInfo}
              </div>
            ) : null}
            <button
              className="export-btn"
              type="button"
              style={{ padding: "2px 8px" }}
              title="Show Problems"
              onClick={() => {
                setTsPanel("problems");
                openBottom("problems");
              }}
            >
              Problems: {tsDiagnostics.length}
            </button>
            <div>{language}</div>
            <div>
              Ln {Number(cursorPos?.line || 1)}, Col {Number(cursorPos?.column || 1)}
            </div>
          </div>
        </div>
      </div>



      {/* AI sidebar (right) */}
      <div style={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, gridRow: 2 }}>
        <div
          style={{
            padding: 10,
            paddingTop: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            flex: 1,
            minHeight: 0,
            background: "rgba(0,0,0,0.02)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "6px 0 4px 0", borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5, opacity: 0.9 }}>VS CODE + AI</span>
          </div>
          {/* Advanced Analysis Panel */}
          {aiShowAdvanced && (
            <div style={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 12, overflow: "hidden", background: "#f5f3ff" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", borderBottom: "1px solid rgba(0,0,0,0.08)", background: "#ede9fe" }}>
                <div style={{ fontWeight: 900, fontSize: 12 }}>ðŸ”¬ Advanced Analysis</div>
                <div style={{ flex: 1 }} />
                <button 
                  className="export-btn" 
                  type="button" 
                  onClick={() => setAiShowAdvanced(false)} 
                  style={{ padding: "2px 8px", fontSize: 10 }}
                >
                  âœ•
                </button>
              </div>
              
              <div style={{ padding: 10, fontSize: 11, maxHeight: 300, overflow: "auto" }}>
                {/* Code Metrics */}
                {aiCodeAnalysis && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, color: "#6d28d9", marginBottom: 6 }}>ðŸ“Š Metrics</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 10 }}>
                      <div>Functions: {aiCodeAnalysis.functions.length}</div>
                      <div>Classes: {aiCodeAnalysis.classes.length}</div>
                      <div>Complexity: {aiCodeAnalysis.complexity.level}</div>
                      <div>Lines: {aiCodeAnalysis.complexity.lines}</div>
                    </div>
                  </div>
                )}

                {/* Bug List */}
                {aiBugs.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, color: "#dc2626", marginBottom: 6 }}>ðŸ› Bugs ({aiBugs.length})</div>
                    {aiBugs.slice(0, 3).map((bug, i) => (
                      <div key={i} style={{ fontSize: 10, marginBottom: 4, padding: 4, background: "#fee2e2", borderRadius: 4 }}>
                        <strong>[{bug.severity}]</strong> {bug.message}
                      </div>
                    ))}
                    {aiBugs.length > 3 && <div style={{ fontSize: 9, color: "#999" }}>+{aiBugs.length - 3} more...</div>}
                  </div>
                )}

                {/* Refactoring Suggestions */}
                {aiRefactorings.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, color: "#0891b2", marginBottom: 6 }}>ðŸ’¡ Refactor ({aiRefactorings.length})</div>
                    {aiRefactorings.slice(0, 3).map((ref, i) => (
                      <div key={i} style={{ fontSize: 10, marginBottom: 4, padding: 4, background: "#cffafe", borderRadius: 4 }}>
                        <strong>{ref.title}</strong>
                      </div>
                    ))}
                  </div>
                )}

                {/* Performance Issues */}
                {aiPerformance.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, color: "#ea580c", marginBottom: 6 }}>âš¡ Performance ({aiPerformance.length})</div>
                    {aiPerformance.slice(0, 3).map((perf, i) => (
                      <div key={i} style={{ fontSize: 10, marginBottom: 4, padding: 4, background: "#ffedd5", borderRadius: 4 }}>
                        {perf.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plan + Output in one scrollable area; composer on top */}
          <div
            className="dev-ide-cursor-panel"
            style={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {/* Single scrollable area: Plan + Output scroll together */}
            <div
              ref={aiOutputRef}
              onScroll={() => {
                try {
                  const el = aiOutputRef.current;
                  if (!el) return;
                  const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
                  setAiOutputStickBottom(dist < 40);
                } catch (_) {}
              }}
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                paddingBottom: 200,
                background: "rgba(30, 30, 30, 0.75)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderRadius: 12,
                scrollbarGutter: "stable",
              }}
            >
              {/* Plan section (scrolls with output) */}
          {aiPlanOut || (executablePlanSteps && executablePlanSteps.length > 0) ? (
                <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", marginBottom: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <div style={{ fontWeight: 900, fontSize: 12, color: "rgba(255,255,255,0.9)" }}>ðŸ“‹ Plan</div>
                <div style={{ flex: 1 }} />
                {executablePlanSteps?.length > 0 ? (
                  <>
                    <button className="export-btn" type="button" onClick={applyAllPlanSteps} style={{ padding: "2px 8px" }} title="Apply all steps">Apply all</button>
                    <button className="export-btn" type="button" onClick={rollbackAllPlanSteps} style={{ padding: "2px 8px" }} title="Rollback all steps">Rollback all</button>
                  </>
                ) : null}
                <button className="export-btn" type="button" onClick={clearPlan} style={{ padding: "2px 8px" }} title="Clear plan">
                  Clear
                </button>
              </div>
              {executablePlanSteps?.length > 0 ? (
                <div style={{ padding: "8px 10px", fontSize: 11, maxHeight: 280, overflow: "auto" }}>
                  {executablePlanSteps.map((step, i) => {
                    const expanded = planStepExpanded.has(i);
                    const paths = getStepPaths(step);
                    const conf = step.confidence === "high" ? "high" : step.confidence === "low" ? "low" : "medium";
                    const confStyle = conf === "high" ? { borderLeft: "3px solid rgba(34,197,94,0.7)", icon: "âœ“", label: "Auto" } : conf === "low" ? { borderLeft: "3px solid rgba(239,68,68,0.6)", icon: "?", label: "Review" } : { borderLeft: "3px solid rgba(234,179,8,0.6)", icon: "!", label: "Confirm" };
                    return (
                      <div key={step.id} style={{ marginBottom: 8, padding: 6, background: expanded ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.05)", borderRadius: 6, border: expanded ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(255,255,255,0.08)", ...confStyle }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <button type="button" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", flex: "1 1 auto", minWidth: 0 }} onClick={() => togglePlanStepExpanded(i)} title="Expand to see files">
                            <span style={{ fontWeight: 700, opacity: 0.95 }}>{expanded ? "â–¼" : "â–¶"} {i + 1}. {step.description}</span>
                          </button>
                          <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: conf === "high" ? "rgba(34,197,94,0.25)" : conf === "low" ? "rgba(239,68,68,0.25)" : "rgba(234,179,8,0.25)", color: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }} title={conf === "high" ? "Will auto-apply in Apply all" : conf === "low" ? "Requires review" : "Confirm before apply"}>
                            <span aria-hidden>{confStyle.icon}</span>
                            <span>{confStyle.label}</span>
                          </span>
                          {step.applied ? <span style={{ fontSize: 10, color: "rgba(74,222,128,0.9)" }}>âœ“ Applied</span> : null}
                          <div style={{ flex: "0 0 auto" }} />
                          {!step.applied ? (
                            <button className="export-btn" type="button" onClick={() => applyPlanStep(i)} style={{ padding: "2px 6px" }} title="Apply this step">Apply</button>
                          ) : (
                            <button className="export-btn" type="button" onClick={() => rollbackPlanStep(i)} style={{ padding: "2px 6px" }} title="Rollback this step only">Rollback</button>
                          )}
                        </div>
                        {expanded && paths.length > 0 ? (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 10, opacity: 0.9 }}>
                            Files: {paths.slice(0, 12).join(", ")}{paths.length > 12 ? "â€¦" : ""}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
                  {aiPlanOut ? (
                    <pre style={{ margin: 0, padding: 10, fontSize: 11, whiteSpace: "pre-wrap", opacity: 0.95, color: "#ffffff", fontFamily: "'Monaco', 'Menlo', 'Courier New', monospace" }}>
                      {aiPlanOut}
                    </pre>
                  ) : null}
            </div>
          ) : null}

              {/* Conversation panel (message bubbles, expandable steps/patches) */}
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  padding: "8px 10px",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  fontWeight: 900,
                  fontSize: 12,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                <div style={{ flex: 1 }}>ðŸ’¬ Chat</div>
                <button className="export-btn" type="button" onClick={clearConversation} style={{ padding: "2px 8px" }} title="Clear conversation">
                  Clear
                </button>
              </div>
              <div
                ref={conversationScrollRef}
                style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "10px 8px", display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 10 }}
                onScroll={() => {
                  try {
                    const el = conversationScrollRef.current;
                    if (!el) return;
                    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
                    setAiOutputStickBottom(dist < 40);
                  } catch (_) {}
                }}
              >
                {aiConversation.length === 0 && !aiBusy ? (
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", padding: 12, lineHeight: 1.5 }}>
                    Ask for edits (patch), a plan (steps), or an explanation. Patches and plans appear above and belowâ€”review and use Apply now / Apply to run them.
                  </div>
                ) : null}
                {aiConversation.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "92%",
                      padding: "8px 12px",
                      borderRadius: 12,
                      background: msg.role === "user" ? "rgba(59, 130, 246, 0.2)" : "rgba(255,255,255,0.08)",
                      border: msg.role === "user" ? "1px solid rgba(59, 130, 246, 0.35)" : "1px solid rgba(255,255,255,0.1)",
                      fontSize: 11,
                      wordBreak: "break-word",
                      ...(msg.role === "user" ? { whiteSpace: "pre-wrap" } : {}),
                    }}
                  >
                    <div style={{ marginBottom: msg.contract ? 8 : 0, display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                      {msg.role === "assistant" ? (
                        <>
                          <IdeChatMarkdown content={msg.content} onOpenPath={openPathFromConversation} />
                          {msg.id === streamingMessageId && (
                            <span className="dev-ide-streaming-cursor" style={{ display: "inline-block", marginLeft: 2, opacity: 0.9, animation: "dev-ide-cursor-blink 0.8s step-end infinite" }}>â–‹</span>
                          )}
                        </>
                      ) : (
                        <span style={{ whiteSpace: "pre-wrap" }}>{String(msg.content || "").trim() || "â€”"}</span>
                      )}
                    </div>
                    {msg.role === "assistant" && msg.contract && (() => {
                      const c = msg.contract;
                      const steps = Array.isArray(c?.artifacts?.steps) ? c.artifacts.steps : [];
                      const files = c?.artifacts?.files && typeof c.artifacts.files === "object" ? c.artifacts.files : {};
                      const textEdits = Array.isArray(c?.artifacts?.textEdits) ? c.artifacts.textEdits : [];
                      const filePaths = Object.keys(files);
                      const hasFiles = filePaths.length > 0;
                      const hasSteps = steps.length > 0;
                      if (!hasSteps && !hasFiles && textEdits.length === 0) return null;
                      const fileStats = filePaths.map((path) => {
                        const content = String(files[path] || "");
                        const lineCount = (content.match(/\n/g) || []).length + (content.length > 0 ? 1 : 0);
                        return { path, lines: lineCount, content };
                      });
                      const textEditStats = [];
                      textEdits.forEach((e) => {
                        if (!e?.path) return;
                        const newLines = (String(e?.newText || "").match(/\n/g) || []).length + (e.newText && e.newText.length > 0 ? 1 : 0);
                        const existing = textEditStats.find((x) => x.path === e.path);
                        if (existing) existing.lines += newLines;
                        else textEditStats.push({ path: e.path, lines: newLines, content: null });
                      });
                      const allStats = [...fileStats];
                      textEditStats.forEach((te) => {
                        const inFiles = allStats.find((s) => s.path === te.path);
                        if (inFiles) {
                          inFiles.lines += te.lines;
                          if (te.content != null) inFiles.content = inFiles.content || te.content;
                        } else allStats.push({ path: te.path, lines: te.lines, content: null });
                      });
                      const PREVIEW_LINES = 10;
                      return (
                        <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 8, marginTop: 6 }}>
                          {hasSteps ? (
                            <div style={{ marginBottom: allStats.length ? 8 : 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 10, opacity: 0.9, marginBottom: 4 }}>ðŸ“‹ Steps â€” see Plan panel to apply</div>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10, opacity: 0.9 }}>
                                {steps.slice(0, 8).map((s, i) => (
                                  <li key={i}>{typeof s === "object" ? (s?.description ?? s?.step ?? `Step ${i + 1}`) : String(s)}</li>
                                ))}
                                {steps.length > 8 ? <li style={{ opacity: 0.7 }}>+{steps.length - 8} more</li> : null}
                              </ul>
                            </div>
                          ) : null}
                          {allStats.length > 0 ? (
                            <div style={{ fontSize: 10, opacity: 0.95, fontFamily: "var(--font-mono, monospace)" }}>
                              {allStats.slice(0, 20).map(({ path, lines, content }, i) => {
                                const fileContent = content != null ? String(content) : "";
                                const contentLines = fileContent ? fileContent.split("\n") : [];
                                const previewLines = contentLines.slice(0, PREVIEW_LINES);
                                const hasMore = contentLines.length > PREVIEW_LINES;
                                return (
                                  <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                      <button
                                        type="button"
                                        onClick={() => openPathFromConversation(path)}
                                        style={{ background: "none", border: "none", padding: 0, color: "rgba(255,255,255,0.85)", wordBreak: "break-all", cursor: "pointer", font: "inherit", fontSize: "inherit", textAlign: "left", textDecoration: "underline" }}
                                        title={`Open ${path} in editor`}
                                      >
                                        {path}
                                      </button>
                                      <span style={{ color: "rgba(74,222,128,0.95)", flexShrink: 0 }}>+{lines} lines</span>
                                    </div>
                                    {previewLines.length > 0 ? (
                                      <div
                                        style={{
                                          background: "rgba(34,197,94,0.12)",
                                          borderLeft: "3px solid rgba(34,197,94,0.7)",
                                          borderRadius: 4,
                                          padding: "6px 8px",
                                          overflow: "auto",
                                          maxHeight: 160,
                                          fontSize: 10,
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        <div style={{ color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Lines added:</div>
                                        {previewLines.map((line, idx) => (
                                          <div key={idx} style={{ display: "flex", gap: 8, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                                            <span style={{ color: "rgba(255,255,255,0.5)", flexShrink: 0, minWidth: 20, textAlign: "right" }}>{idx + 1}</span>
                                            <span style={{ color: "rgba(255,255,255,0.9)" }}>{line || " "}</span>
                                          </div>
                                        ))}
                                        {hasMore ? (
                                          <div style={{ color: "rgba(255,255,255,0.5)", marginTop: 4, paddingLeft: 28 }}>... +{contentLines.length - PREVIEW_LINES} more lines</div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                              {allStats.length > 20 ? <div style={{ opacity: 0.7 }}>+{allStats.length - 20} more files</div> : null}
                              <div style={{ marginTop: 6, opacity: 0.8 }}>See Patch panel to review and apply.</div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>
                ))}
                {aiBusy && aiStreamingResponse ? (
                  <div
                    style={{
                      alignSelf: "flex-start",
                      maxWidth: "92%",
                      padding: "8px 12px",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      opacity: 0.95,
                    }}
                  >
                    {aiStreamingResponse}
                    <span style={{ animation: "opacity 0.6s ease-in-out infinite alternate" }}> â–‹</span>
                  </div>
                ) : null}
              </div>
            </div>
        </div>

            {/* Typing box: fixed at bottom, above output */}
        <div
          style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "10px 12px 12px",
                background: "rgba(37, 37, 38, 0.88)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderTop: "1px solid rgba(255,255,255,0.1)",
                borderLeft: "1px solid rgba(255,255,255,0.06)",
                borderRight: "1px solid rgba(255,255,255,0.06)",
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              }}
            >
          {activityLabel ? (
                <div style={{ marginBottom: 6, fontSize: 11, opacity: 0.85, minHeight: 16, color: "rgba(255,255,255,0.9)" }}>
              <span style={{ fontWeight: 800 }}>
                {activityLabel}
                {".".repeat(Math.max(1, Math.min(3, Number(activityDots || 1))))}
              </span>
              {activityDetail ? <span style={{ marginLeft: 8, opacity: 0.9 }}>{activityDetail}</span> : null}
            </div>
          ) : (
            <div style={{ marginBottom: 6, minHeight: 16 }} />
          )}
          <div className="dev-ide-composer-box">
            <textarea
              ref={aiPromptRef}
              value={aiPrompt}
              onChange={(e) => {
                setAiPrompt(e.target.value);
                try {
                  const el = aiPromptRef.current;
                  if (!el) return;
                  el.style.height = "auto";
                  el.style.height = Math.min(320, el.scrollHeight) + "px";
                } catch (_) {}
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                if (e.shiftKey) return; // Shift+Enter = new line
                e.preventDefault();
                if (aiBusy || aiAttachBusy || !aiPrompt.trim()) return;
                sendAi();
              }}
              placeholder="Ask AIâ€¦ Use @path or @file.js to include files. Enter to send, Shift+Enter for new line."
              rows={3}
              className="dev-ide-composer-textarea"
            />
            <div className="dev-ide-input-actions">
              <select
                className="dev-ide-model-select"
                value={ideSelectedModel}
                onChange={(e) => {
                  const v = e.target.value;
                  setIdeSelectedModel(v);
                  try {
                    localStorage.setItem("zolaid-ide-model", v);
                  } catch (_) {}
                }}
                title="AI model"
              >
                {ideModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <button
                ref={aiToolsBtnRef}
                className="export-btn dev-ide-action-btn"
                type="button"
                onClick={openAiToolsMenu}
                title="Tools & Settings"
              >
                âš™ï¸
              </button>
              <button
                ref={agentMenuBtnRef}
                className="export-btn dev-ide-action-btn"
                type="button"
                onClick={() => openAgentMenu({ preferSideBySideWithTools: true })}
                title="Agent (multi-step execution)"
              >
                ðŸ¤–
              </button>
              <input
                ref={aiFileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                accept="image/*,video/*,.pdf,.doc,.docx,.txt,.md,.json,.js,.jsx,.ts,.tsx,.css,.html,.yml,.yaml,.csv,.xlsx,.xls"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length) addAttachments(files);
                  e.target.value = "";
                }}
              />
              <button
                className="export-btn dev-ide-action-btn"
                type="button"
                onClick={() => aiFileInputRef.current?.click?.()}
                disabled={aiBusy || aiAttachBusy}
                title="Attach files"
              >
                ðŸ“Ž
              </button>
              <button
                className="export-btn dev-ide-send-btn"
                type="button"
                onClick={sendAi}
                disabled={aiBusy || aiAttachBusy || !aiPrompt.trim()}
                title="Send (Enter)"
              >
                {aiBusy ? "ðŸ”„" : "â†‘"}
              </button>
            </div>
          </div>
            {patchFiles ? (
                <div style={{ marginTop: 8 }}>
              {lastAiContractMode ? (
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4, marginRight: 8, background: lastAiContractMode === "plan" ? "rgba(59, 130, 246, 0.25)" : "rgba(34, 197, 94, 0.25)", color: "inherit" }}>{lastAiContractMode === "plan" ? "Plan" : "Patch"}</span>
              ) : null}
              {patchSummary ? <span style={{ fontSize: 11, opacity: 0.9 }}>{patchSummary}</span> : null}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                <button className="export-btn" type="button" onClick={queuePatch} style={{ background: "#dbeafe" }}>
                  âœ… Queue changes
                </button>
                <button className="export-btn" type="button" onClick={() => applyPatchNow({ confirm: true })} title="Apply selected files to disk">
                  ðŸ“ Apply now
                </button>
              </div>
              <div style={{ marginTop: 4, fontSize: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="export-btn" style={{ marginRight: 6 }} onClick={selectAllPatch}>Select all</button>
                <button type="button" className="export-btn" onClick={selectNonePatch}>Select none</button>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }} title="Show line-by-line âˆ’/+ diff instead of side-by-side Before/After">
                  <input type="checkbox" checked={patchShowUnifiedDiff} onChange={(e) => setPatchShowUnifiedDiff(e.target.checked)} />
                  <span>Unified diff</span>
                </label>
              </div>
              <div style={{ marginTop: 8, maxHeight: 200, overflow: "auto" }}>
                {Object.entries(patchFiles)
                  .map(([k, c]) => ({ key: k, path: normalizePath(k), content: c }))
                  .filter(({ path }) => !!path)
                  .sort((a, b) => a.path.localeCompare(b.path))
                  .slice(0, 50)
                  .map(({ key, path, content }) => (
                    <div key={path} style={{ marginBottom: 4 }}>
                      <button
                        type="button"
                        style={{ fontSize: 11, textAlign: "left", width: "100%", display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", background: patchExpanded.has(path) ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)", border: patchExpanded.has(path) ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(255,255,255,0.1)", borderRadius: 4 }}
                        onClick={() => togglePatchFile(path)}
                      >
                        <input type="checkbox" checked={patchSelected.has(path)} onChange={() => togglePatchSelected(path)} onClick={(e) => e.stopPropagation()} />
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{path}</span>
                        {patchExpanded.has(path) ? "â–¼" : "â–¶"}
                      </button>
                      {patchExpanded.has(path) ? (
                        <div style={{ marginLeft: 12, marginTop: 4, fontSize: 10, maxHeight: 200, overflow: "auto" }}>
                          {patchShowUnifiedDiff ? (
                            <div style={{ margin: 0, padding: "6px 8px", background: "rgba(0,0,0,0.35)", borderRadius: 4, fontFamily: "monospace", fontSize: 10, overflow: "auto" }}>
                              <div style={{ marginBottom: 4, opacity: 0.8, fontSize: 9 }}>âˆ’ removed  + added</div>
                              {(patchDiffByPath[path] || simpleLineDiff((patchBaseFiles && patchBaseFiles[path]) != null ? String(patchBaseFiles[path]) : "", String(content ?? "")))
                                .map(({ type, line, nBefore, nAfter }, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 8,
                                      padding: "1px 0",
                                      background: type === "-" ? "rgba(248,113,113,0.08)" : type === "+" ? "rgba(74,222,128,0.08)" : "transparent",
                                      color: type === "-" ? "rgba(248,113,113,0.98)" : type === "+" ? "rgba(74,222,128,0.98)" : "rgba(255,255,255,0.75)",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    <span style={{ flex: "0 0 42px", textAlign: "right", opacity: 0.7 }}>{nBefore || ""}</span>
                                    <span style={{ flex: "0 0 42px", textAlign: "right", opacity: 0.7 }}>{nAfter || ""}</span>
                                    <span style={{ flex: "0 0 18px" }}>{type === " " ? " " : type}</span>
                                    <span style={{ flex: 1, minWidth: 0 }}>{line || " "}</span>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div>
                                <div style={{ fontWeight: 600, marginBottom: 2, color: "rgba(248,113,113,0.9)" }}>Before</div>
                                <pre style={{ margin: 0, padding: 6, background: "rgba(0,0,0,0.3)", borderRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{(patchBaseFiles && patchBaseFiles[path]) != null ? String(patchBaseFiles[path]).slice(0, 3000) : "(new file)"}</pre>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, marginBottom: 2, color: "rgba(74,222,128,0.9)" }}>After</div>
                                <pre style={{ margin: 0, padding: 6, background: "rgba(0,0,0,0.3)", borderRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{String(content ?? "").slice(0, 3000)}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
              </div>
                </div>
            ) : null}
            {lastAppliedPatchSnapshot?.paths?.length ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
              <button className="export-btn" type="button" onClick={rollbackLastPatch} style={{ background: "#fef3c7", color: "#92400e" }} title="Revert last applied patch">
                â†© Rollback last patch
              </button>
                </div>
            ) : null}
          </div>
          </div>

          {aiErr ? <div style={{ fontSize: 11, color: "#b91c1c", whiteSpace: "pre-wrap", padding: "8px 10px", background: "#fee2e2", borderRadius: 8 }}>âŒ {aiErr}</div> : null}
        </div>
      </div>
    </div>
  );
}

